import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Switch,
  Radio,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Checkbox,
  Avatar,
  Rating,
  Chip,
  Tooltip,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Autocomplete,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  DeleteOutline as DeleteOutlineIcon,
  Folder as FolderIcon,
  Description as FileIcon,
  ArrowUpward as ArrowUpIcon,
  History as HistoryIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  CalendarMonth as CalendarMonthIcon,
  ViewList as ViewListIcon,
  Groups as GroupsIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Quiz as QuizIcon,
  FamilyRestroom as FamilyIcon,
  Close as CloseIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { toast } from 'react-toastify';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/fr';
import studentService, { Student } from '../services/student.service';
import classService, { ClassName } from '../services/class.service';
import timeSlotService, { TimeSlot } from '../services/timeSlot.service';
import api from '../services/api';
import StudentBacPanel from '../components/bac/StudentBacPanel';
import StudentSatPanel from '../components/sat/StudentSatPanel';
import invoiceService, { Invoice } from '../services/invoice.service';
import quizAssignmentService, { QuizAssignment } from '../services/quizAssignment.service';
import quizService, { Quiz } from '../services/quiz.service';
import quizContainerService, { QuizContainer } from '../services/quizContainerService';
import nextQuizItemService from '../services/nextQuizItem.service';
import quizSessionService, { QuizSession } from '../services/quizSession.service';
import punctualTimeSlotService, { PunctualTimeSlot } from '../services/punctualTimeSlot.service';
import objectiveAssignmentService, { ObjectiveAssignment } from '../services/objectiveAssignment.service';
import objectiveService, { Objective } from '../services/objective.service';
import objectiveContainerService, { ObjectiveContainer } from '../services/objectiveContainerService';
import parentService, { Parent as ParentModel, CreateParentData } from '../services/parent.service';
import notificationService, { TeacherNotification, SendNotificationData } from '../services/notification.service';
import { useNotificationStore } from '../store/notificationStore';
import QuestionHistoryModal from '../components/QuestionHistoryModal';
import quizRecommendationService, { QuizRecommendation, TeacherControlSettings } from '../services/quizRecommendation.service';
import { getPushStatusOverview } from '../services/pushService';
import { useAuthStore } from '../store/authStore';

const AVAILABLE_COLORS = [
  '6ea8fe', 'a370f7', 'a98eda', 'e685b5', 'ea868f',
  'feb272', 'ffda6a', '75b798', '79dfc1', '6edff6', 'dee2e6'
];

const Students: React.FC = () => {
  const { user } = useAuthStore();
  const isErwan = user?.username === 'erwan';
  const [plaintextPasswords, setPlaintextPasswords] = useState<Record<string, string>>({});
  const [studentSearch, setStudentSearch] = useState('');
  const [students, setStudents] = useState<Student[]>([]);
  const [classes, setClasses] = useState<ClassName[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [gradeDialogOpen, setGradeDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<ObjectiveAssignment | null>(null);
  const [tempGrade, setTempGrade] = useState<number | null>(null);
  const [allTimeSlots, setAllTimeSlots] = useState<TimeSlot[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [calendarTeacherId, setCalendarTeacherId] = useState<number | null>(null);
  const [calendarTeachers, setCalendarTeachers] = useState<{id: number; name: string}[]>([]);
  const [activeTab, setActiveTab] = useState(0);

  // Notifications state
  const [teacherNotifications, setTeacherNotifications] = useState<TeacherNotification[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationBody, setNotificationBody] = useState('');
  const [notificationRecipient, setNotificationRecipient] = useState<'student' | 'parent'>('student');
  const [sendingNotification, setSendingNotification] = useState(false);

  // Push notification status (who has notifications enabled)
  const [pushStudentIds, setPushStudentIds] = useState<Set<number>>(new Set());
  const [pushParentIds, setPushParentIds] = useState<Set<number>>(new Set());

  // Payment invoice state
  const [invoices, setInvoices] = useState<Invoice[]>([]);

  // Objective assignment state
  const [objectiveAssignments, setObjectiveAssignments] = useState<ObjectiveAssignment[]>([]);

  // Objective tree structure for browsing
  interface ObjectiveTreeNode {
    id: string;
    title: string;
    type: 'container' | 'objective';
    containerId?: number;
    objectiveData?: Objective;
    children?: ObjectiveTreeNode[];
  }
  const [objectiveTree, setObjectiveTree] = useState<ObjectiveTreeNode[]>([]);

  // Assignment tree structure (for left panel)
  interface AssignmentTreeNode {
    id: string;
    title: string;
    type: 'container' | 'assignment';
    containerId?: number;
    assignment?: ObjectiveAssignment;
    children?: AssignmentTreeNode[];
    averageGrade?: number;
    weight: number;
  }
  const [assignmentTree, setAssignmentTree] = useState<AssignmentTreeNode[]>([]);

  // Quiz assignment state
  const [quizAssignments, setQuizAssignments] = useState<QuizAssignment[]>([]);
  const [quizSessions, setQuizSessions] = useState<QuizSession[]>([]);
  const [selectedQuizAssignment, setSelectedQuizAssignment] = useState<QuizAssignment | null>(null);
  const [selectedQuizSession, setSelectedQuizSession] = useState<QuizSession | null>(null);
  const [isConfiguringNextQuiz, setIsConfiguringNextQuiz] = useState(false);

  // Next quiz configuration state
  interface SelectedQuizItem {
    type: 'quiz' | 'container';
    id: number;
    data: Quiz | QuizContainer;
    excludedQuizIds?: number[]; // For containers only
    allQuizzes?: Quiz[]; // All quizzes in the container
  }
  const [selectedQuizItems, setSelectedQuizItems] = useState<SelectedQuizItem[]>([]);
  const [browseTab, setBrowseTab] = useState(0); // 0: sessions, 1: atelier, 2: professeurs
  const [browseQuizzes, setBrowseQuizzes] = useState<Quiz[]>([]);
  const [browseContainers, setBrowseContainers] = useState<QuizContainer[]>([]);
  const [browseCurrentContainer, setBrowseCurrentContainer] = useState<number | null>(null);
  const [browseParentContainer, setBrowseParentContainer] = useState<number | null>(null);
  const [browseSessions, setBrowseSessions] = useState<QuizSession[]>([]);
  const [browseSessionContent, setBrowseSessionContent] = useState<QuizAssignment[] | null>(null);
  const [selectedBrowseSession, setSelectedBrowseSession] = useState<QuizSession | null>(null);
  const [selectedBrowseStudent, setSelectedBrowseStudent] = useState<Student | null>(null);

  // Question history modal state
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [questionHistory, setQuestionHistory] = useState<any[]>([]);
  const [selectedQuestionForHistory, setSelectedQuestionForHistory] = useState<QuizAssignment | null>(null);

  // Customization dialog state
  const [customizeDialogOpen, setCustomizeDialogOpen] = useState(false);
  const [customizingContainer, setCustomizingContainer] = useState<QuizContainer | null>(null);
  const [containerQuizzes, setContainerQuizzes] = useState<Quiz[]>([]);
  const [excludedQuizIds, setExcludedQuizIds] = useState<number[]>([]);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [editInvoiceDialogOpen, setEditInvoiceDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [showAllPayments, setShowAllPayments] = useState(false);
  const [allInvoices, setAllInvoices] = useState<Invoice[]>([]);
  const [showOrgInvoices, setShowOrgInvoices] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [invoiceToMarkPaid, setInvoiceToMarkPaid] = useState<number | null>(null);
  const [invoicesToMarkPaid, setInvoicesToMarkPaid] = useState<number[]>([]);
  const [selectedInvoices, setSelectedInvoices] = useState<number[]>([]);
  const [draggingInvoiceId, setDraggingInvoiceId] = useState<number | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'cheque' | 'cash' | 'transfer' | 'card' | 'nature'>('cheque');
  const [paymentDiscount, setPaymentDiscount] = useState('');
  const [paymentDiscountLabel, setPaymentDiscountLabel] = useState('');
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [invoiceStartTime, setInvoiceStartTime] = useState<Dayjs | null>(null);
  const [invoiceEndTime, setInvoiceEndTime] = useState<Dayjs | null>(null);
  const [invoicePrice, setInvoicePrice] = useState('6000');
  const [invoiceDiscount, setInvoiceDiscount] = useState('');
  const [invoiceDiscountLabel, setInvoiceDiscountLabel] = useState('');
  const [startTimePickerOpen, setStartTimePickerOpen] = useState(false);
  const [endTimePickerOpen, setEndTimePickerOpen] = useState(false);

  // Ref for quiz sessions list to auto-scroll
  const quizSessionsListRef = useRef<HTMLDivElement>(null);

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [classGroup, setClassGroup] = useState<number | ''>('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [parentGender, setParentGender] = useState<'M' | 'F' | ''>('');
  const [parentPhone, setParentPhone] = useState('');
  const [selectedColor, setSelectedColor] = useState('6ea8fe');
  const [hourlyRate, setHourlyRate] = useState('3000');
  // Première : sujets de l'épreuve anticipée avec ou sans spécialité dans l'onglet Bac de l'appli élèves
  const [mathsSpecialite, setMathsSpecialite] = useState(true);
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
  const [calendarView, setCalendarView] = useState<'detail' | 'count'>('detail');
  const [calendarWeekOffset, setCalendarWeekOffset] = useState(0);
  const [ncTime, setNcTime] = useState(new Date());

  // Quiz timeline state
  const [quizTimelineOpen, setQuizTimelineOpen] = useState(false);
  const [quizTimelinePeriod, setQuizTimelinePeriod] = useState<'month' | 'week' | '5days' | '3days' | 'day'>('week');
  const [quizTimelineOffset, setQuizTimelineOffset] = useState(0);
  const [quizTimelineStudents, setQuizTimelineStudents] = useState<Student[]>([]);
  const [quizAttempts, setQuizAttempts] = useState<{student: number, completed_date: string, was_correct: boolean}[]>([]);
  const [quizTimelineLoading, setQuizTimelineLoading] = useState(false);

  // Punctual time slots
  const [punctualTimeSlots, setPunctualTimeSlots] = useState<PunctualTimeSlot[]>([]);
  const [punctualStudent, setPunctualStudent] = useState<number | ''>('');
  const [punctualDateMode, setPunctualDateMode] = useState<'offset' | 'picker'>('offset');
  const [punctualDate, setPunctualDate] = useState('');
  const [punctualDay, setPunctualDay] = useState('Lundi');
  const [punctualWeeksOffset, setPunctualWeeksOffset] = useState(0);
  const [punctualStartHour, setPunctualStartHour] = useState(8);
  const [punctualStartMinute, setPunctualStartMinute] = useState(0);
  const [punctualEndHour, setPunctualEndHour] = useState(9);
  const [punctualEndMinute, setPunctualEndMinute] = useState(0);

  // Family state
  const [familyMembers, setFamilyMembers] = useState<ParentModel[]>([]);
  const [loadingFamily, setLoadingFamily] = useState(false);
  const [familyDialogOpen, setFamilyDialogOpen] = useState(false);
  const [editingParent, setEditingParent] = useState<ParentModel | null>(null);
  const [parentFirstName, setParentFirstName] = useState('');
  const [parentLastName, setParentLastName] = useState('');
  const [parentPhoneNumber, setParentPhoneNumber] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [parentPassword, setParentPassword] = useState('');
  const [parentGenderValue, setParentGenderValue] = useState<'M' | 'F' | ''>('');
  const [parentLinkedChildren, setParentLinkedChildren] = useState<number[]>([]);
  const [selectedParentId, setSelectedParentId] = useState<number | null>(null);
  const [creatingParentFromStudentDialog, setCreatingParentFromStudentDialog] = useState(false);
  const [showFamilyView, setShowFamilyView] = useState(false);
  const [familyParentFilter, setFamilyParentFilter] = useState('');
  const [familyStudentFilter, setFamilyStudentFilter] = useState('');
  const [familyHighlightParentId, setFamilyHighlightParentId] = useState<number | null>(null);
  const [familyHighlightStudentId, setFamilyHighlightStudentId] = useState<number | null>(null);

  // Recommendation state
  const [recommendations, setRecommendations] = useState<QuizRecommendation[]>([]);
  const [recoDialogOpen, setRecoDialogOpen] = useState(false);
  const [editingReco, setEditingReco] = useState<QuizRecommendation | null>(null);
  const [recoStartDate, setRecoStartDate] = useState('');
  const [recoEndDate, setRecoEndDate] = useState('');
  const [recoSessionsPerDay, setRecoSessionsPerDay] = useState(3);
  const [recoSelectedSessions, setRecoSelectedSessions] = useState<number[]>([]);
  const [recoCalendarMonth, setRecoCalendarMonth] = useState(dayjs().format('YYYY-MM'));
  const [recoDragging, setRecoDragging] = useState<{recoId: number; bound: 'start' | 'end'} | null>(null);
  const [recoDragDate, setRecoDragDate] = useState<string | null>(null);
  const recoDraggingRef = useRef(recoDragging);
  recoDraggingRef.current = recoDragging;
  const recoDragDateRef = useRef(recoDragDate);
  recoDragDateRef.current = recoDragDate;
  const recommendationsRef = useRef(recommendations);
  recommendationsRef.current = recommendations;
  const [recoDialogMonth, setRecoDialogMonth] = useState(dayjs().format('YYYY-MM'));
  const [recoPickingBound, setRecoPickingBound] = useState<'start' | 'end'>('start');
  const [teacherControlledBy, setTeacherControlledBy] = useState<'teacher' | 'parent'>('teacher');
  const [teacherTimeSlots, setTeacherTimeSlots] = useState<{ label: string; start_time: string; end_time: string; max_quizzes: number; order: number }[]>([]);

  // TimeSlot form state
  const [newSlotDay, setNewSlotDay] = useState<'Lundi' | 'Mardi' | 'Mercredi' | 'Jeudi' | 'Vendredi' | 'Samedi' | 'Dimanche'>('Lundi');
  const [newSlotStartHour, setNewSlotStartHour] = useState<number>(8);
  const [newSlotStartMinute, setNewSlotStartMinute] = useState<number>(0);
  const [newSlotEndHour, setNewSlotEndHour] = useState<number>(9);
  const [newSlotEndMinute, setNewSlotEndMinute] = useState<number>(0);

  // Define these functions early so they can be used in useEffect dependencies
  const loadBrowseQuizzes = useCallback(async () => {
    try {
      const params: any = {};
      if (browseCurrentContainer === null) {
        params.container = 'null';
      } else if (browseCurrentContainer) {
        params.container = browseCurrentContainer;
      }
      params.is_deleted = false;

      const data = await quizService.getAll(params);
      setBrowseQuizzes(data);
    } catch (error) {
      console.error('Error loading browse quizzes:', error);
    }
  }, [browseCurrentContainer]);

  const loadBrowseContainers = useCallback(async () => {
    try {
      const data = await quizContainerService.getContainers(browseCurrentContainer);
      setBrowseContainers(data);

      // Load parent info if we're inside a container
      if (browseCurrentContainer !== null) {
        const containerInfo = await quizContainerService.getContainer(browseCurrentContainer);
        setBrowseParentContainer(containerInfo.parent);
      } else {
        setBrowseParentContainer(null);
      }
    } catch (error) {
      console.error('Error loading browse containers:', error);
    }
  }, [browseCurrentContainer]);

  useEffect(() => {
    loadStudents();
    loadClasses();
    loadAllTimeSlots();
    loadPunctualTimeSlots();
    loadFamilyMembers();
    getPushStatusOverview().then(data => {
      setPushStudentIds(new Set(data.student_ids));
      setPushParentIds(new Set(data.parent_ids));
    }).catch(() => {});
    if (isErwan) {
      studentService.getPlaintextPasswords().then(setPlaintextPasswords).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (isConfiguringNextQuiz && browseTab === 1) {
      // Load data for Atelier tab
      loadBrowseQuizzes();
      loadBrowseContainers();
    }
  }, [isConfiguringNextQuiz, browseTab, browseCurrentContainer, loadBrowseQuizzes, loadBrowseContainers]);

  // Reset browse student selection when switching tabs
  useEffect(() => {
    if (isConfiguringNextQuiz && browseTab !== 0) {
      setSelectedBrowseStudent(null);
      setBrowseSessions([]);
      setBrowseSessionContent(null);
      setSelectedBrowseSession(null);
    }
  }, [browseTab, isConfiguringNextQuiz]);

  // Auto-scroll to bottom when quiz sessions list changes or tab becomes active
  useEffect(() => {
    if (activeTab === 4 && quizSessionsListRef.current && quizSessions.length > 0) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        if (quizSessionsListRef.current) {
          quizSessionsListRef.current.scrollTop = quizSessionsListRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [quizSessions, activeTab]);

  // Load notifications when Notifications tab is active
  useEffect(() => {
    const loadNotifications = async () => {
      if (activeTab === 1 && selectedStudent) {
        try {
          setLoadingNotifications(true);
          const data = await notificationService.getByStudent(selectedStudent.id);
          setTeacherNotifications(data);
        } catch (error) {
          console.error('Error loading notifications:', error);
          setTeacherNotifications([]);
        } finally {
          setLoadingNotifications(false);
        }
      }
    };
    loadNotifications();
  }, [selectedStudent, activeTab]);

  // Real-time notification status updates via WebSocket
  const notifications = useNotificationStore((s) => s.notifications);
  useEffect(() => {
    if (activeTab !== 1 || !selectedStudent) return;
    const latest = notifications[0];
    if (!latest || latest.category !== 'notification_status_update') return;
    const data = latest.data as Record<string, unknown>;
    const notifId = data.notification_id as number;
    if (!notifId) return;
    setTeacherNotifications(prev => prev.map(n =>
      n.id === notifId ? {
        ...n,
        status: data.status as TeacherNotification['status'],
        delivered_at: (data.delivered_at as string | null) ?? n.delivered_at,
        clicked_at: (data.clicked_at as string | null) ?? n.clicked_at,
        first_app_open_at: (data.first_app_open_at as string | null) ?? n.first_app_open_at,
      } : n
    ));
  }, [notifications, activeTab, selectedStudent]);

  // Load family members when Famille or Echanges tab is active
  useEffect(() => {
    if ((activeTab === 5 || activeTab === 1) && familyMembers.length === 0) {
      loadFamilyMembers();
    }
  }, [activeTab]);

  // Load recommendations when Quizz tab is active
  useEffect(() => {
    if (activeTab === 4 && selectedStudent) {
      (async () => {
        try {
          const recos = await quizRecommendationService.getAll(selectedStudent.id);
          setRecommendations(recos);
        } catch (e) { console.error('Error loading recommendations:', e); }
        try {
          const ctrl = await quizRecommendationService.getTeacherControl(selectedStudent.id);
          setTeacherControlledBy(ctrl.controlled_by);
          setTeacherTimeSlots(ctrl.time_slots.map((s, i) => ({
            label: s.label,
            start_time: s.start_time,
            end_time: s.end_time,
            max_quizzes: s.max_quizzes,
            order: s.order ?? i,
          })));
        } catch (e) { console.error('Error loading teacher control:', e); }
      })();
    }
  }, [activeTab, selectedStudent]);

  // Shared color palette for recommendations
  const RECO_COLORS = ['#64b5f6', '#81c784', '#e57373'];
  const RECO_BG_COLORS = ['#e3f2fd', '#e8f5e9', '#ffebee'];
  const sortedRecommendations = [...recommendations].sort((a, b) => a.start_date.localeCompare(b.start_date));
  const getRecoColor = (recoId: number) => {
    const idx = sortedRecommendations.findIndex(r => r.id === recoId);
    return RECO_COLORS[(idx >= 0 ? idx : 0) % RECO_COLORS.length];
  };
  const getRecoBgColor = (recoId: number) => {
    const idx = sortedRecommendations.findIndex(r => r.id === recoId);
    return RECO_BG_COLORS[(idx >= 0 ? idx : 0) % RECO_BG_COLORS.length];
  };
  const getEffectiveRange = (reco: QuizRecommendation) => {
    if (recoDragging?.recoId === reco.id && recoDragDate) {
      return {
        start: recoDragging.bound === 'start' ? recoDragDate : reco.start_date,
        end: recoDragging.bound === 'end' ? recoDragDate : reco.end_date,
      };
    }
    return { start: reco.start_date, end: reco.end_date };
  };

  // Drag-to-resize: commit on mouseup (uses refs for fresh values)
  useEffect(() => {
    const handleMouseUp = async () => {
      const dragging = recoDraggingRef.current;
      const dragDate = recoDragDateRef.current;
      if (dragging && dragDate) {
        const recos = recommendationsRef.current;
        const reco = recos.find(r => r.id === dragging.recoId);
        if (reco) {
          const newStart = dragging.bound === 'start' ? dragDate : reco.start_date;
          const newEnd = dragging.bound === 'end' ? dragDate : reco.end_date;
          if (newStart <= newEnd) {
            try {
              const updated = await quizRecommendationService.update(reco.id, {
                start_date: newStart,
                end_date: newEnd,
              });
              setRecommendations(prev =>
                prev.map(r => r.id === reco.id ? updated : r)
              );
            } catch { toast.error('Erreur de mise à jour'); }
          }
        }
      }
      setRecoDragging(null);
      setRecoDragDate(null);
    };
    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, []);

  const loadFamilyMembers = async () => {
    try {
      setLoadingFamily(true);
      const data = await parentService.getAll();
      setFamilyMembers(Array.isArray(data) ? data : (data as any).results || []);
    } catch (error) {
      console.error('Error loading family members:', error);
      toast.error('Erreur lors du chargement des membres de famille');
    } finally {
      setLoadingFamily(false);
    }
  };

  const openFamilyDialog = (parent?: ParentModel) => {
    if (parent) {
      setEditingParent(parent);
      setParentFirstName(parent.first_name);
      setParentLastName(parent.last_name);
      setParentPhoneNumber(parent.phone_number);
      setParentEmail(parent.email || '');
      setParentPassword('');
      setParentGenderValue(parent.gender || '');
      setParentLinkedChildren(parent.children || []);
    } else {
      setEditingParent(null);
      setParentFirstName('');
      setParentLastName('');
      setParentPhoneNumber('');
      setParentEmail('');
      setParentPassword('');
      setParentGenderValue('');
      setParentLinkedChildren(selectedStudent ? [selectedStudent.id] : []);
    }
    setFamilyDialogOpen(true);
  };

  const closeFamilyDialog = () => {
    setFamilyDialogOpen(false);
    setEditingParent(null);
    setCreatingParentFromStudentDialog(false);
  };

  const handleSaveFamilyMember = async () => {
    try {
      const data: CreateParentData = {
        first_name: parentFirstName,
        last_name: parentLastName,
        phone_number: parentPhoneNumber,
        email: parentEmail || undefined,
        gender: parentGenderValue || undefined,
        password: parentPassword || undefined,
      };

      if (editingParent) {
        await parentService.update(editingParent.id, data);
        if (parentPassword) {
          await parentService.setPassword(editingParent.id, parentPassword);
        }
        await parentService.linkChildren(editingParent.id, parentLinkedChildren);
        toast.success('Membre de famille mis à jour');
      } else {
        if (!parentPassword) {
          toast.error('Le mot de passe est obligatoire pour un nouveau membre');
          return;
        }
        const created = await parentService.create({ ...data, password: parentPassword });
        await parentService.linkChildren(created.id, parentLinkedChildren);
        toast.success('Membre de famille créé');
        if (creatingParentFromStudentDialog) {
          setSelectedParentId(created.id);
        }
      }
      closeFamilyDialog();
      loadFamilyMembers();
      if (isErwan && parentPassword) studentService.getPlaintextPasswords().then(setPlaintextPasswords).catch(() => {});
    } catch (error: any) {
      console.error('Error saving family member:', error);
      const detail = error?.response?.data;
      if (detail && typeof detail === 'object') {
        const messages = Object.entries(detail).map(([k, v]) => `${k}: ${v}`).join(', ');
        toast.error(messages);
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    }
  };

  const handleDeleteFamilyMember = async (id: number) => {
    if (!window.confirm('Supprimer ce membre de famille ?')) return;
    try {
      await parentService.delete(id);
      toast.success('Membre de famille supprimé');
      loadFamilyMembers();
    } catch (error) {
      console.error('Error deleting family member:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const loadAllTimeSlots = async (viewTeacherId?: number | null) => {
    try {
      const params: any = {};
      if (viewTeacherId) params.view_teacher = viewTeacherId;
      const data = await timeSlotService.getAll(params);
      setAllTimeSlots(data);
    } catch (error) {
      console.error('Error loading all time slots:', error);
    }
  };

  const loadCalendarTeachers = async () => {
    try {
      const resp = await api.get('/organizations/colleagues_granting_timeslots/');
      setCalendarTeachers(resp.data || []);
    } catch (e) {
      console.error('Error loading calendar teachers:', e);
    }
  };

  const loadStudents = async () => {
    try {
      setLoading(true);
      const data = await studentService.getAll();
      setStudents(data);
    } catch (error) {
      console.error('Error loading students:', error);
      toast.error('Erreur lors du chargement des élèves');
    } finally {
      setLoading(false);
    }
  };

  const loadClasses = async () => {
    try {
      const data = await classService.getClassNames();
      setClasses(data);
    } catch (error) {
      console.error('Error loading classes:', error);
      toast.error('Erreur lors du chargement des classes');
    }
  };

  const loadTimeSlots = async (studentId: number) => {
    try {
      const data = await timeSlotService.getByStudent(studentId);
      setTimeSlots(data);
    } catch (error) {
      console.error('Error loading time slots:', error);
      toast.error('Erreur lors du chargement des créneaux');
    }
  };

  const loadInvoices = async (studentId: number) => {
    try {
      const data = await invoiceService.getByStudent(studentId);
      setInvoices(data);
    } catch (error) {
      console.error('Error loading invoices:', error);
      toast.error('Erreur lors du chargement des factures');
    }
  };

  const loadAllInvoices = async (includeShared = false) => {
    try {
      const params: any = {};
      if (includeShared) params.include_shared = 'true';
      const data = await invoiceService.getAll(params);
      setAllInvoices(data);
    } catch (error) {
      console.error('Error loading all invoices:', error);
      toast.error('Erreur lors du chargement des factures');
    }
  };

  const loadQuizSessions = async (studentId: number) => {
    try {
      const data = await quizSessionService.getByStudent(studentId);
      // Sort sessions chronologically by assigned_date
      const sortedData = data.sort((a, b) =>
        new Date(a.assigned_date).getTime() - new Date(b.assigned_date).getTime()
      );
      setQuizSessions(sortedData);
    } catch (error) {
      console.error('Error loading quiz sessions:', error);
      toast.error('Erreur lors du chargement des sessions de quiz');
    }
  };

  const loadNextQuizItems = async (studentId: number) => {
    try {
      const items = await nextQuizItemService.getByStudent(studentId);

      // Convert backend items to frontend format
      const convertedItems: SelectedQuizItem[] = items.map(item => ({
        type: item.item_type,
        id: item.item_type === 'quiz' ? item.quiz! : item.container!,
        data: item.item_type === 'quiz' ? item.quiz_data : item.container_data,
        excludedQuizIds: item.excluded_quiz_ids,
        allQuizzes: item.container_quizzes_data || undefined,
      }));

      setSelectedQuizItems(convertedItems);
    } catch (error) {
      console.error('Error loading next quiz items:', error);
    }
  };

  const saveNextQuizItems = async (studentId: number, items: SelectedQuizItem[]) => {
    try {
      // Convert frontend items to backend format
      const backendItems = items.map((item, index) => ({
        item_type: item.type,
        quiz: item.type === 'quiz' ? item.id : null,
        container: item.type === 'container' ? item.id : null,
        excluded_quiz_ids: item.excludedQuizIds || [],
        container_quizzes_data: item.allQuizzes || null,
        order: index,
      }));

      await nextQuizItemService.bulkReplace(studentId, backendItems);
    } catch (error) {
      console.error('Error saving next quiz items:', error);
      toast.error('Erreur lors de la sauvegarde de la configuration');
    }
  };

  const handleSelectStudent = (student: Student) => {
    setSelectedStudent(student);
    setShowAllPayments(false);
    setShowFamilyView(false);
    setFirstName(student.first_name);
    setLastName(student.last_name || '');
    setEmail(student.email || '');
    setClassGroup(student.class_group);
    setPhoneNumber(student.phone_number || '');
    setParentGender(student.parent_gender || '');
    setParentPhone(student.parent_phone || '');
    loadTimeSlots(student.id);
    loadInvoices(student.id);
    loadQuizSessions(student.id);
    loadNextQuizItems(student.id);
    loadObjectiveAssignments(student.id);
    loadObjectiveTree();
  };

  const handleNew = () => {
    setDialogOpen(true);
    setSelectedStudent(null);
    resetForm();
  };

  const handleEdit = (student: Student) => {
    setSelectedStudent(student);
    setFirstName(student.first_name);
    setLastName(student.last_name || '');
    setEmail(student.email || '');
    setClassGroup(student.class_group);
    setPhoneNumber(student.phone_number || '');
    setParentGender(student.parent_gender || '');
    setParentPhone(student.parent_phone || '');
    setSelectedColor(student.color || '6ea8fe');
    setHourlyRate(student.hourly_rate?.toString() || '3000');
    setMathsSpecialite(student.maths_specialite ?? true);
    // Find linked parent (by M2M children, or fallback by phone number)
    const linkedParent = familyMembers.find(p => p.children?.includes(student.id))
      || (student.parent_phone ? familyMembers.find(p => p.phone_number === student.parent_phone) : null);
    setSelectedParentId(linkedParent ? linkedParent.id : null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    // Trim all text fields
    const trimmedFirstName = firstName?.trim();
    const trimmedEmail = email?.trim();

    if (!classGroup) {
      toast.error('La classe est requise');
      return;
    }

    // Validate email format if provided
    if (trimmedEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        toast.error('Veuillez entrer un email valide');
        return;
      }
    }

    try {
      const studentData: any = {
        first_name: trimmedFirstName || null,
        class_group: classGroup,
        last_name: lastName?.trim() || null,
        email: trimmedEmail || null,
        phone_number: phoneNumber?.trim() || null,
        parent_gender: parentGender || null,
        parent_phone: parentPhone?.trim() || null,
        color: selectedColor,
        hourly_rate: parseInt(hourlyRate) || 3000,
        maths_specialite: mathsSpecialite,
      };

      let savedStudentId: number;
      if (selectedStudent && dialogOpen) {
        const updatedStudent = await studentService.update(selectedStudent.id, studentData);
        toast.success('Élève mis à jour');
        setSelectedStudent(updatedStudent);
        savedStudentId = updatedStudent.id;
      } else {
        const newStudent = await studentService.create(studentData);
        toast.success('Élève créé');
        handleSelectStudent(newStudent);
        savedStudentId = newStudent.id;
      }

      // Link/unlink parent
      if (selectedParentId) {
        const parent = familyMembers.find(p => p.id === selectedParentId);
        if (parent && !parent.children?.includes(savedStudentId)) {
          await parentService.linkChildren(selectedParentId, [...(parent.children || []), savedStudentId]);
        }
        // Unlink from previous parent if changed
        const previousParent = familyMembers.find(p => p.id !== selectedParentId && p.children?.includes(savedStudentId));
        if (previousParent) {
          await parentService.linkChildren(previousParent.id, previousParent.children.filter(id => id !== savedStudentId));
        }
      } else if (selectedStudent) {
        // No parent selected - unlink from any existing parent
        const previousParent = familyMembers.find(p => p.children?.includes(savedStudentId));
        if (previousParent) {
          await parentService.linkChildren(previousParent.id, previousParent.children.filter(id => id !== savedStudentId));
        }
      }

      setDialogOpen(false);
      loadStudents();
      loadAllTimeSlots();
      loadFamilyMembers();
    } catch (error: any) {
      console.error('Error saving student:', error);
      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.email) {
          toast.error('Cet email est déjà utilisé');
        } else {
          toast.error('Erreur lors de la sauvegarde');
        }
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet élève ?')) {
      return;
    }

    try {
      await studentService.delete(id);
      toast.success('Élève supprimé');
      if (selectedStudent?.id === id) {
        setSelectedStudent(null);
        resetForm();
      }
      loadStudents();
    } catch (error) {
      console.error('Error deleting student:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleOpenPasswordDialog = () => {
    setNewPassword('');
    setConfirmPassword('');
    setPasswordDialogOpen(true);
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleSavePassword = async () => {
    if (!selectedStudent) return;

    if (!newPassword) {
      toast.error('Veuillez saisir un mot de passe');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    try {
      await studentService.update(selectedStudent.id, { password: newPassword });
      toast.success('Mot de passe modifié avec succès');
      if (isErwan) studentService.getPlaintextPasswords().then(setPlaintextPasswords).catch(() => {});
      handleClosePasswordDialog();
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error('Erreur lors de la modification du mot de passe');
    }
  };

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setClassGroup('');
    setPhoneNumber('');
    setParentGender('');
    setParentPhone('');
    setSelectedColor('6ea8fe');
    setHourlyRate('3000');
    setMathsSpecialite(true);
    setSelectedParentId(null);
  };

  // Calendar utilities
  const DAYS_ORDER = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

  const getNCTime = () => {
    return new Date(new Date().toLocaleString('en-US', { timeZone: 'Pacific/Noumea' }));
  };

  const getNCDayName = () => {
    const ncNow = getNCTime();
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    return days[ncNow.getDay()];
  };

  const getDayAmount = (day: string) => {
    return allTimeSlots
      .filter(slot => slot.day === day)
      .reduce((sum, slot) => {
        const student = students.find(s => s.id === slot.student);
        const rate = student?.hourly_rate || 3000;
        const [sh, sm] = slot.start_time.split(':').map(Number);
        const [eh, em] = slot.end_time.split(':').map(Number);
        const durationHours = (eh * 60 + em - sh * 60 - sm) / 60;
        return sum + Math.round(durationHours * rate);
      }, 0);
  };

  // Total working minutes for a day (merge overlapping slots)
  const getDayMinutes = (day: string) => {
    const intervals = allTimeSlots
      .filter(slot => slot.day === day)
      .map(slot => {
        const [sh, sm] = slot.start_time.split(':').map(Number);
        const [eh, em] = slot.end_time.split(':').map(Number);
        return { start: sh * 60 + sm, end: eh * 60 + em };
      })
      .sort((a, b) => a.start - b.start);

    if (intervals.length === 0) return 0;

    // Merge overlapping intervals
    const merged = [intervals[0]];
    for (let i = 1; i < intervals.length; i++) {
      const last = merged[merged.length - 1];
      if (intervals[i].start <= last.end) {
        last.end = Math.max(last.end, intervals[i].end);
      } else {
        merged.push({ ...intervals[i] });
      }
    }

    return merged.reduce((sum, iv) => sum + (iv.end - iv.start), 0);
  };

  const formatMinutes = (totalMin: number) => {
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return m > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
  };

  const getWeekMinutes = () => {
    return DAYS_ORDER.reduce((sum, day) => sum + getDayMinutes(day), 0);
  };

  const getWeekTotal = () => {
    return DAYS_ORDER.reduce((sum, day) => sum + getDayAmount(day), 0);
  };

  const getCurrentEarnings = () => {
    const ncNow = getNCTime();
    const currentDay = getNCDayName();
    const currentMinutes = ncNow.getHours() * 60 + ncNow.getMinutes();

    return Math.round(allTimeSlots
      .filter(slot => slot.day === currentDay)
      .reduce((sum, slot) => {
        const student = students.find(s => s.id === slot.student);
        const rate = student?.hourly_rate || 3000;
        const [sh, sm] = slot.start_time.split(':').map(Number);
        const [eh, em] = slot.end_time.split(':').map(Number);
        const startMin = sh * 60 + sm;
        const endMin = eh * 60 + em;

        if (currentMinutes <= startMin) return sum;
        const elapsedMin = Math.min(currentMinutes - startMin, endMin - startMin);
        return sum + (elapsedMin / 60) * rate;
      }, 0));
  };

  // Punctual time slot utilities
  const getNCWeekBounds = (weekOffset = 0) => {
    const nc = getNCTime();
    const dow = nc.getDay(); // 0=Sun
    const mondayOffset = dow === 0 ? -6 : 1 - dow;
    const monday = new Date(nc);
    monday.setDate(nc.getDate() + mondayOffset + weekOffset * 7);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { monday: fmt(monday), sunday: fmt(sunday) };
  };

  const dayOffsetToDate = (dayName: string, weeksOffset: number) => {
    const nc = getNCTime();
    const dow = nc.getDay();
    const mondayOffset = dow === 0 ? -6 : 1 - dow;
    const monday = new Date(nc);
    monday.setDate(nc.getDate() + mondayOffset + weeksOffset * 7);
    const dayMap: Record<string, number> = { 'Lundi': 0, 'Mardi': 1, 'Mercredi': 2, 'Jeudi': 3, 'Vendredi': 4, 'Samedi': 5, 'Dimanche': 6 };
    const target = new Date(monday);
    target.setDate(monday.getDate() + (dayMap[dayName] || 0));
    return `${target.getFullYear()}-${String(target.getMonth() + 1).padStart(2, '0')}-${String(target.getDate()).padStart(2, '0')}`;
  };

  const dateToDayName = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    return days[d.getDay()];
  };

  const loadPunctualTimeSlots = async () => {
    try {
      const { monday } = getNCWeekBounds();
      // Load current week + 3 future weeks
      const endDate = new Date(monday + 'T00:00:00');
      endDate.setDate(endDate.getDate() + 27); // 4 weeks total
      const sundayFar = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
      const data = await punctualTimeSlotService.getByWeek(monday, sundayFar);
      setPunctualTimeSlots(data);
    } catch (error) {
      console.error('Error loading punctual time slots:', error);
    }
  };

  const handleAddPunctualSlot = async () => {
    if (!punctualStudent) return;
    let dateStr: string;
    if (punctualDateMode === 'picker') {
      dateStr = punctualDate;
    } else {
      dateStr = dayOffsetToDate(punctualDay, punctualWeeksOffset);
    }
    if (!dateStr) { toast.error('Veuillez sélectionner une date'); return; }

    const startTime = `${String(punctualStartHour).padStart(2, '0')}:${String(punctualStartMinute).padStart(2, '0')}:00`;
    const endTime = `${String(punctualEndHour).padStart(2, '0')}:${String(punctualEndMinute).padStart(2, '0')}:00`;

    try {
      await punctualTimeSlotService.create({ student: punctualStudent as number, date: dateStr, start_time: startTime, end_time: endTime });
      toast.success('Cours ponctuel ajouté');
      loadPunctualTimeSlots();
      setPunctualStudent('');
      setPunctualStartHour(8); setPunctualStartMinute(0);
      setPunctualEndHour(9); setPunctualEndMinute(0);
    } catch (error) {
      console.error('Error adding punctual slot:', error);
      toast.error("Erreur lors de l'ajout du cours ponctuel");
    }
  };

  const handleDeletePunctualSlot = async (id: number) => {
    try {
      await punctualTimeSlotService.delete(id);
      toast.success('Cours ponctuel supprimé');
      loadPunctualTimeSlots();
    } catch (error) {
      console.error('Error deleting punctual slot:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const getPunctualDayAmount = (day: string, weekOffset = 0) => {
    const { monday, sunday } = getNCWeekBounds(weekOffset);
    return punctualTimeSlots
      .filter(slot => slot.date >= monday && slot.date <= sunday && dateToDayName(slot.date) === day)
      .reduce((sum, slot) => {
        const student = students.find(s => s.id === slot.student);
        const rate = student?.hourly_rate || 3000;
        const [sh, sm] = slot.start_time.split(':').map(Number);
        const [eh, em] = slot.end_time.split(':').map(Number);
        return sum + Math.round(((eh * 60 + em - sh * 60 - sm) / 60) * rate);
      }, 0);
  };

  const getPunctualWeekTotal = (weekOffset = 0) => DAYS_ORDER.reduce((sum, day) => sum + getPunctualDayAmount(day, weekOffset), 0);

  const getPunctualAllTotal = () => {
    return punctualTimeSlots.reduce((sum, slot) => {
      const student = students.find(s => s.id === slot.student);
      const rate = student?.hourly_rate || 3000;
      const [sh, sm] = slot.start_time.split(':').map(Number);
      const [eh, em] = slot.end_time.split(':').map(Number);
      return sum + Math.round(((eh * 60 + em - sh * 60 - sm) / 60) * rate);
    }, 0);
  };

  // Timer for calendar dialog + load colleague teachers
  useEffect(() => {
    if (!calendarDialogOpen) return;
    loadCalendarTeachers();
    const interval = setInterval(() => {
      setNcTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, [calendarDialogOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Quiz timeline helpers
  const getQuizPeriodBounds = (period: string, offset: number) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const periodDays: Record<string, number> = { month: 30, week: 7, '5days': 5, '3days': 3, day: 1 };
    const days = periodDays[period] || 7;
    const to = new Date(today);
    to.setDate(to.getDate() + offset * days);
    if (to > today) { to.setTime(today.getTime()); }
    const from = new Date(to);
    from.setDate(from.getDate() - days);
    from.setHours(0, 0, 0, 0);
    return { from, to };
  };

  const getPeriodLabel = (period: string, offset: number) => {
    const { from, to } = getQuizPeriodBounds(period, offset);
    const fmt = (d: Date) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
    return `${fmt(from)} – ${fmt(to)}`;
  };

  const groupAttemptsIntoSessions = (attempts: {student: number, completed_date: string, was_correct: boolean}[]) => {
    const sorted = [...attempts].sort((a, b) => new Date(a.completed_date).getTime() - new Date(b.completed_date).getTime());
    const sessions: { student: number, start: Date, end: Date, count: number, correct: number }[] = [];
    let current: typeof sessions[0] | null = null;

    for (const a of sorted) {
      const t = new Date(a.completed_date);
      if (current && current.student === a.student && (t.getTime() - current.end.getTime()) < 5 * 60 * 1000) {
        current.end = t;
        current.count++;
        if (a.was_correct) current.correct++;
      } else {
        if (current) sessions.push(current);
        current = { student: a.student, start: t, end: t, count: 1, correct: a.was_correct ? 1 : 0 };
      }
    }
    if (current) sessions.push(current);
    return sessions;
  };

  // Load quiz attempts when timeline dialog opens or period/offset changes + polling
  useEffect(() => {
    if (!quizTimelineOpen) return;
    let isFirst = true;
    const load = async () => {
      if (isFirst) setQuizTimelineLoading(true);
      try {
        const { from } = getQuizPeriodBounds(quizTimelinePeriod, quizTimelineOffset);
        const statsFrom = new Date(from);
        statsFrom.setDate(statsFrom.getDate() - 35);
        const today = new Date();
        const fmtDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const data = await quizSessionService.getAttemptsTimeline(fmtDate(statsFrom), fmtDate(today));
        setQuizAttempts(data);
      } catch (e) {
        console.error('Failed to load quiz attempts', e);
      } finally {
        if (isFirst) setQuizTimelineLoading(false);
        isFirst = false;
      }
    };
    load();
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, [quizTimelineOpen, quizTimelinePeriod, quizTimelineOffset]);

  const handleAddTimeSlot = async () => {
    if (!selectedStudent) return;

    const startTime = `${String(newSlotStartHour).padStart(2, '0')}:${String(newSlotStartMinute).padStart(2, '0')}:00`;
    const endTime = `${String(newSlotEndHour).padStart(2, '0')}:${String(newSlotEndMinute).padStart(2, '0')}:00`;

    try {
      await timeSlotService.create({
        student: selectedStudent.id,
        day: newSlotDay,
        start_time: startTime,
        end_time: endTime,
      });
      toast.success('Créneau ajouté');
      loadTimeSlots(selectedStudent.id);
      loadAllTimeSlots();
      // Reset to default values
      setNewSlotDay('Lundi');
      setNewSlotStartHour(8);
      setNewSlotStartMinute(0);
      setNewSlotEndHour(9);
      setNewSlotEndMinute(0);
    } catch (error) {
      console.error('Error adding time slot:', error);
      toast.error('Erreur lors de l\'ajout du créneau');
    }
  };

  const handleDeleteTimeSlot = async (slotId: number) => {
    if (!selectedStudent) return;

    try {
      await timeSlotService.delete(slotId);
      toast.success('Créneau supprimé');
      loadTimeSlots(selectedStudent.id);
      loadAllTimeSlots();
    } catch (error) {
      console.error('Error deleting time slot:', error);
      toast.error('Erreur lors de la suppression du créneau');
    }
  };

  const handleSendNotification = async () => {
    if (!selectedStudent || !notificationTitle.trim() || !notificationBody.trim()) return;

    setSendingNotification(true);
    try {
      const payload: SendNotificationData = {
        recipient_type: notificationRecipient,
        student: selectedStudent.id,
        title: notificationTitle.trim(),
        body: notificationBody.trim(),
      };

      if (notificationRecipient === 'parent') {
        const linkedParent = familyMembers.find(p => p.children?.includes(selectedStudent.id));
        if (linkedParent) payload.parent = linkedParent.id;
      }

      const newNotif = await notificationService.send(payload);
      setTeacherNotifications(prev => [newNotif, ...prev]);
      setNotificationTitle('');
      setNotificationBody('');
      toast.success('Notification envoyée');
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error("Erreur lors de l'envoi de la notification");
    } finally {
      setSendingNotification(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    if (selectedStudent) {
      handleSelectStudent(selectedStudent);
    } else {
      resetForm();
    }
  };

  // Invoice management functions
  const handleQuickDuration = (hours: number, minutes: number = 0) => {
    if (!invoiceStartTime) {
      toast.error('Veuillez d\'abord sélectionner l\'heure de début');
      return;
    }
    const endTime = invoiceStartTime.add(hours, 'hour').add(minutes, 'minute');
    setInvoiceEndTime(endTime);
    calculateDurationAndPrice(invoiceStartTime, endTime);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInvoiceDate(e.target.value);
    // Ouvrir automatiquement le picker d'heure de début après la sélection de la date
    if (e.target.value) {
      setTimeout(() => setStartTimePickerOpen(true), 300);
    }
  };

  const calculateDurationAndPrice = (startTime: Dayjs | null, endTime: Dayjs | null) => {
    if (startTime && endTime) {
      const durationInMinutes = endTime.diff(startTime, 'minute');
      const durationInHours = durationInMinutes / 60;
      const rate = selectedStudent?.hourly_rate || 3000;
      const calculatedPrice = Math.round(durationInHours * rate);
      setInvoicePrice(calculatedPrice.toString());
      return durationInHours;
    }
    return null;
  };

  const formatDuration = (startTime: Dayjs | null, endTime: Dayjs | null): string => {
    if (!startTime || !endTime) return '';
    const durationInMinutes = endTime.diff(startTime, 'minute');
    const hours = Math.floor(durationInMinutes / 60);
    const minutes = durationInMinutes % 60;

    if (minutes === 0) {
      return `${hours}h`;
    }
    return `${hours}h${minutes.toString().padStart(2, '0')}`;
  };

  const handleStartTimeChange = (newValue: Dayjs | null) => {
    setInvoiceStartTime(newValue);
    calculateDurationAndPrice(newValue, invoiceEndTime);
  };

  const handleStartTimeAccept = (newValue: Dayjs | null) => {
    setInvoiceStartTime(newValue);
    calculateDurationAndPrice(newValue, invoiceEndTime);
    setStartTimePickerOpen(false);
    // Ouvrir automatiquement le picker d'heure de fin après la sélection de l'heure de début
    if (newValue) {
      setTimeout(() => setEndTimePickerOpen(true), 300);
    }
  };

  const handleEndTimeChange = (newValue: Dayjs | null) => {
    setInvoiceEndTime(newValue);
    calculateDurationAndPrice(invoiceStartTime, newValue);
  };

  const handleCreateInvoice = async () => {
    if (!selectedStudent || !invoiceDate || !invoiceStartTime || !invoiceEndTime || !invoicePrice) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      await invoiceService.create({
        student: selectedStudent.id,
        date: invoiceDate,
        start_time: invoiceStartTime.format('HH:mm:ss'),
        end_time: invoiceEndTime.format('HH:mm:ss'),
        price: parseFloat(invoicePrice),
        discount: invoiceDiscount ? parseFloat(invoiceDiscount) : undefined,
        discount_label: invoiceDiscountLabel || undefined,
        status: 'draft',
      });

      toast.success('Facture créée');
      loadInvoices(selectedStudent.id);
      setInvoiceDialogOpen(false);
      setInvoiceDate('');
      setInvoiceStartTime(null);
      setInvoiceEndTime(null);
      setInvoicePrice('6000');
      setInvoiceDiscount('');
      setInvoiceDiscountLabel('');
      setStartTimePickerOpen(false);
      setEndTimePickerOpen(false);
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error('Erreur lors de la création de la facture');
    }
  };

  const handleOpenEditInvoice = (invoice: Invoice) => {
    setEditingInvoice(invoice);
    setInvoiceDate(invoice.date);

    // Parse time strings to Dayjs objects
    const startTime = dayjs(`2000-01-01 ${invoice.start_time}`);
    const endTime = dayjs(`2000-01-01 ${invoice.end_time}`);

    setInvoiceStartTime(startTime);
    setInvoiceEndTime(endTime);
    setInvoicePrice(invoice.price.toString());
    setInvoiceDiscount(invoice.discount ? invoice.discount.toString() : '');
    setInvoiceDiscountLabel(invoice.discount_label || '');

    // Load payment method if invoice is paid
    if (invoice.payment_method) {
      setPaymentMethod(invoice.payment_method);
    }

    setEditInvoiceDialogOpen(true);
  };

  const handleUpdateInvoice = async () => {
    if (!editingInvoice || !selectedStudent || !invoiceDate || !invoiceStartTime || !invoiceEndTime || !invoicePrice) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const updateData: any = {
        date: invoiceDate,
        start_time: invoiceStartTime.format('HH:mm:ss'),
        end_time: invoiceEndTime.format('HH:mm:ss'),
        price: parseFloat(invoicePrice),
        discount: invoiceDiscount ? parseFloat(invoiceDiscount) : undefined,
        discount_label: invoiceDiscountLabel || undefined,
      };

      // Include payment_method if invoice is paid
      if (editingInvoice.status === 'paid') {
        updateData.payment_method = paymentMethod;
      }

      await invoiceService.update(editingInvoice.id, updateData);

      toast.success('Facture modifiée');
      loadInvoices(selectedStudent.id);
      setEditInvoiceDialogOpen(false);
      setEditingInvoice(null);
      setInvoiceDate('');
      setInvoiceStartTime(null);
      setInvoiceEndTime(null);
      setInvoicePrice('6000');
      setInvoiceDiscount('');
      setInvoiceDiscountLabel('');
      setPaymentMethod('cheque');
      setStartTimePickerOpen(false);
      setEndTimePickerOpen(false);
    } catch (error) {
      console.error('Error updating invoice:', error);
      toast.error('Erreur lors de la modification de la facture');
    }
  };

  const handleGenerateInvoicePdf = async (invoiceId: number) => {
    try {
      await invoiceService.generatePdf(invoiceId);
      toast.success('PDF de la facture généré');
      if (selectedStudent) {
        loadInvoices(selectedStudent.id);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  const handleDownloadInvoicePdf = async (invoiceId: number) => {
    try {
      const blob = await invoiceService.downloadPdf(invoiceId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `facture_${invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Facture téléchargée');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Erreur lors du téléchargement du PDF');
    }
  };

  const handleConfirmPayment = async () => {
    if (!selectedStudent) return;

    // Utiliser invoicesToMarkPaid si on a plusieurs factures, sinon invoiceToMarkPaid
    const invoiceIds = invoicesToMarkPaid.length > 0 ? invoicesToMarkPaid : (invoiceToMarkPaid ? [invoiceToMarkPaid] : []);
    if (invoiceIds.length === 0) return;

    try {
      await invoiceService.batchMarkPaid({
        invoice_ids: invoiceIds,
        student_id: selectedStudent.id,
        payment_method: paymentMethod,
        discount: paymentDiscount ? parseFloat(paymentDiscount) : undefined,
        discount_label: paymentDiscountLabel || undefined,
      });

      if (invoiceIds.length > 1) {
        toast.success(`${invoiceIds.length} factures marquées comme réglées`);
      } else {
        toast.success('Facture marquée comme réglée et PDF généré');
      }

      loadInvoices(selectedStudent.id);
      setPaymentDialogOpen(false);
      setInvoiceToMarkPaid(null);
      setInvoicesToMarkPaid([]);
      setSelectedInvoices([]);
      setPaymentMethod('cheque');
      setPaymentDiscount('');
      setPaymentDiscountLabel('');
    } catch (error) {
      console.error('Error marking invoices as paid:', error);
      toast.error('Erreur lors du règlement');
    }
  };

  const handleInvoiceDragEnd = async (result: DropResult) => {
    const { destination, draggableId } = result;

    if (!destination || !selectedStudent) return;

    const draggedInvoiceId = parseInt(draggableId);

    // Déterminer quelles factures déplacer : si la facture draggée fait partie de la sélection, déplacer toutes les factures sélectionnées
    const invoiceIdsToMove = selectedInvoices.includes(draggedInvoiceId) && selectedInvoices.length > 0
      ? selectedInvoices
      : [draggedInvoiceId];

    // If dropped in trash
    if (destination.droppableId === 'trash') {
      try {
        for (const invoiceId of invoiceIdsToMove) {
          await invoiceService.delete(invoiceId);
        }
        if (invoiceIdsToMove.length > 1) {
          toast.success(`${invoiceIdsToMove.length} factures supprimées`);
        } else {
          toast.success('Facture supprimée');
        }
        setSelectedInvoices([]);
        loadInvoices(selectedStudent.id);
      } catch (error) {
        console.error('Error deleting invoices:', error);
        toast.error('Erreur lors de la suppression');
      }
      return;
    }

    // Moving between columns
    const newStatus = destination.droppableId as 'draft' | 'issued' | 'paid';

    // If moving to "paid", open payment dialog
    if (newStatus === 'paid') {
      // Si plusieurs factures sont sélectionnées, les stocker pour paiement groupé
      if (invoiceIdsToMove.length > 1) {
        setInvoicesToMarkPaid(invoiceIdsToMove);
        setInvoiceToMarkPaid(null);
      } else {
        setInvoiceToMarkPaid(draggedInvoiceId);
        setInvoicesToMarkPaid([]);
      }

      // Calculer la réduction totale si plusieurs factures
      const selectedInvoiceObjects = invoices.filter(inv => invoiceIdsToMove.includes(inv.id));
      const totalDiscount = selectedInvoiceObjects.reduce((sum, inv) => sum + (inv.discount || 0), 0);
      setPaymentDiscount(totalDiscount > 0 ? totalDiscount.toString() : '');
      setPaymentDiscountLabel('');
      setPaymentDialogOpen(true);
      return;
    }

    // For other status changes, update directly
    try {
      for (const invoiceId of invoiceIdsToMove) {
        await invoiceService.update(invoiceId, { status: newStatus });
      }
      setSelectedInvoices([]);
      loadInvoices(selectedStudent.id);
    } catch (error) {
      console.error('Error updating invoice status:', error);
      toast.error('Erreur lors du déplacement');
    }
  };

  const getInvoicesByStatus = (status: 'draft' | 'issued' | 'paid') => {
    return invoices.filter(inv => inv.status === status);
  };

  const getTotalByStatus = (status: 'draft' | 'issued' | 'paid') => {
    const statusInvoices = getInvoicesByStatus(status);
    return statusInvoices.reduce((total, inv) => {
      const amount = inv.price - (inv.discount || 0);
      return total + amount;
    }, 0);
  };

  const formatInvoiceDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const dayName = days[date.getDay()];
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${dayName} ${day}/${month}/${year}`;
  };

  const formatPaymentMethod = (method?: string) => {
    const methods: Record<string, string> = {
      'cheque': 'Chèque',
      'cash': 'Espèces',
      'transfer': 'Virement',
      'card': 'Carte',
      'nature': 'Nature',
    };
    return method ? methods[method] || method : '';
  };

  const formatPaymentDate = (dateStr?: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} à ${hours}:${minutes}`;
  };

  const loadObjectiveAssignments = async (studentId: number) => {
    try {
      const assignments = await objectiveAssignmentService.getAssignments({ student: studentId });
      setObjectiveAssignments(assignments);

      // Build assignment tree
      await buildAssignmentTree(assignments);
    } catch (error) {
      console.error('Error loading objective assignments:', error);
    }
  };

  const buildAssignmentTree = async (assignments: ObjectiveAssignment[]) => {
    try {
      // Map to store container info
      const containerMap = new Map<number, ObjectiveContainer>();
      const objectiveMap = new Map<number, Objective>();
      const assignmentsByContainer = new Map<number | null, ObjectiveAssignment[]>();

      // Load full objective data for each assignment and group by container
      for (const assignment of assignments) {
        const objective: Objective = await objectiveService.getObjective(assignment.objective);
        objectiveMap.set(objective.id, objective);
        const containerId = objective.container || null;

        if (!assignmentsByContainer.has(containerId)) {
          assignmentsByContainer.set(containerId, []);
        }
        assignmentsByContainer.get(containerId)!.push(assignment);

        // Load container chain for this objective
        if (objective.container) {
          let currentContainerId: number | null = objective.container;
          while (currentContainerId !== null) {
            if (!containerMap.has(currentContainerId)) {
              const container: ObjectiveContainer = await objectiveContainerService.getContainer(currentContainerId);
              containerMap.set(currentContainerId, container);
              currentContainerId = container.parent;
            } else {
              break;
            }
          }
        }
      }

      // Load all child containers for each container in the map
      // This ensures we have the complete hierarchy, including intermediate containers
      const loadChildContainers = async (parentId: number) => {
        try {
          const childContainers = await objectiveContainerService.getContainers(parentId);
          for (const childContainer of childContainers) {
            if (!containerMap.has(childContainer.id)) {
              containerMap.set(childContainer.id, childContainer);
              await loadChildContainers(childContainer.id);
            }
          }
        } catch (error) {
          // Silently ignore errors when loading child containers
          // This can happen if a container was deleted or is inaccessible
          console.warn(`Failed to load child containers for parent ${parentId}:`, error);
        }
      };

      // Load child containers for all containers we already have
      const containerIds = Array.from(containerMap.keys());
      for (const containerId of containerIds) {
        await loadChildContainers(containerId);
      }

      // Calculate weighted average grade for a node and its children
      const calculateAverageGrade = (node: AssignmentTreeNode): number | undefined => {
        if (node.type === 'assignment') {
          // Return the grade if set, otherwise return 0 (not undefined)
          return node.assignment?.grade !== null && node.assignment?.grade !== undefined
            ? node.assignment.grade
            : 0;
        }

        // For containers, calculate weighted average of all children
        if (node.children && node.children.length > 0) {
          let weightedSum = 0;
          let totalWeight = 0;

          for (const child of node.children) {
            const childGrade = calculateAverageGrade(child);
            // Now childGrade is always defined (0 for ungraded objectives)
            if (childGrade !== undefined) {
              weightedSum += childGrade * child.weight;
              totalWeight += child.weight;
            }
          }

          if (totalWeight > 0) {
            return Math.round((weightedSum / totalWeight) * 10) / 10; // Round to 1 decimal
          }
        }

        return undefined;
      };

      // Build tree structure
      const buildTree = (parentId: number | null): AssignmentTreeNode[] => {
        const nodes: AssignmentTreeNode[] = [];

        // Find containers at this level
        const childContainers: ObjectiveContainer[] = Array.from(containerMap.values()).filter((c: ObjectiveContainer) => c.parent === parentId);

        for (const container of childContainers) {
          const children = buildTree(container.id);
          const containerAssignments = assignmentsByContainer.get(container.id) || [];

          // Add assignments as children
          for (const assignment of containerAssignments) {
            const objective = objectiveMap.get(assignment.objective);
            children.push({
              id: `assignment-${assignment.id}`,
              title: assignment.objective_title,
              type: 'assignment',
              assignment,
              weight: objective?.weight || 1,
            });
          }

          // Only add container if it has children (sub-containers or assignments)
          if (children.length > 0) {
            const containerNode: AssignmentTreeNode = {
              id: `container-${container.id}`,
              title: container.title,
              type: 'container',
              containerId: container.id,
              children,
              weight: container.weight || 1,
            };

            // Calculate and set average grade
            containerNode.averageGrade = calculateAverageGrade(containerNode);

            nodes.push(containerNode);
          }
        }

        // Add assignments at this level (no container)
        if (parentId === null) {
          const rootAssignments = assignmentsByContainer.get(null) || [];
          for (const assignment of rootAssignments) {
            const objective = objectiveMap.get(assignment.objective);
            nodes.push({
              id: `assignment-${assignment.id}`,
              title: assignment.objective_title,
              type: 'assignment',
              assignment,
              weight: objective?.weight || 1,
            });
          }
        }

        return nodes;
      };

      const tree = buildTree(null);
      setAssignmentTree(tree);
    } catch (error) {
      console.error('Error building assignment tree:', error);
    }
  };

  // Load complete objective tree recursively
  const loadObjectiveTree = async () => {
    try {
      const buildTree = async (parentId: number | null = null): Promise<ObjectiveTreeNode[]> => {
        const nodes: ObjectiveTreeNode[] = [];

        // Load containers at this level
        const containers = await objectiveContainerService.getContainers(parentId);

        // Load objectives at this level
        const objectives = await objectiveService.getAll({ container: parentId });

        // Add containers to tree (recursively load their children)
        for (const container of containers) {
          const children = await buildTree(container.id);
          nodes.push({
            id: `container-${container.id}`,
            title: container.title,
            type: 'container',
            containerId: container.id,
            children,
          });
        }

        // Add objectives to tree
        for (const objective of objectives) {
          nodes.push({
            id: `objective-${objective.id}`,
            title: objective.title,
            type: 'objective',
            objectiveData: objective,
          });
        }

        return nodes;
      };

      const tree = await buildTree(null);
      setObjectiveTree(tree);
    } catch (error) {
      console.error('Error loading objective tree:', error);
    }
  };

  const handleObjectiveDragEnd = async (result: DropResult) => {
    if (!result.destination) return;
    if (!selectedStudent) return;

    const { destination, draggableId } = result;

    // Only handle drops to assigned objectives
    if (destination.droppableId !== 'assigned-objectives') return;

    // Extract type and id from draggableId (format: "container-123" or "objective-123")
    const parts = draggableId.split('-');
    const type = parts[0];
    const id = parseInt(parts[1]);

    if (type === 'container') {
      // Container drag - assign all objectives recursively
      try {
        const getAllObjectivesFromContainer = async (containerId: number): Promise<number[]> => {
          const objectiveIds: number[] = [];

          // Get objectives in this container
          const objectives = await objectiveService.getAll({ container: containerId });
          objectiveIds.push(...objectives.map(obj => obj.id));

          // Get child containers and recursively get their objectives
          const containers = await objectiveContainerService.getContainers(containerId);
          for (const childContainer of containers) {
            const childObjectiveIds = await getAllObjectivesFromContainer(childContainer.id);
            objectiveIds.push(...childObjectiveIds);
          }

          return objectiveIds;
        };

        const objectiveIds = await getAllObjectivesFromContainer(id);

        if (objectiveIds.length === 0) {
          toast.info('Ce dossier ne contient aucun objectif');
          return;
        }

        await objectiveAssignmentService.assignObjectives(selectedStudent.id, objectiveIds);
        toast.success(`${objectiveIds.length} objectif(s) assigné(s)`);
        loadObjectiveAssignments(selectedStudent.id);
      } catch (error) {
        console.error('Error assigning container objectives:', error);
        toast.error('Erreur lors de l\'assignation des objectifs');
      }
    } else if (type === 'objective') {
      // Individual objective drag
      try {
        await objectiveAssignmentService.assignObjectives(selectedStudent.id, [id]);
        toast.success('Objectif assigné');
        loadObjectiveAssignments(selectedStudent.id);
      } catch (error) {
        console.error('Error assigning objective:', error);
        toast.error('Erreur lors de l\'assignation de l\'objectif');
      }
    }
  };


  const loadBrowseSessions = async (studentId: number) => {
    try {
      const data = await quizSessionService.getByStudent(studentId);
      // Sort sessions chronologically by assigned_date
      const sortedData = data.sort((a, b) =>
        new Date(a.assigned_date).getTime() - new Date(b.assigned_date).getTime()
      );
      setBrowseSessions(sortedData);
    } catch (error) {
      console.error('Error loading browse sessions:', error);
    }
  };

  const loadBrowseSessionContent = async (sessionId: number, studentId: number) => {
    try {
      const assignments = await quizAssignmentService.getByStudent(studentId);
      const sessionAssignments = assignments.filter(a => a.session === sessionId);
      setBrowseSessionContent(sessionAssignments);
    } catch (error) {
      console.error('Error loading session content:', error);
    }
  };

  const handleAddQuizItem = (quiz: Quiz) => {
    if (!selectedStudent) return;

    // Check if already added
    if (selectedQuizItems.some(item => item.type === 'quiz' && item.id === quiz.id)) {
      toast.info('Cette question est déjà ajoutée');
      return;
    }

    const newItems = [...selectedQuizItems, {
      type: 'quiz' as const,
      id: quiz.id,
      data: quiz,
    }];

    setSelectedQuizItems(newItems);
    saveNextQuizItems(selectedStudent.id, newItems);
    toast.success('Question ajoutée');
  };

  const handleAddContainerItem = async (container: QuizContainer) => {
    // Check if already added
    if (selectedQuizItems.some(item => item.type === 'container' && item.id === container.id)) {
      toast.info('Ce groupe est déjà ajouté');
      return;
    }

    // Load quizzes in this container to allow customization
    try {
      const contents = await quizContainerService.getContainerContents(container.id);
      setCustomizingContainer(container);
      setContainerQuizzes(contents.quizzes || []);
      setExcludedQuizIds([]);
      setCustomizeDialogOpen(true);
    } catch (error) {
      console.error('Error loading container contents:', error);
      toast.error('Erreur lors du chargement du contenu du groupe');
    }
  };

  const handleConfirmCustomization = () => {
    if (!customizingContainer || !selectedStudent) return;

    // Check if container already exists in selected items
    const existingIndex = selectedQuizItems.findIndex(
      item => item.type === 'container' && item.id === customizingContainer.id
    );

    let newItems: SelectedQuizItem[];

    if (existingIndex >= 0) {
      // Update existing item
      newItems = selectedQuizItems.map((item, index) =>
        index === existingIndex
          ? { ...item, excludedQuizIds, allQuizzes: containerQuizzes }
          : item
      );
      toast.success('Groupe mis à jour');
    } else {
      // Add new item
      newItems = [...selectedQuizItems, {
        type: 'container',
        id: customizingContainer.id,
        data: customizingContainer,
        excludedQuizIds,
        allQuizzes: containerQuizzes,
      }];
      toast.success('Groupe ajouté');
    }

    setSelectedQuizItems(newItems);
    saveNextQuizItems(selectedStudent.id, newItems);

    setCustomizeDialogOpen(false);
    setCustomizingContainer(null);
    setContainerQuizzes([]);
    setExcludedQuizIds([]);
  };

  const handleValidateAndAssign = async () => {
    if (!selectedStudent) return;

    try {
      // Get next session number
      const existingSessions = await quizSessionService.getByStudent(selectedStudent.id);
      const sessionNumber = existingSessions.length + 1;

      // Create the quiz session
      const session = await quizSessionService.create({
        student: selectedStudent.id,
        name: `Session ${sessionNumber}`,
      });

      let assignedCount = 0;
      const assignedQuizIds = new Set<number>(); // Track already assigned quizzes to avoid duplicates

      // Process each selected item and assign to session
      for (const item of selectedQuizItems) {
        if (item.type === 'quiz') {
          // Create assignment for individual quiz if not already assigned
          if (!assignedQuizIds.has(item.id)) {
            await quizAssignmentService.create({
              student: selectedStudent.id,
              quiz: item.id,
              session: session.id,
              status: 'pending',
            });
            assignedQuizIds.add(item.id);
            assignedCount++;
          }
        } else if (item.type === 'container') {
          // Create assignments for all quizzes in container (except excluded ones)
          const quizzesToAssign = (item.allQuizzes || []).filter(
            quiz => !item.excludedQuizIds?.includes(quiz.id)
          );

          for (const quiz of quizzesToAssign) {
            // Only create assignment if not already assigned
            if (!assignedQuizIds.has(quiz.id)) {
              await quizAssignmentService.create({
                student: selectedStudent.id,
                quiz: quiz.id,
                session: session.id,
                status: 'pending',
              });
              assignedQuizIds.add(quiz.id);
              assignedCount++;
            }
          }
        }
      }

      // Clear the configuration
      setSelectedQuizItems([]);
      await nextQuizItemService.clearStudent(selectedStudent.id);

      // Refresh quiz sessions list
      await loadQuizSessions(selectedStudent.id);

      // Close configuration interface
      setIsConfiguringNextQuiz(false);

      toast.success(`Session créée avec ${assignedCount} quiz`);
    } catch (error) {
      console.error('Error assigning quizzes:', error);
      toast.error('Erreur lors de l\'assignation des quiz');
    }
  };

  const handleQuizDragEnd = async (result: DropResult) => {
    if (!result.destination) return;
    if (!selectedStudent) return;

    const { destination, draggableId } = result;

    // Only handle drops to the selected items area
    if (destination.droppableId !== 'selected-quiz-items') return;

    // Extract type and id from draggableId (format: "browse-quiz-123", "browse-container-456", or "browse-session-789")
    const [, itemType, itemIdStr] = draggableId.split('-');
    const itemId = parseInt(itemIdStr);

    if (itemType === 'quiz') {
      // Find the quiz in browseQuizzes
      const quiz = browseQuizzes.find(q => q.id === itemId);
      if (quiz) {
        handleAddQuizItem(quiz);
      }
    } else if (itemType === 'container') {
      // Find the container in browseContainers
      const container = browseContainers.find(c => c.id === itemId);
      if (container) {
        handleAddContainerItem(container);
      }
    } else if (itemType === 'session') {
      // Handle adding all quizzes from a session, preserving container structure
      try {
        // Get all assignments for this session (regardless of student)
        const sessionAssignments = await quizAssignmentService.getAll({ session: itemId });

        // Fetch all quiz details
        const quizzes: Quiz[] = [];
        for (const assignment of sessionAssignments) {
          const quiz = await quizService.getQuiz(assignment.quiz);
          quizzes.push(quiz);
        }

        // Group quizzes by container
        const quizzesByContainer = new Map<number | null, Quiz[]>();
        for (const quiz of quizzes) {
          const containerId = quiz.container || null;
          if (!quizzesByContainer.has(containerId)) {
            quizzesByContainer.set(containerId, []);
          }
          quizzesByContainer.get(containerId)!.push(quiz);
        }

        const newItems = [...selectedQuizItems];
        let addedCount = 0;

        // Process each container group
        for (const [containerId, containerQuizzes] of Array.from(quizzesByContainer.entries())) {
          if (containerId === null) {
            // Add standalone quizzes (no container)
            for (const quiz of containerQuizzes) {
              if (!newItems.some(item => item.type === 'quiz' && item.id === quiz.id)) {
                newItems.push({
                  type: 'quiz',
                  id: quiz.id,
                  data: quiz,
                });
                addedCount++;
              }
            }
          } else {
            // Add as container if not already added
            if (!newItems.some(item => item.type === 'container' && item.id === containerId)) {
              // Fetch the container object
              const container = await quizContainerService.getContainer(containerId);

              // Fetch all quizzes in the container
              const containerContents = await quizContainerService.getContainerContents(containerId);
              const allContainerQuizzes = containerContents.quizzes || [];

              // Determine which quizzes from the container are NOT in the session (these are excluded)
              const sessionQuizIds = new Set(containerQuizzes.map(q => q.id));
              const excludedIds = allContainerQuizzes
                .filter(q => !sessionQuizIds.has(q.id))
                .map(q => q.id);

              newItems.push({
                type: 'container',
                id: containerId,
                data: container,
                excludedQuizIds: excludedIds,
                allQuizzes: allContainerQuizzes,
              });
              addedCount += containerQuizzes.length;
            }
          }
        }

        if (addedCount > 0) {
          setSelectedQuizItems(newItems);
          saveNextQuizItems(selectedStudent.id, newItems);
          toast.success(`${addedCount} question(s) ajoutée(s) depuis la session`);
        } else {
          toast.info('Toutes les questions de cette session sont déjà ajoutées');
        }
      } catch (error) {
        console.error('Error adding session quizzes:', error);
        toast.error('Erreur lors de l\'ajout des questions de la session');
      }
    }
  };

  const getClassName = (classId: number) => {
    const classObj = classes.find(c => c.id === classId);
    return classObj ? classObj.name : '';
  };

  const weekDays = [
    'Lundi',
    'Mardi',
    'Mercredi',
    'Jeudi',
    'Vendredi',
    'Samedi',
    'Dimanche',
    'Non défini'
  ];

  const getStudentsByDay = (day: string) => {
    const searchLower = studentSearch.toLowerCase().trim();
    const baseStudents = searchLower
      ? students.filter(s => {
          const name = `${s.first_name || ''} ${s.last_name || ''}`.toLowerCase();
          const phone = (s.phone_number || '').toLowerCase();
          return name.includes(searchLower) || phone.includes(searchLower);
        })
      : students;

    let filtered;
    if (day === 'Non défini') {
      filtered = baseStudents.filter(s => {
        const studentSlots = allTimeSlots.filter(slot => slot.student === s.id);
        return studentSlots.length === 0;
      });
    } else {
      filtered = baseStudents.filter(s => {
        const studentSlots = allTimeSlots.filter(slot => slot.student === s.id && slot.day === day);
        return studentSlots.length > 0;
      });
    }
    return filtered.sort((a, b) => a.order - b.order);
  };

  const handleDragEnd = async (result: DropResult) => {
    const { source, destination, draggableId } = result;

    if (!destination) return;

    const sourceDay = source.droppableId;
    const destDay = destination.droppableId;
    // Extract student ID from draggableId format: "studentId-day"
    const studentId = parseInt(draggableId.split('-')[0]);

    // Only allow reordering within the same day
    if (sourceDay !== destDay) {
      toast.info('Pour changer le jour d\'un élève, modifiez ses créneaux horaires');
      return;
    }

    // Get students for the day
    const sourceStudents = getStudentsByDay(sourceDay);

    // Find the dragged student
    const draggedStudent = students.find(s => s.id === studentId);
    if (!draggedStudent) return;

    // Reorder students
    const reordered = Array.from(sourceStudents);
    const [removed] = reordered.splice(source.index, 1);
    reordered.splice(destination.index, 0, removed);

    // Update orders for all students in this day
    try {
      for (let i = 0; i < reordered.length; i++) {
        await studentService.update(reordered[i].id, { order: i });
      }
      toast.success('Ordre mis à jour');
      loadStudents();
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const formatTime = (time: string) => {
    // Remove seconds from time string (HH:MM:SS -> HH:MM)
    return time.substring(0, 5);
  };

  const formatPrice = (amount: number) => {
    // Format number with space as thousands separator
    return Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  const renderStudentCard = (student: any, index: number, day: string) => {
    // Get time slots for this student on this day
    const studentDaySlots = allTimeSlots.filter(
      slot => slot.student === student.id && slot.day === day
    );

    // Create unique draggableId by combining student id and day
    const draggableId = `${student.id}-${day}`;

    return (
      <Draggable key={draggableId} draggableId={draggableId} index={index}>
        {(provided, snapshot) => (
          <Card
            ref={provided.innerRef}
            {...provided.draggableProps}
            sx={{
              width: 'calc(33.33% - 6px)',
              minWidth: 120,
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden',
              backgroundColor: student.color ? `#${student.color}` : (selectedStudent?.id === student.id ? 'action.selected' : snapshot.isDragging ? 'action.hover' : 'inherit'),
              border: selectedStudent?.id === student.id ? '2px solid' : pushStudentIds.has(student.id) ? '3px solid' : 'none',
              borderColor: selectedStudent?.id === student.id ? 'primary.main' : pushStudentIds.has(student.id) ? 'success.main' : undefined,
            }}
            onClick={() => handleSelectStudent(student)}
          >
            {student.profile_picture ? (
              <Box sx={{ width: '100%', paddingTop: '100%', position: 'relative', overflow: 'hidden' }}>
                <Box component="img" src={student.profile_picture} sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
              </Box>
            ) : (
              <Box sx={{ width: '100%', paddingTop: '70%', position: 'relative', bgcolor: 'action.disabledBackground', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PersonIcon sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: 40, color: 'grey.400' }} />
              </Box>
            )}
            <Box {...provided.dragHandleProps} sx={{ position: 'absolute', top: 4, left: 4, zIndex: 1 }}>
              <DragIndicatorIcon sx={{ fontSize: 16, cursor: 'grab', opacity: 0.5, color: student.profile_picture ? '#fff' : undefined, filter: student.profile_picture ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' : undefined }} />
            </Box>
            <CardContent sx={{ py: 0.5, px: 1, '&:last-child': { pb: 0.5 } }}>
              <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
                <Typography variant="body2" fontWeight="bold" noWrap sx={{ maxWidth: '100%' }}>
                  {student.first_name}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: '100%' }}>
                  {student.last_name || ''}
                </Typography>
                {studentDaySlots.length > 0 && (
                  <Typography variant="caption" color="primary.main" noWrap sx={{ maxWidth: '100%' }}>
                    {formatTime(studentDaySlots[0].start_time)}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        )}
      </Draggable>
    );
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box>
        <Grid container spacing={3}>
          {/* Left Panel - List */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: 'calc(100vh - 250px)', overflow: 'auto' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Élèves</Typography>
                <Box display="flex" gap={1}>
                  <Button
                    variant={showAllPayments ? "contained" : "outlined"}
                    onClick={() => {
                      setShowAllPayments(!showAllPayments);
                      setShowFamilyView(false);
                      setSelectedStudent(null);
                      if (!showAllPayments) {
                        loadAllInvoices();
                      }
                    }}
                    size="small"
                    color="primary"
                  >
                    Paiements
                  </Button>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleNew}
                    size="small"
                  >
                    Nouveau
                  </Button>
                  <Tooltip title="Calendrier">
                    <IconButton onClick={() => setCalendarDialogOpen(true)} size="small">
                      <CalendarMonthIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Quizz">
                    <IconButton onClick={() => setQuizTimelineOpen(true)} size="small">
                      <QuizIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Famille">
                    <IconButton
                      onClick={() => {
                        setShowFamilyView(!showFamilyView);
                        setSelectedStudent(null);
                        setShowAllPayments(false);
                        loadFamilyMembers();
                      }}
                      size="small"
                      color={showFamilyView ? 'primary' : 'default'}
                    >
                      <FamilyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <TextField
                size="small"
                placeholder="Rechercher (nom, prénom, tél.)"
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
              />

              {loading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : students.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center">
                  Aucun élève trouvé
                </Typography>
              ) : (
                weekDays.map((day) => {
                  const dayStudents = getStudentsByDay(day);
                  if (dayStudents.length === 0) return null;

                  return (
                    <Accordion key={day} defaultExpanded={true}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {day} ({dayStudents.length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0, pt: 1 }}>
                        <Droppable droppableId={day} type="STUDENT">
                          {(provided, snapshot) => (
                            <Box
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              sx={{
                                minHeight: 50,
                                backgroundColor: snapshot.isDraggingOver ? 'action.hover' : 'inherit',
                                transition: 'background-color 0.2s',
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1,
                              }}
                            >
                              {dayStudents.map((student, index) => renderStudentCard(student, index, day))}
                              {provided.placeholder}
                            </Box>
                          )}
                        </Droppable>
                      </AccordionDetails>
                    </Accordion>
                  );
                })
              )}
            </Paper>
          </Grid>

        {/* Right Panel - Details */}
        <Grid item xs={12} md={8}>
          {showFamilyView && !selectedStudent ? (
            /* Vue Famille */
            <Paper sx={{ p: 2, height: 'calc(100vh - 250px)', overflow: 'auto' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Vue Famille</Typography>
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => openFamilyDialog()}
                  >
                    Nouveau parent
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowFamilyView(false)}
                  >
                    Fermer
                  </Button>
                </Box>
              </Box>
              <Grid container spacing={2}>
                {/* Colonne Parents */}
                <Grid item xs={12} md={5}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Rechercher un parent..."
                    value={familyParentFilter}
                    onChange={(e) => setFamilyParentFilter(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  {loadingFamily ? (
                    <Box display="flex" justifyContent="center" p={3}><CircularProgress size={24} /></Box>
                  ) : (() => {
                    const pFilter = familyParentFilter.toLowerCase();
                    const filteredParents = familyMembers.filter(p =>
                      !pFilter || `${p.first_name} ${p.last_name} ${p.phone_number}`.toLowerCase().includes(pFilter)
                    );
                    // IDs of children linked to highlighted parent
                    const highlightedChildIds = familyHighlightParentId
                      ? (familyMembers.find(p => p.id === familyHighlightParentId)?.children || [])
                      : [];
                    return filteredParents.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                        Aucun parent
                      </Typography>
                    ) : (
                      filteredParents.map((parent) => {
                        // Highlight if a student is selected and this parent is linked to them
                        const isHighlighted = familyHighlightStudentId
                          ? parent.children?.includes(familyHighlightStudentId)
                          : false;
                        return (
                        <Card
                          key={parent.id}
                          sx={{
                            mb: 1, cursor: 'pointer', '&:hover': { boxShadow: 3 },
                            bgcolor: isHighlighted ? 'rgba(25,118,210,0.12)' : undefined,
                            border: isHighlighted ? '1px solid #1976d2' : undefined,
                          }}
                          onClick={() => {
                            setFamilyHighlightParentId(familyHighlightParentId === parent.id ? null : parent.id);
                            setFamilyHighlightStudentId(null);
                          }}
                        >
                          <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Avatar sx={{ width: 36, height: 36, bgcolor: parent.gender === 'F' ? '#e685b5' : '#6ea8fe' }}>
                                <PersonIcon sx={{ fontSize: 20 }} />
                              </Avatar>
                              <Box flex={1} minWidth={0}>
                                <Typography variant="body2" fontWeight="bold" noWrap>
                                  {parent.gender === 'F' ? 'Mme' : parent.gender === 'M' ? 'M.' : ''} {parent.first_name} {parent.last_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">{parent.phone_number}</Typography>
                              </Box>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); openFamilyDialog(parent); }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteFamilyMember(parent.id); }}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Box>
                            {parent.children_details && parent.children_details.length > 0 && (
                              <Box mt={0.5} display="flex" flexWrap="wrap" gap={0.5}>
                                {parent.children_details.map((child) => {
                                  const studentObj = students.find(s => s.id === child.id);
                                  return (
                                    <Chip
                                      key={child.id}
                                      label={`${child.first_name} ${child.last_name || ''}`}
                                      size="small"
                                      sx={{
                                        backgroundColor: studentObj?.color ? `#${studentObj.color}` : undefined,
                                        fontWeight: 500,
                                        fontSize: '0.7rem',
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                        );
                      })
                    );
                  })()}
                </Grid>
                {/* Zone centrale LIENS */}
                <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ writingMode: 'vertical-rl', textOrientation: 'mixed', letterSpacing: 4, fontWeight: 'bold', opacity: 0.3, fontSize: '1rem' }}>
                    LIENS
                  </Typography>
                </Grid>
                {/* Colonne Élèves */}
                <Grid item xs={12} md={5}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Rechercher un élève..."
                    value={familyStudentFilter}
                    onChange={(e) => setFamilyStudentFilter(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  {(() => {
                    const sFilter = familyStudentFilter.toLowerCase();
                    const filteredStudents = students.filter(s =>
                      !sFilter || `${s.first_name} ${s.last_name || ''}`.toLowerCase().includes(sFilter)
                    );
                    // IDs of parents linked to highlighted student
                    const highlightedParentIds = familyHighlightStudentId
                      ? familyMembers.filter(p => p.children?.includes(familyHighlightStudentId)).map(p => p.id)
                      : [];
                    // IDs of children linked to highlighted parent
                    const highlightedChildIds = familyHighlightParentId
                      ? (familyMembers.find(p => p.id === familyHighlightParentId)?.children || [])
                      : [];
                    return filteredStudents.map((student) => {
                      const studentParents = familyMembers.filter(p => p.children?.includes(student.id));
                      const isHighlighted = familyHighlightParentId
                        ? highlightedChildIds.includes(student.id)
                        : false;
                      return (
                        <Card
                          key={student.id}
                          sx={{
                            mb: 1, cursor: 'pointer', '&:hover': { boxShadow: 3 },
                            bgcolor: isHighlighted ? 'rgba(25,118,210,0.12)' : undefined,
                            border: isHighlighted ? '2px solid #1976d2' : pushStudentIds.has(student.id) ? '3px solid #4caf50' : '1px solid #e0e0e0',
                          }}
                          onClick={() => {
                            setFamilyHighlightStudentId(familyHighlightStudentId === student.id ? null : student.id);
                            setFamilyHighlightParentId(null);
                          }}
                        >
                          <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Avatar
                                src={student.profile_picture}
                                sx={{
                                  width: 36,
                                  height: 36,
                                  bgcolor: student.color ? `#${student.color}` : undefined,
                                }}
                              >
                                {!student.profile_picture && <PersonIcon sx={{ fontSize: 20 }} />}
                              </Avatar>
                              <Box flex={1} minWidth={0}>
                                <Typography variant="body2" fontWeight="bold" noWrap>
                                  {student.first_name} {student.last_name || ''}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {classes.find(c => c.id === student.class_group)?.name || ''}
                                </Typography>
                              </Box>
                              <Tooltip title="Voir la fiche élève">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleSelectStudent(student); }}>
                                  <PersonIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            {studentParents.length > 0 && (
                              <Box mt={0.5} display="flex" flexWrap="wrap" gap={0.5}>
                                {studentParents.map((p) => (
                                  <Chip
                                    key={p.id}
                                    label={`${p.gender === 'F' ? 'Mme' : p.gender === 'M' ? 'M.' : ''} ${p.first_name} ${p.last_name}`}
                                    size="small"
                                    variant="outlined"
                                    color={pushParentIds.has(p.id) ? 'success' : 'default'}
                                    sx={{ fontWeight: 500, fontSize: '0.7rem' }}
                                  />
                                ))}
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      );
                    });
                  })()}
                </Grid>
              </Grid>
            </Paper>
          ) : showAllPayments && !selectedStudent ? (
            /* Vue tous les paiements */
            <Paper sx={{ p: 2, height: 'calc(100vh - 250px)', overflow: 'auto' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Tous les paiements</Typography>
                <Box display="flex" gap={1} alignItems="center">
                  <FormControlLabel
                    control={<Switch size="small" checked={showOrgInvoices}
                      onChange={(e) => {
                        setShowOrgInvoices(e.target.checked);
                        loadAllInvoices(e.target.checked);
                      }} />}
                    label={<Typography variant="caption">Organisation</Typography>}
                  />
                  <Button variant="outlined" size="small" onClick={() => setShowAllPayments(false)}>
                    Fermer
                  </Button>
                </Box>
              </Box>

              {/* Résumé des totaux */}
              <Box display="flex" gap={2} mb={3}>
                <Paper sx={{ p: 2, flex: 1, bgcolor: 'action.hover' }}>
                  <Typography variant="caption" color="text.secondary">Avant émission</Typography>
                  <Typography variant="h6" color="text.primary">
                    {formatPrice(allInvoices.filter(i => i.status === 'draft').reduce((sum, i) => sum + (Number(i.price) || 0), 0))} frs
                  </Typography>
                  <Typography variant="caption">{allInvoices.filter(i => i.status === 'draft').length} factures</Typography>
                </Paper>
                <Paper sx={{ p: 2, flex: 1, bgcolor: 'warning.light' }}>
                  <Typography variant="caption" color="text.secondary">Émises (à encaisser)</Typography>
                  <Typography variant="h6" color="warning.dark">
                    {formatPrice(allInvoices.filter(i => i.status === 'issued').reduce((sum, i) => sum + (Number(i.price) || 0), 0))} frs
                  </Typography>
                  <Typography variant="caption">{allInvoices.filter(i => i.status === 'issued').length} factures</Typography>
                </Paper>
                <Paper sx={{ p: 2, flex: 1, bgcolor: 'success.light' }}>
                  <Typography variant="caption" color="text.secondary">Réglées</Typography>
                  <Typography variant="h6" color="success.dark">
                    {formatPrice(allInvoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (Number(i.price) || 0), 0))} frs
                  </Typography>
                  <Typography variant="caption">{allInvoices.filter(i => i.status === 'paid').length} factures</Typography>
                </Paper>
              </Box>

              {/* Organisation summary blocks (own + colleagues = total org) */}
              {showOrgInvoices && (() => {
                const orgDraft = allInvoices.filter(i => i.status === 'draft');
                const orgIssued = allInvoices.filter(i => i.status === 'issued');
                const orgPaid = allInvoices.filter(i => i.status === 'paid');
                const orgDraftTotal = orgDraft.reduce((sum, i) => sum + (Number(i.price) || 0), 0);
                const orgIssuedTotal = orgIssued.reduce((sum, i) => sum + (Number(i.price) || 0), 0);
                const orgPaidTotal = orgPaid.reduce((sum, i) => sum + (Number(i.price) || 0), 0);
                if (allInvoices.filter(i => i.is_foreign).length === 0) return null;
                return (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Total organisation</Typography>
                    <Box display="flex" gap={2}>
                      <Paper sx={{ p: 2, flex: 1, bgcolor: 'rgba(156, 39, 176, 0.06)', border: '1px solid', borderColor: 'secondary.light' }}>
                        <Typography variant="caption" color="secondary.main">Avant émission</Typography>
                        <Typography variant="h6" color="secondary.main">
                          {formatPrice(orgDraftTotal)} frs
                        </Typography>
                        <Typography variant="caption">{orgDraft.length} factures</Typography>
                      </Paper>
                      <Paper sx={{ p: 2, flex: 1, bgcolor: 'rgba(156, 39, 176, 0.06)', border: '1px solid', borderColor: 'secondary.light' }}>
                        <Typography variant="caption" color="secondary.main">Émises (à encaisser)</Typography>
                        <Typography variant="h6" color="secondary.main">
                          {formatPrice(orgIssuedTotal)} frs
                        </Typography>
                        <Typography variant="caption">{orgIssued.length} factures</Typography>
                      </Paper>
                      <Paper sx={{ p: 2, flex: 1, bgcolor: 'rgba(156, 39, 176, 0.06)', border: '1px solid', borderColor: 'secondary.light' }}>
                        <Typography variant="caption" color="secondary.main">Réglées</Typography>
                        <Typography variant="h6" color="secondary.main">
                          {formatPrice(orgPaidTotal)} frs
                        </Typography>
                        <Typography variant="caption">{orgPaid.length} factures</Typography>
                      </Paper>
                    </Box>
                  </Box>
                );
              })()}

              {/* Liste des factures par élève */}
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>Factures émises par élève</Typography>
              {students.map((student) => {
                const ownInvoices = allInvoices.filter(i => i.student === student.id && i.status === 'issued' && !i.is_foreign);
                const foreignInvoices = allInvoices.filter(i => i.student === student.id && i.status === 'issued' && i.is_foreign);
                const ownTotal = ownInvoices.reduce((sum, i) => sum + (Number(i.price) || 0), 0);
                const foreignTotal = foreignInvoices.reduce((sum, i) => sum + (Number(i.price) || 0), 0);
                return { student, ownInvoices, foreignInvoices, ownTotal, foreignTotal };
              })
              .filter(r => r.ownInvoices.length > 0 || r.foreignInvoices.length > 0)
              // montants décroissants (total organisation si affiché, sinon mes factures)
              .sort((a, b) => (b.ownTotal + (showOrgInvoices ? b.foreignTotal : 0)) - (a.ownTotal + (showOrgInvoices ? a.foreignTotal : 0)))
              .map(({ student, ownInvoices, foreignInvoices, ownTotal, foreignTotal }) => {
                return (
                  <Card
                    key={student.id}
                    sx={{
                      mb: 1,
                      cursor: 'pointer',
                      backgroundColor: student.color ? `#${student.color}` : 'inherit',
                      '&:hover': { opacity: 0.8 },
                    }}
                    onClick={() => {
                      setSelectedStudent(student);
                      setShowAllPayments(false);
                      setActiveTab(2);
                      loadInvoices(student.id);
                      loadTimeSlots(student.id);
                    }}
                  >
                    <CardContent sx={{ py: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar src={student.profile_picture} sx={{ width: 32, height: 32 }}>
                            {!student.profile_picture && <PersonIcon sx={{ fontSize: 18 }} />}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {student.first_name} {student.last_name || ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {ownInvoices.length} facture{ownInvoices.length > 1 ? 's' : ''} en attente
                            </Typography>
                          </Box>
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="body1" fontWeight="bold" color="warning.dark">
                            {formatPrice(ownTotal)} frs
                          </Typography>
                          {showOrgInvoices && foreignTotal > 0 && (
                            <Typography variant="caption" color="secondary.main" fontWeight="bold">
                              +{formatPrice(foreignTotal)} frs ({foreignInvoices.map(i => i.teacher_name).filter((v, i, a) => a.indexOf(v) === i).join(', ')})
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}

              {allInvoices.filter(i => i.status === 'issued').length === 0 && (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                  Aucune facture émise en attente
                </Typography>
              )}
            </Paper>
          ) : selectedStudent ? (
            <Box>
              <Paper sx={{ p: 2, mb: 2, backgroundColor: selectedStudent.color ? `#${selectedStudent.color}` : 'inherit' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar
                      src={selectedStudent.profile_picture}
                      sx={{ width: 56, height: 56 }}
                    >
                      {!selectedStudent.profile_picture && <PersonIcon sx={{ fontSize: 32 }} />}
                    </Avatar>
                    <Typography variant="h6">Détails de l'élève</Typography>
                  </Box>
                  <Box display="flex" gap={1}>
                    <Button
                      startIcon={<EditIcon />}
                      onClick={() => handleEdit(selectedStudent)}
                      variant="outlined"
                      size="small"
                    >
                      Modifier
                    </Button>
                    <Button
                      onClick={handleOpenPasswordDialog}
                      variant="outlined"
                      size="small"
                      color="secondary"
                    >
                      Modifier mot de passe
                    </Button>
                    <Button
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDelete(selectedStudent.id)}
                      variant="outlined"
                      size="small"
                      color="error"
                    >
                      Supprimer
                    </Button>
                  </Box>
                  {isErwan && plaintextPasswords[`student:${selectedStudent.id}`] && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Mot de passe</Typography>
                      <Typography variant="body2" fontFamily="monospace">
                        {plaintextPasswords[`student:${selectedStudent.id}`]}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box display="flex" flexWrap="wrap" gap={3}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Prénom</Typography>
                    <Typography variant="body2">{selectedStudent.first_name}</Typography>
                  </Box>

                  {selectedStudent.last_name && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Nom</Typography>
                      <Typography variant="body2">{selectedStudent.last_name}</Typography>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="caption" color="text.secondary">Classe</Typography>
                    <Typography variant="body2">{getClassName(selectedStudent.class_group)}</Typography>
                  </Box>

                  {selectedStudent.phone_number && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Téléphone</Typography>
                      <Typography variant="body2">{selectedStudent.phone_number}</Typography>
                    </Box>
                  )}

                  {selectedStudent.parent_phone && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Téléphone parent{selectedStudent.parent_gender ? ` (${selectedStudent.parent_gender === 'M' ? 'Homme' : 'Femme'})` : ''}
                      </Typography>
                      <Typography variant="body2">{selectedStudent.parent_phone}</Typography>
                    </Box>
                  )}

                  {(selectedStudent.pronote_username || selectedStudent.pronote_password) && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Pronote</Typography>
                      <Typography variant="body2">
                        {selectedStudent.pronote_username && <>ID : {selectedStudent.pronote_username}</>}
                        {selectedStudent.pronote_username && selectedStudent.pronote_password && ' — '}
                        {selectedStudent.pronote_password && <>MDP : {selectedStudent.pronote_password}</>}
                      </Typography>
                    </Box>
                  )}

                  {timeSlots.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Créneaux horaires</Typography>
                      <Typography variant="body2">
                        {timeSlots.map((slot, index) => (
                          <span key={slot.id}>
                            {slot.day} {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                            {index < timeSlots.length - 1 && ', '}
                          </span>
                        ))}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>

              {/* Tabs Navigation */}
              <Paper sx={{ mt: 2 }}>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} variant="scrollable" scrollButtons="auto">
                  <Tab label="Historique" />
                  <Tab label="Notifications" />
                  <Tab label="Paiements" />
                  <Tab label="Objectifs" />
                  <Tab label="Quizz" />
                  <Tab label="Famille" />
                  <Tab label={/^3/.test(getClassName(selectedStudent.class_group)) ? 'Brevet' : 'Bac'} />
                  <Tab label="Sat" />
                </Tabs>
              </Paper>

              {/* Tab Content */}
              <Paper sx={{ p: 2, mt: 2, minHeight: 300 }}>
                {activeTab === 0 && selectedStudent && (
                  <Box>
                    <Typography variant="h6" gutterBottom>Historique</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Contenu à venir...
                    </Typography>
                  </Box>
                )}
                {activeTab === 1 && selectedStudent && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Notifications pour {selectedStudent.first_name} {selectedStudent.last_name}
                    </Typography>

                    {/* Formulaire d'envoi */}
                    <Card sx={{ mb: 3, p: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Envoyer une notification</Typography>
                      <ToggleButtonGroup
                        value={notificationRecipient}
                        exclusive
                        onChange={(_e, val) => val && setNotificationRecipient(val)}
                        size="small"
                        sx={{ mb: 2 }}
                      >
                        <ToggleButton value="student">Élève</ToggleButton>
                        {familyMembers.find(p => p.children?.includes(selectedStudent.id)) && (
                          <ToggleButton value="parent">Parent</ToggleButton>
                        )}
                      </ToggleButtonGroup>
                      <TextField
                        fullWidth
                        size="small"
                        label="Titre"
                        sx={{ mb: 1 }}
                        value={notificationTitle}
                        onChange={(e) => setNotificationTitle(e.target.value)}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Message"
                        multiline
                        rows={2}
                        sx={{ mb: 1 }}
                        value={notificationBody}
                        onChange={(e) => setNotificationBody(e.target.value)}
                      />
                      <Button
                        variant="contained"
                        startIcon={<SendIcon />}
                        onClick={handleSendNotification}
                        disabled={!notificationTitle.trim() || !notificationBody.trim() || sendingNotification}
                      >
                        Envoyer
                      </Button>
                    </Card>

                    {/* Historique des notifications */}
                    {loadingNotifications ? (
                      <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                      </Box>
                    ) : teacherNotifications.length === 0 ? (
                      <Box textAlign="center" py={4}>
                        <Typography variant="body2" color="text.secondary">
                          Aucune notification envoyée
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              {['Date', 'Destinataire', 'Contenu', 'Statut', 'Ouverture app'].map(h => (
                                <th key={h} style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid #e0e0e0', fontSize: '0.8rem', color: '#666' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {teacherNotifications.map(notif => {
                              const statusConfig: Record<string, { label: string; color: 'default' | 'info' | 'success' | 'warning' | 'error' }> = {
                                pending: { label: 'En attente', color: 'default' },
                                delivered: { label: 'Délivré', color: 'info' },
                                clicked: { label: 'Lu', color: 'success' },
                                ignored: { label: 'Ignoré', color: 'warning' },
                                failed: { label: 'Échoué', color: 'error' },
                              };
                              const sc = statusConfig[notif.status] || statusConfig.pending;
                              const formatDt = (dt: string | null) => dt
                                ? new Date(dt).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                                : '-';
                              return (
                                <tr key={notif.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                  <td style={{ padding: '8px 12px', fontSize: '0.85rem' }}>
                                    {formatDt(notif.created_at)}
                                  </td>
                                  <td style={{ padding: '8px 12px' }}>
                                    <Chip
                                      label={notif.recipient_name || '-'}
                                      size="small"
                                      color={notif.recipient_type === 'student' ? 'primary' : 'secondary'}
                                      variant="outlined"
                                    />
                                  </td>
                                  <td style={{ padding: '8px 12px' }}>
                                    <Typography variant="body2" fontWeight="bold">{notif.title}</Typography>
                                    <Typography variant="caption" color="text.secondary">{notif.body}</Typography>
                                  </td>
                                  <td style={{ padding: '8px 12px' }}>
                                    <Chip label={sc.label} size="small" color={sc.color} />
                                  </td>
                                  <td style={{ padding: '8px 12px', fontSize: '0.85rem' }}>
                                    {formatDt(notif.first_app_open_at)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </Box>
                    )}
                  </Box>
                )}
                {activeTab === 2 && selectedStudent && (
                  <DragDropContext
                    onDragStart={(start) => setDraggingInvoiceId(parseInt(start.draggableId))}
                    onDragEnd={(result) => {
                      setDraggingInvoiceId(null);
                      handleInvoiceDragEnd(result);
                    }}
                  >
                    <Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">Paiements</Typography>
                        <Button
                          startIcon={<AddIcon />}
                          variant="contained"
                          size="small"
                          onClick={() => setInvoiceDialogOpen(true)}
                        >
                          Nouvelle facture
                        </Button>
                      </Box>

                      {/* Kanban Board */}
                      <Grid container spacing={2}>
                        {/* Draft Column */}
                        <Grid item xs={4}>
                          <Paper sx={{ p: 1, bgcolor: 'action.hover', minHeight: 400 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                              <Typography variant="subtitle2" fontWeight="bold">
                                Factures avant émission
                              </Typography>
                              <Typography variant="caption" color="primary.main" fontWeight="bold">
                                {getTotalByStatus('draft')} frs
                              </Typography>
                            </Box>
                            <Droppable droppableId="draft" type="INVOICE">
                              {(provided, snapshot) => (
                                <Box
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                  sx={{
                                    minHeight: 350,
                                    backgroundColor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                                  }}
                                >
                                  {getInvoicesByStatus('draft').map((invoice, index) => (
                                    <Draggable key={invoice.id} draggableId={String(invoice.id)} index={index}>
                                      {(provided, snapshot) => (
                                        <Card
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          {...provided.dragHandleProps}
                                          onClick={() => handleOpenEditInvoice(invoice)}
                                          sx={{
                                            mb: 1,
                                            cursor: 'pointer',
                                            backgroundColor: snapshot.isDragging ? 'action.hover' : 'background.paper',
                                            '&:hover': {
                                              backgroundColor: 'action.hover',
                                            },
                                          }}
                                        >
                                          <CardContent sx={{
                                            py: 1, px: 1.5, '&:last-child': { pb: 1 },
                                            ...(invoice.is_foreign ? { bgcolor: 'rgba(156, 39, 176, 0.06)', borderLeft: '3px solid', borderColor: 'secondary.main' } : {}),
                                          }}>
                                            {invoice.is_foreign && invoice.teacher_name && (
                                              <Typography variant="caption" color="secondary.main" fontWeight="bold" display="block" sx={{ mb: 0.25 }}>
                                                {invoice.teacher_name}
                                              </Typography>
                                            )}
                                            <Typography variant="body2" fontWeight="bold">
                                              {formatInvoiceDate(invoice.date)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              de {formatTime(invoice.start_time)} à {formatTime(invoice.end_time)}
                                            </Typography>
                                            <Typography variant="body2" color="primary.main" sx={{ mt: 0.5 }}>
                                              {formatPrice(Number(invoice.price) || 0)} frs
                                            </Typography>
                                            {invoice.discount && (
                                              <Typography variant="caption" color="error.main" display="block">
                                                -{formatPrice(Number(invoice.discount) || 0)} frs {invoice.discount_label}
                                              </Typography>
                                            )}
                                          </CardContent>
                                        </Card>
                                      )}
                                    </Draggable>
                                  ))}
                                  {provided.placeholder}
                                </Box>
                              )}
                            </Droppable>
                          </Paper>
                        </Grid>

                        {/* Issued Column */}
                        <Grid item xs={4}>
                          <Paper sx={{ p: 1, bgcolor: 'action.hover', minHeight: 400 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                              <Typography variant="subtitle2" fontWeight="bold">
                                Factures émises
                              </Typography>
                              <Typography variant="caption" color="primary.main" fontWeight="bold">
                                {getTotalByStatus('issued')} frs
                              </Typography>
                            </Box>
                            <Droppable droppableId="issued" type="INVOICE">
                              {(provided, snapshot) => (
                                <Box
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                  sx={{
                                    minHeight: 350,
                                    backgroundColor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                                  }}
                                >
                                  {getInvoicesByStatus('issued').map((invoice, index) => {
                                    const isSelected = selectedInvoices.includes(invoice.id);
                                    const isDraggedItem = draggingInvoiceId === invoice.id;
                                    const isGhosted = draggingInvoiceId !== null
                                      && selectedInvoices.includes(draggingInvoiceId)
                                      && isSelected
                                      && !isDraggedItem;
                                    const dragCount = isDraggedItem && isSelected && selectedInvoices.length > 1
                                      ? selectedInvoices.length
                                      : 0;

                                    return (
                                      <Draggable key={invoice.id} draggableId={String(invoice.id)} index={index}>
                                        {(provided, snapshot) => (
                                          <Card
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            onClick={() => handleOpenEditInvoice(invoice)}
                                            sx={{
                                              mb: 1,
                                              cursor: 'pointer',
                                              position: 'relative',
                                              backgroundColor: isSelected
                                                ? 'primary.light'
                                                : snapshot.isDragging
                                                  ? 'action.hover'
                                                  : 'background.paper',
                                              border: isSelected ? '2px solid' : 'none',
                                              borderColor: 'primary.main',
                                              opacity: isGhosted ? 0.4 : 1,
                                              transform: isGhosted ? 'scale(0.95)' : 'none',
                                              transition: 'opacity 0.2s, transform 0.2s',
                                              '&:hover': {
                                                backgroundColor: isSelected
                                                  ? 'primary.light'
                                                  : 'action.hover',
                                              },
                                            }}
                                          >
                                            {/* Badge indiquant le nombre de factures sélectionnées */}
                                            {snapshot.isDragging && dragCount > 1 && (
                                              <Box
                                                sx={{
                                                  position: 'absolute',
                                                  top: -8,
                                                  right: -8,
                                                  width: 24,
                                                  height: 24,
                                                  borderRadius: '50%',
                                                  backgroundColor: 'primary.main',
                                                  color: 'white',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  fontSize: '0.75rem',
                                                  fontWeight: 'bold',
                                                  boxShadow: 2,
                                                  zIndex: 10,
                                                }}
                                              >
                                                {dragCount}
                                              </Box>
                                            )}
                                            <CardContent sx={{
                                              py: 1, px: 1.5, '&:last-child': { pb: 1 },
                                              ...(invoice.is_foreign ? { bgcolor: 'rgba(156, 39, 176, 0.06)', borderLeft: '3px solid', borderColor: 'secondary.main' } : {}),
                                            }}>
                                              {invoice.is_foreign && invoice.teacher_name && (
                                                <Typography variant="caption" color="secondary.main" fontWeight="bold" display="block" sx={{ mb: 0.25 }}>
                                                  {invoice.teacher_name}
                                                </Typography>
                                              )}
                                              <Box display="flex" alignItems="flex-start">
                                                <Checkbox
                                                  size="small"
                                                  checked={isSelected}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedInvoices(prev =>
                                                      prev.includes(invoice.id)
                                                        ? prev.filter(id => id !== invoice.id)
                                                        : [...prev, invoice.id]
                                                    );
                                                  }}
                                                  sx={{ p: 0, mr: 1, mt: -0.5 }}
                                                />
                                                <Box flex={1}>
                                                  <Typography variant="body2" fontWeight="bold">
                                                    {formatInvoiceDate(invoice.date)}
                                                  </Typography>
                                                  <Typography variant="caption" color="text.secondary">
                                                    de {formatTime(invoice.start_time)} à {formatTime(invoice.end_time)}
                                                  </Typography>
                                                  <Typography variant="body2" color="primary.main" sx={{ mt: 0.5 }}>
                                                    {formatPrice(Number(invoice.price) || 0)} frs
                                                  </Typography>
                                                  {invoice.discount && (
                                                    <Typography variant="caption" color="error.main" display="block">
                                                      -{formatPrice(Number(invoice.discount) || 0)} frs {invoice.discount_label}
                                                    </Typography>
                                                  )}
                                                </Box>
                                              </Box>
                                            </CardContent>
                                          </Card>
                                        )}
                                      </Draggable>
                                    );
                                  })}
                                  {provided.placeholder}
                                </Box>
                              )}
                            </Droppable>
                          </Paper>
                        </Grid>

                        {/* Paid Column */}
                        <Grid item xs={4}>
                          <Paper sx={{ p: 1, bgcolor: 'action.hover', minHeight: 400 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                              <Typography variant="subtitle2" fontWeight="bold">
                                Factures réglées
                              </Typography>
                              <Typography variant="caption" color="primary.main" fontWeight="bold">
                                {getTotalByStatus('paid')} frs
                              </Typography>
                            </Box>
                            <Droppable droppableId="paid" type="INVOICE">
                              {(provided, snapshot) => {
                                // Grouper les factures par payment_date
                                const paidInvoices = getInvoicesByStatus('paid');
                                const groupedInvoices: { [key: string]: typeof paidInvoices } = {};

                                paidInvoices.forEach(invoice => {
                                  const key = invoice.payment_date || `single_${invoice.id}`;
                                  if (!groupedInvoices[key]) {
                                    groupedInvoices[key] = [];
                                  }
                                  groupedInvoices[key].push(invoice);
                                });

                                // Trier les factures dans chaque groupe par date de facturation (plus récente en premier)
                                Object.values(groupedInvoices).forEach(group => {
                                  group.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
                                });

                                // Trier les groupes par la date de facturation la plus récente du groupe
                                const sortedGroups = Object.entries(groupedInvoices).sort(([, groupA], [, groupB]) => {
                                  const maxDateA = Math.max(...groupA.map(inv => new Date(inv.date).getTime()));
                                  const maxDateB = Math.max(...groupB.map(inv => new Date(inv.date).getTime()));
                                  return maxDateB - maxDateA;
                                });

                                return (
                                  <Box
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    sx={{
                                      minHeight: 350,
                                      backgroundColor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                                    }}
                                  >
                                    {sortedGroups.map(([paymentDate, groupInvoices]) => {
                                      const isGroup = groupInvoices.length > 1;
                                      const groupTotal = groupInvoices.reduce((sum, inv) => sum + (Number(inv.price) || 0), 0);
                                      const groupDiscount = groupInvoices.reduce((sum, inv) => sum + (Number(inv.discount) || 0), 0);
                                      const firstInvoice = groupInvoices[0];

                                      // Affichage groupé
                                      if (isGroup) {
                                        return (
                                          <Card
                                            key={paymentDate}
                                            sx={{
                                              mb: 1.5,
                                              border: '2px solid',
                                              borderColor: 'success.main',
                                              backgroundColor: '#e8f5e9',
                                            }}
                                          >
                                            {/* En-tête du groupe */}
                                            <Box sx={{
                                              px: 1.5,
                                              py: 1,
                                              backgroundColor: 'success.main',
                                              color: 'white',
                                            }}>
                                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                                <Typography variant="body2" fontWeight="bold">
                                                  Paiement groupé
                                                </Typography>
                                                <Typography variant="body1" fontWeight="bold">
                                                  {formatPrice(groupTotal - groupDiscount)} frs
                                                </Typography>
                                              </Box>
                                              <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
                                                <Typography variant="caption">
                                                  {formatPaymentMethod(firstInvoice.payment_method)} • {groupInvoices.length} cours
                                                </Typography>
                                                <Typography variant="caption">
                                                  {formatPaymentDate(firstInvoice.payment_date)}
                                                </Typography>
                                              </Box>
                                              {groupDiscount > 0 && (
                                                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                                  (dont réduction: -{formatPrice(groupDiscount)} frs)
                                                </Typography>
                                              )}
                                            </Box>

                                            {/* Liste des cours du groupe */}
                                            <Box sx={{ p: 1 }}>
                                              {groupInvoices.map((invoice) => (
                                                <Box
                                                  key={invoice.id}
                                                  onClick={() => handleOpenEditInvoice(invoice)}
                                                  sx={{
                                                    py: 0.5,
                                                    px: 1,
                                                    mb: 0.5,
                                                    cursor: 'pointer',
                                                    backgroundColor: 'background.paper',
                                                    borderRadius: 0.5,
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    '&:hover': {
                                                      backgroundColor: 'action.hover',
                                                    },
                                                    '&:last-child': {
                                                      mb: 0,
                                                    },
                                                  }}
                                                >
                                                  <Box display="flex" justifyContent="space-between" alignItems="center">
                                                    <Box>
                                                      <Typography variant="body2" fontWeight="medium">
                                                        {formatInvoiceDate(invoice.date)}
                                                      </Typography>
                                                      <Typography variant="caption" color="text.secondary">
                                                        {formatTime(invoice.start_time)} - {formatTime(invoice.end_time)}
                                                      </Typography>
                                                    </Box>
                                                    <Typography variant="body2" color="primary.main" fontWeight="bold">
                                                      {formatPrice(Number(invoice.price) || 0)} frs
                                                    </Typography>
                                                  </Box>
                                                </Box>
                                              ))}
                                            </Box>
                                          </Card>
                                        );
                                      }

                                      // Affichage facture individuelle
                                      const invoice = firstInvoice;
                                      return (
                                        <Card
                                          key={invoice.id}
                                          onClick={() => handleOpenEditInvoice(invoice)}
                                          sx={{
                                            mb: 1,
                                            cursor: 'pointer',
                                            backgroundColor: 'background.paper',
                                            '&:hover': {
                                              backgroundColor: 'action.hover',
                                            },
                                          }}
                                        >
                                          <CardContent sx={{
                                            py: 1, px: 1.5, '&:last-child': { pb: 1 },
                                            ...(invoice.is_foreign ? { bgcolor: 'rgba(156, 39, 176, 0.06)', borderLeft: '3px solid', borderColor: 'secondary.main' } : {}),
                                          }}>
                                            {invoice.is_foreign && invoice.teacher_name && (
                                              <Typography variant="caption" color="secondary.main" fontWeight="bold" display="block" sx={{ mb: 0.25 }}>
                                                {invoice.teacher_name}
                                              </Typography>
                                            )}
                                            <Typography variant="body2" fontWeight="bold">
                                              {formatInvoiceDate(invoice.date)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              de {formatTime(invoice.start_time)} à {formatTime(invoice.end_time)}
                                            </Typography>
                                            <Typography variant="body2" color="primary.main" sx={{ mt: 0.5 }}>
                                              {formatPrice(Number(invoice.price) || 0)} frs
                                            </Typography>
                                            {invoice.discount && (
                                              <Typography variant="caption" color="error.main" display="block">
                                                -{formatPrice(Number(invoice.discount) || 0)} frs {invoice.discount_label}
                                              </Typography>
                                            )}
                                            {invoice.payment_method && (
                                              <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5, fontWeight: 'bold' }}>
                                                {formatPaymentMethod(invoice.payment_method)}
                                              </Typography>
                                            )}
                                            {invoice.payment_date && (
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                Réglé le {formatPaymentDate(invoice.payment_date)}
                                              </Typography>
                                            )}
                                          </CardContent>
                                        </Card>
                                      );
                                    })}
                                    {provided.placeholder}
                                  </Box>
                                );
                              }}
                            </Droppable>
                          </Paper>
                        </Grid>
                      </Grid>

                      {/* Trash Zone */}
                      <Box mt={2}>
                        <Droppable droppableId="trash" type="INVOICE">
                          {(provided, snapshot) => (
                            <Paper
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              sx={{
                                p: 2,
                                textAlign: 'center',
                                backgroundColor: snapshot.isDraggingOver ? 'error.light' : 'action.hover',
                                border: '2px dashed',
                                borderColor: snapshot.isDraggingOver ? 'error.main' : 'grey.400',
                              }}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                Glissez une facture ici pour la supprimer
                              </Typography>
                              {provided.placeholder}
                            </Paper>
                          )}
                        </Droppable>
                      </Box>
                    </Box>
                  </DragDropContext>
                )}
                {activeTab === 3 && selectedStudent && (
                  <DragDropContext onDragEnd={handleObjectiveDragEnd}>
                    <Grid container spacing={2}>
                      {/* Left Panel - Assigned Objectives */}
                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, height: 'calc(100vh - 400px)', display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="h6" gutterBottom>
                            Objectifs assignés
                          </Typography>
                          <Droppable droppableId="assigned-objectives" type="OBJECTIVE">
                            {(provided, snapshot) => (
                              <Box
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                flex={1}
                                overflow="auto"
                                sx={{
                                  backgroundColor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                                  transition: 'background-color 0.2s',
                                  border: snapshot.isDraggingOver ? '2px dashed' : '2px dashed transparent',
                                  borderColor: snapshot.isDraggingOver ? 'primary.main' : 'transparent',
                                  borderRadius: 1,
                                }}
                              >
                                {objectiveAssignments.length === 0 ? (
                                  <Box
                                    sx={{
                                      p: 3,
                                      textAlign: 'center',
                                      border: '2px dashed',
                                      borderColor: 'grey.300',
                                      borderRadius: 1,
                                    }}
                                  >
                                    <Typography variant="body2" color="text.secondary">
                                      Aucun objectif assigné
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Glissez-déposez des objectifs ou des dossiers depuis la zone de navigation
                                    </Typography>
                                  </Box>
                                ) : (
                                  (() => {
                                    const renderAssignmentNode = (node: AssignmentTreeNode, level: number = 0): JSX.Element => {
                                      if (node.type === 'container') {
                                        return (
                                          <Accordion
                                            key={node.id}
                                            defaultExpanded
                                            sx={{
                                              ml: level * 2,
                                              mb: 0.5,
                                              '&:before': { display: 'none' },
                                            }}
                                          >
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                                <Box display="flex" alignItems="center" gap={1}>
                                                  <FolderIcon color="primary" sx={{ fontSize: 20 }} />
                                                  <Typography variant="body2" fontWeight="bold">
                                                    {node.title}
                                                  </Typography>
                                                </Box>
                                                {node.averageGrade !== undefined && (
                                                  <Box display="flex" alignItems="center" gap={1} sx={{ pr: 2 }}>
                                                    <Rating
                                                      size="small"
                                                      value={node.averageGrade}
                                                      readOnly
                                                      precision={0.1}
                                                    />
                                                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                                                      {node.averageGrade.toFixed(1)}/5
                                                    </Typography>
                                                  </Box>
                                                )}
                                              </Box>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ p: 0, pt: 1 }}>
                                              <Box>
                                                {node.children && node.children.map(child => renderAssignmentNode(child, level + 1))}
                                              </Box>
                                            </AccordionDetails>
                                          </Accordion>
                                        );
                                      } else {
                                        // Assignment node
                                        const assignment = node.assignment!;
                                        return (
                                          <Card
                                            key={node.id}
                                            sx={{
                                              ml: level * 2,
                                              mb: 0.5,
                                              cursor: 'pointer',
                                              '&:hover': {
                                                backgroundColor: 'action.hover',
                                              },
                                            }}
                                            onClick={() => {
                                              setSelectedAssignment(assignment);
                                              setTempGrade(assignment.grade || 0);
                                              setGradeDialogOpen(true);
                                            }}
                                          >
                                            <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                                              <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                                                <Box flex={1}>
                                                  <Typography variant="body2" fontWeight="bold">
                                                    {assignment.objective_title}
                                                  </Typography>
                                                  {assignment.objective_description && (
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                      {assignment.objective_description}
                                                    </Typography>
                                                  )}
                                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                    Assigné le {new Date(assignment.assigned_date).toLocaleDateString('fr-FR')}
                                                  </Typography>
                                                  {assignment.grade !== null && assignment.grade !== undefined && (
                                                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                                                      <Typography variant="caption" color="text.secondary">
                                                        Note:
                                                      </Typography>
                                                      <Rating
                                                        size="small"
                                                        value={assignment.grade}
                                                        readOnly
                                                      />
                                                      <Typography variant="caption" color="text.secondary">
                                                        {assignment.grade}/5
                                                      </Typography>
                                                    </Box>
                                                  )}
                                                </Box>
                                                <Box display="flex" gap={1} onClick={(e) => e.stopPropagation()}>
                                                  <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={async () => {
                                                      try {
                                                        await objectiveAssignmentService.delete(assignment.id);
                                                        toast.success('Objectif désassigné');
                                                        if (selectedStudent) {
                                                          loadObjectiveAssignments(selectedStudent.id);
                                                        }
                                                      } catch (error) {
                                                        console.error('Error deleting objective assignment:', error);
                                                        toast.error('Erreur lors de la suppression');
                                                      }
                                                    }}
                                                  >
                                                    <DeleteIcon fontSize="small" />
                                                  </IconButton>
                                                </Box>
                                              </Box>
                                            </CardContent>
                                          </Card>
                                        );
                                      }
                                    };

                                    return assignmentTree.map(node => renderAssignmentNode(node, 0));
                                  })()
                                )}
                                {provided.placeholder}
                              </Box>
                            )}
                          </Droppable>
                        </Paper>
                      </Grid>

                      {/* Right Panel - Browse Objectives Tree */}
                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, height: 'calc(100vh - 400px)', display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="h6" gutterBottom>
                            Naviguer dans les objectifs
                          </Typography>

                          <Droppable droppableId="browse-objectives" type="OBJECTIVE">
                            {(provided) => (
                              <Box ref={provided.innerRef} {...provided.droppableProps} flex={1} overflow="auto">
                                {(() => {
                                  let dragIndex = 0;
                                  const renderTreeNode = (node: ObjectiveTreeNode, level: number = 0): JSX.Element => {
                                    if (node.type === 'container') {
                                      const currentIndex = dragIndex++;
                                      return (
                                        <Draggable
                                          key={node.id}
                                          draggableId={node.id}
                                          index={currentIndex}
                                        >
                                          {(provided, snapshot) => (
                                            <Accordion
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                              defaultExpanded
                                              sx={{
                                                ml: level * 2,
                                                mb: 0.5,
                                                backgroundColor: snapshot.isDragging ? 'action.hover' : 'inherit',
                                                '&:before': { display: 'none' },
                                              }}
                                            >
                                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                <Box display="flex" alignItems="center" gap={1} width="100%">
                                                  <Box {...provided.dragHandleProps} display="flex">
                                                    <DragIndicatorIcon sx={{ fontSize: 20, cursor: 'grab' }} />
                                                  </Box>
                                                  <FolderIcon color="primary" sx={{ fontSize: 20 }} />
                                                  <Typography variant="body2" fontWeight="bold">
                                                    {node.title}
                                                  </Typography>
                                                </Box>
                                              </AccordionSummary>
                                              <AccordionDetails sx={{ p: 0, pt: 1 }}>
                                                <Box>
                                                  {node.children && node.children.map(child => renderTreeNode(child, level + 1))}
                                                </Box>
                                              </AccordionDetails>
                                            </Accordion>
                                          )}
                                        </Draggable>
                                      );
                                    } else {
                                      // Objective node
                                      const currentIndex = dragIndex++;
                                      return (
                                        <Draggable
                                          key={node.id}
                                          draggableId={node.id}
                                          index={currentIndex}
                                        >
                                          {(provided, snapshot) => (
                                            <Card
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                              sx={{
                                                ml: level * 2,
                                                mb: 0.5,
                                                backgroundColor: snapshot.isDragging ? 'action.hover' : 'inherit',
                                                cursor: 'grab',
                                              }}
                                            >
                                              <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                  <Box {...provided.dragHandleProps}>
                                                    <DragIndicatorIcon sx={{ fontSize: 20, cursor: 'grab' }} />
                                                  </Box>
                                                  <FileIcon sx={{ fontSize: 20 }} />
                                                  <Box flex={1}>
                                                    <Typography variant="body2" fontWeight="bold">
                                                      {node.title}
                                                    </Typography>
                                                    {node.objectiveData?.description && (
                                                      <Typography variant="caption" color="text.secondary">
                                                        {node.objectiveData.description}
                                                      </Typography>
                                                    )}
                                                  </Box>
                                                </Box>
                                              </CardContent>
                                            </Card>
                                          )}
                                        </Draggable>
                                      );
                                    }
                                  };

                                  return (
                                    <>
                                      {objectiveTree.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                                          Aucun objectif disponible
                                        </Typography>
                                      ) : (
                                        objectiveTree.map(node => renderTreeNode(node, 0))
                                      )}
                                      {provided.placeholder}
                                    </>
                                  );
                                })()}
                              </Box>
                            )}
                          </Droppable>
                        </Paper>
                      </Grid>
                    </Grid>
                  </DragDropContext>
                )}
                {activeTab === 4 && selectedStudent && (
                  <Box>
                    <Grid container spacing={2}>
                      {/* Left Panel - Quiz Sessions List */}
                      <Grid item xs={12} md={6}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                          <Typography variant="h6">Sessions de Quiz</Typography>
                        </Box>
                        <Box ref={quizSessionsListRef} sx={{ maxHeight: 400, overflow: 'auto' }}>
                          {quizSessions.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                              Aucune session de quiz
                            </Typography>
                          ) : (
                            quizSessions.map((session) => (
                                <Card
                                  key={session.id}
                                  sx={{
                                    mb: 1,
                                    cursor: 'pointer',
                                    backgroundColor: selectedQuizSession?.id === session.id ? 'action.selected' : 'inherit',
                                  }}
                                  onClick={async () => {
                                    setSelectedQuizSession(session);
                                    setSelectedQuizAssignment(null);
                                    setIsConfiguringNextQuiz(false);
                                    // Load assignments for this session
                                    if (selectedStudent) {
                                      const assignments = await quizAssignmentService.getByStudent(selectedStudent.id);
                                      const sessionAssignments = assignments.filter(a => a.session === session.id);
                                      setQuizAssignments(sessionAssignments);
                                    }
                                  }}
                                >
                                  <CardContent sx={{ py: 1.5 }}>
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                      <Box flex={1}>
                                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                          <Typography variant="body2" fontWeight="bold">
                                            {session.name}
                                          </Typography>
                                          <Box
                                            sx={{
                                              px: 1,
                                              py: 0.25,
                                              borderRadius: 1,
                                              backgroundColor: session.is_active ? 'success.main' : 'grey.400',
                                              color: 'white',
                                              fontSize: '0.7rem',
                                              fontWeight: 'bold',
                                            }}
                                          >
                                            {session.is_active ? 'Active' : 'Inactive'}
                                          </Box>
                                          <Box
                                            sx={{
                                              px: 1,
                                              py: 0.25,
                                              borderRadius: 1,
                                              backgroundColor: session.is_available ? 'info.main' : 'warning.main',
                                              color: 'white',
                                              fontSize: '0.7rem',
                                              fontWeight: 'bold',
                                            }}
                                          >
                                            {session.is_available ? 'Disponible' : 'Indisponible'}
                                          </Box>
                                        </Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          {session.quiz_count} questions
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Assigné le : {new Date(session.assigned_date).toLocaleDateString('fr-FR')}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </CardContent>
                                </Card>
                            ))
                          )}
                        </Box>
                        <Box mt={2}>
                          <Button
                            variant="contained"
                            fullWidth
                            startIcon={<AddIcon />}
                            onClick={() => {
                              setIsConfiguringNextQuiz(true);
                              setSelectedQuizAssignment(null);
                              setSelectedQuizSession(null);
                              setBrowseTab(0); // Start with Sessions tab
                              // Pre-select the current student and load their sessions
                              if (selectedStudent) {
                                setSelectedBrowseStudent(selectedStudent);
                                loadBrowseSessions(selectedStudent.id);
                              }
                            }}
                          >
                            Configurer le prochain quiz
                          </Button>
                        </Box>
                      </Grid>

                      {/* Right Panel - Session Details or Next Quiz Configuration */}
                      <Grid item xs={12} md={6}>
                        {isConfiguringNextQuiz ? (
                          <DragDropContext onDragEnd={handleQuizDragEnd}>
                            <Grid container spacing={2}>
                              {/* Browse Zone - Left */}
                              <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2, height: 'calc(100vh - 400px)', overflow: 'auto' }}>
                                  <Typography variant="h6" gutterBottom>
                                    Naviguer
                                  </Typography>
                                  <Tabs value={browseTab} onChange={(e, newValue) => setBrowseTab(newValue)} sx={{ mb: 2 }}>
                                    <Tab label="Sessions" />
                                    <Tab label="Atelier" />
                                    <Tab label="Professeurs" />
                                  </Tabs>

                                  {browseTab === 0 && (
                                    <Box>
                                      {/* Show students list if no student is selected */}
                                      {!selectedBrowseStudent ? (
                                        <Box>
                                          {students.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                                              Aucun élève disponible
                                            </Typography>
                                          ) : (
                                            students.map((student) => (
                                              <Card
                                                key={student.id}
                                                sx={{
                                                  mb: 1,
                                                  cursor: 'pointer',
                                                  backgroundColor: student.color ? `#${student.color}` : 'inherit',
                                                  '&:hover': {
                                                    backgroundColor: student.color ? `#${student.color}` : 'action.hover',
                                                    opacity: 0.8,
                                                  },
                                                }}
                                                onClick={() => {
                                                  setSelectedBrowseStudent(student);
                                                  loadBrowseSessions(student.id);
                                                }}
                                              >
                                                <CardContent sx={{ py: 1 }}>
                                                  <Box display="flex" alignItems="center" gap={1}>
                                                    <Avatar
                                                      src={student.profile_picture}
                                                      sx={{ width: 48, height: 48 }}
                                                    >
                                                      {!student.profile_picture && <PersonIcon sx={{ fontSize: 28 }} />}
                                                    </Avatar>
                                                    <Box flex={1}>
                                                      <Typography variant="body2" fontWeight="bold">
                                                        {student.first_name} {student.last_name || ''}
                                                      </Typography>
                                                      <Typography variant="caption" color="text.secondary">
                                                        {getClassName(student.class_group)}
                                                      </Typography>
                                                    </Box>
                                                  </Box>
                                                </CardContent>
                                              </Card>
                                            ))
                                          )}
                                        </Box>
                                      ) : (
                                        /* Show sessions for selected student */
                                        <Box>
                                          {/* Back button to return to students list */}
                                          <Card
                                            sx={{
                                              mb: 1,
                                              cursor: 'pointer',
                                              backgroundColor: 'action.hover',
                                            }}
                                            onClick={() => {
                                              if (selectedBrowseSession && browseSessionContent) {
                                                // If viewing session content, go back to sessions list
                                                setSelectedBrowseSession(null);
                                                setBrowseSessionContent(null);
                                              } else {
                                                // If viewing sessions list, go back to students list
                                                setSelectedBrowseStudent(null);
                                                setBrowseSessions([]);
                                              }
                                            }}
                                          >
                                            <CardContent sx={{ py: 1 }}>
                                              <Box display="flex" alignItems="center" gap={1}>
                                                <ArrowUpIcon color="action" />
                                                <Typography variant="body2" fontWeight="bold">
                                                  {selectedBrowseSession && browseSessionContent
                                                    ? 'Retour aux sessions'
                                                    : 'Retour aux élèves'}
                                                </Typography>
                                              </Box>
                                            </CardContent>
                                          </Card>

                                          {/* Show session content if a session is selected */}
                                          {selectedBrowseSession && browseSessionContent ? (
                                            <Box>
                                              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                                {selectedBrowseSession.name}
                                              </Typography>
                                              {browseSessionContent.map((assignment) => (
                                                <Card key={assignment.id} sx={{ mb: 1 }}>
                                                  <CardContent sx={{ py: 1 }}>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                      <FileIcon sx={{ fontSize: 20 }} />
                                                      <Box flex={1}>
                                                        <Typography variant="body2">
                                                          {assignment.quiz_question.substring(0, 40)}
                                                          {assignment.quiz_question.length > 40 && '...'}
                                                        </Typography>
                                                        {assignment.quiz_chapitre && (
                                                          <Typography variant="caption" color="text.secondary" display="block">
                                                            {assignment.quiz_chapitre}
                                                          </Typography>
                                                        )}
                                                        <Box display="flex" gap={1} mt={0.5}>
                                                          <Chip
                                                            label={`Score: ${assignment.student_question_score?.toFixed(1) || '0.0'}%`}
                                                            size="small"
                                                            color="primary"
                                                            variant="outlined"
                                                          />
                                                          <Chip
                                                            label={`Posée ${assignment.times_asked || 0}×`}
                                                            size="small"
                                                            variant="outlined"
                                                          />
                                                        </Box>
                                                      </Box>
                                                      <IconButton
                                                        size="small"
                                                        onClick={async () => {
                                                          if (!selectedBrowseStudent) return;
                                                          try {
                                                            const history = await quizAssignmentService.getQuestionHistory(
                                                              selectedBrowseStudent.id,
                                                              assignment.quiz
                                                            );
                                                            setQuestionHistory(history);
                                                            setSelectedQuestionForHistory(assignment);
                                                            setHistoryModalOpen(true);
                                                          } catch (error) {
                                                            console.error('Error loading history:', error);
                                                            toast.error('Erreur lors du chargement de l\'historique');
                                                          }
                                                        }}
                                                      >
                                                        <HistoryIcon />
                                                      </IconButton>
                                                    </Box>
                                                  </CardContent>
                                                </Card>
                                              ))}
                                            </Box>
                                          ) : (
                                            /* Show sessions list */
                                            <Droppable droppableId="browse-sessions" type="BROWSE">
                                              {(provided) => (
                                                <Box ref={provided.innerRef} {...provided.droppableProps}>
                                                  {browseSessions.length === 0 ? (
                                                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                                                      Aucune session disponible pour cet élève
                                                    </Typography>
                                                  ) : (
                                                    browseSessions.map((session, index) => {
                                                      const isLastSession = index === browseSessions.length - 1;
                                                      return (
                                                        <Draggable
                                                          key={`browse-session-${session.id}`}
                                                          draggableId={`browse-session-${session.id}`}
                                                          index={index}
                                                        >
                                                          {(provided, snapshot) => (
                                                            <Card
                                                              ref={provided.innerRef}
                                                              {...provided.draggableProps}
                                                              sx={{
                                                                mb: 1,
                                                                backgroundColor: snapshot.isDragging ? 'action.hover' : 'inherit',
                                                                cursor: 'grab',
                                                              }}
                                                            >
                                                              <CardContent sx={{ py: 1 }}>
                                                                <Box display="flex" alignItems="center" gap={1}>
                                                                  <Box {...provided.dragHandleProps}>
                                                                    <DragIndicatorIcon sx={{ fontSize: 20, cursor: 'grab' }} />
                                                                  </Box>
                                                                  <Box
                                                                    flex={1}
                                                                    onClick={() => {
                                                                      setSelectedBrowseSession(session);
                                                                      loadBrowseSessionContent(session.id, selectedBrowseStudent.id);
                                                                    }}
                                                                    sx={{ cursor: 'pointer' }}
                                                                  >
                                                                    <Box display="flex" alignItems="center" gap={1}>
                                                                      <Typography variant="body2" fontWeight="bold">
                                                                        {session.name}
                                                                      </Typography>
                                                                      <Box
                                                                        sx={{
                                                                          px: 1,
                                                                          py: 0.25,
                                                                          borderRadius: 1,
                                                                          backgroundColor: isLastSession ? 'success.main' : 'error.main',
                                                                          color: 'white',
                                                                          fontSize: '0.7rem',
                                                                          fontWeight: 'bold',
                                                                        }}
                                                                      >
                                                                        {isLastSession ? 'Active' : 'Terminée'}
                                                                      </Box>
                                                                    </Box>
                                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                                      {session.quiz_count} questions
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                      {new Date(session.assigned_date).toLocaleDateString('fr-FR')}
                                                                    </Typography>
                                                                  </Box>
                                                                </Box>
                                                              </CardContent>
                                                            </Card>
                                                          )}
                                                        </Draggable>
                                                      );
                                                    })
                                                  )}
                                                  {provided.placeholder}
                                                </Box>
                                              )}
                                            </Droppable>
                                          )}
                                        </Box>
                                      )}
                                    </Box>
                                  )}

                                  {browseTab === 1 && (
                                    <Droppable droppableId="browse-items" type="BROWSE">
                                      {(provided) => (
                                        <Box ref={provided.innerRef} {...provided.droppableProps}>
                                          {/* Parent navigation */}
                                          {browseCurrentContainer !== null && (
                                            <Card
                                              sx={{
                                                mb: 1,
                                                cursor: 'pointer',
                                                backgroundColor: 'action.hover',
                                              }}
                                              onClick={() => setBrowseCurrentContainer(browseParentContainer)}
                                            >
                                              <CardContent sx={{ py: 1 }}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                  <ArrowUpIcon color="action" />
                                                  <Typography variant="body2" fontWeight="bold">..</Typography>
                                                </Box>
                                              </CardContent>
                                            </Card>
                                          )}

                                          {/* Containers */}
                                          {browseContainers.map((container, index) => (
                                            <Draggable
                                              key={`browse-container-${container.id}`}
                                              draggableId={`browse-container-${container.id}`}
                                              index={index}
                                            >
                                              {(provided, snapshot) => (
                                                <Card
                                                  ref={provided.innerRef}
                                                  {...provided.draggableProps}
                                                  sx={{
                                                    mb: 1,
                                                    backgroundColor: snapshot.isDragging ? 'action.hover' : 'inherit',
                                                    cursor: 'grab',
                                                  }}
                                                >
                                                  <CardContent sx={{ py: 1 }}>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                      <Box {...provided.dragHandleProps}>
                                                        <DragIndicatorIcon sx={{ fontSize: 20, cursor: 'grab' }} />
                                                      </Box>
                                                      <FolderIcon color="primary" />
                                                      <Box
                                                        flex={1}
                                                        onClick={() => setBrowseCurrentContainer(container.id)}
                                                        sx={{ cursor: 'pointer' }}
                                                      >
                                                        <Typography variant="body2" fontWeight="bold">
                                                          {container.title}
                                                        </Typography>
                                                      </Box>
                                                    </Box>
                                                  </CardContent>
                                                </Card>
                                              )}
                                            </Draggable>
                                          ))}

                                          {/* Quizzes */}
                                          {browseQuizzes.map((quiz, index) => (
                                            <Draggable
                                              key={`browse-quiz-${quiz.id}`}
                                              draggableId={`browse-quiz-${quiz.id}`}
                                              index={browseContainers.length + index}
                                            >
                                              {(provided, snapshot) => (
                                                <Card
                                                  ref={provided.innerRef}
                                                  {...provided.draggableProps}
                                                  sx={{
                                                    mb: 1,
                                                    backgroundColor: snapshot.isDragging ? 'action.hover' : 'inherit',
                                                    cursor: 'grab',
                                                  }}
                                                >
                                                  <CardContent sx={{ py: 1 }}>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                      <Box {...provided.dragHandleProps}>
                                                        <DragIndicatorIcon sx={{ fontSize: 20, cursor: 'grab' }} />
                                                      </Box>
                                                      <FileIcon sx={{ fontSize: 20 }} />
                                                      <Box flex={1}>
                                                        <Typography variant="body2" fontWeight="bold">
                                                          {quiz.question.substring(0, 40)}
                                                          {quiz.question.length > 40 && '...'}
                                                        </Typography>
                                                        {quiz.chapitre && (
                                                          <Typography variant="caption" color="text.secondary">
                                                            {quiz.chapitre}
                                                          </Typography>
                                                        )}
                                                      </Box>
                                                    </Box>
                                                  </CardContent>
                                                </Card>
                                              )}
                                            </Draggable>
                                          ))}
                                          {provided.placeholder}
                                        </Box>
                                      )}
                                    </Droppable>
                                  )}

                                  {browseTab === 2 && (
                                    <Typography variant="body2" color="text.secondary" align="center">
                                      Professeurs - À venir
                                    </Typography>
                                  )}
                                </Paper>
                              </Grid>

                            {/* Selected Items Zone - Right */}
                            <Grid item xs={12} md={6}>
                              <Paper sx={{ p: 2, height: 'calc(100vh - 400px)', display: 'flex', flexDirection: 'column' }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                  <Typography variant="h6">
                                    Éléments sélectionnés
                                  </Typography>
                                  <Button
                                    size="small"
                                    onClick={() => setIsConfiguringNextQuiz(false)}
                                  >
                                    Annuler
                                  </Button>
                                </Box>

                                <Droppable droppableId="selected-quiz-items" type="BROWSE">
                                  {(provided, snapshot) => (
                                    <Box
                                      ref={provided.innerRef}
                                      {...provided.droppableProps}
                                      flex={1}
                                      overflow="auto"
                                      mb={2}
                                      sx={{
                                        backgroundColor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                                        transition: 'background-color 0.2s',
                                        border: snapshot.isDraggingOver ? '2px dashed' : '2px dashed transparent',
                                        borderColor: snapshot.isDraggingOver ? 'primary.main' : 'transparent',
                                        borderRadius: 1,
                                      }}
                                    >
                                      {selectedQuizItems.length === 0 ? (
                                        <Box
                                          sx={{
                                            p: 3,
                                            textAlign: 'center',
                                            border: '2px dashed',
                                            borderColor: 'grey.300',
                                            borderRadius: 1,
                                          }}
                                        >
                                          <Typography variant="body2" color="text.secondary">
                                            Aucun élément sélectionné
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Glissez-déposez des questions ou des groupes depuis la zone de navigation
                                          </Typography>
                                        </Box>
                                      ) : (
                                        <Box>
                                          {selectedQuizItems.map((item, index) => (
                                            <Card key={`${item.type}-${item.id}`} sx={{ mb: 1 }}>
                                              <CardContent sx={{ py: 1.5 }}>
                                                <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                                                  <Box flex={1}>
                                                    <Typography variant="body2" fontWeight="bold">
                                                      {item.type === 'quiz'
                                                        ? (item.data as Quiz).question.substring(0, 40) + ((item.data as Quiz).question.length > 40 ? '...' : '')
                                                        : (item.data as QuizContainer).title
                                                      }
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                      {item.type === 'quiz' ? 'Question individuelle' : 'Groupe de questions'}
                                                      {item.type === 'quiz' && (item.data as Quiz).chapitre && ` • ${(item.data as Quiz).chapitre}`}
                                                    </Typography>
                                                    {item.type === 'container' && item.excludedQuizIds && item.excludedQuizIds.length > 0 && item.allQuizzes && (
                                                      <Box mt={0.5}>
                                                        <Typography variant="caption" color="error.main" fontWeight="bold">
                                                          Questions exclues:
                                                        </Typography>
                                                        {item.allQuizzes
                                                          .filter(q => item.excludedQuizIds?.includes(q.id))
                                                          .map(excludedQuiz => (
                                                            <Typography key={excludedQuiz.id} variant="caption" color="text.secondary" display="block" sx={{ pl: 1 }}>
                                                              • {excludedQuiz.question.substring(0, 100)}
                                                              {excludedQuiz.question.length > 100 && '...'}
                                                            </Typography>
                                                          ))}
                                                      </Box>
                                                    )}
                                                  </Box>
                                                  <Box display="flex" gap={1}>
                                                    {item.type === 'container' && (
                                                      <IconButton
                                                        size="small"
                                                        onClick={async () => {
                                                          // Reload container to modify exclusions
                                                          try {
                                                            // Use stored quizzes if available, otherwise fetch
                                                            const quizzes = item.allQuizzes || [];
                                                            if (!item.allQuizzes || item.allQuizzes.length === 0) {
                                                              const contents = await quizContainerService.getContainerContents(item.id);
                                                              setContainerQuizzes(contents.quizzes || []);
                                                            } else {
                                                              setContainerQuizzes(quizzes);
                                                            }
                                                            setCustomizingContainer(item.data as QuizContainer);
                                                            setExcludedQuizIds(item.excludedQuizIds || []);
                                                            setCustomizeDialogOpen(true);
                                                          } catch (error) {
                                                            console.error('Error loading container:', error);
                                                            toast.error('Erreur lors du chargement du groupe');
                                                          }
                                                        }}
                                                      >
                                                        <EditIcon fontSize="small" />
                                                      </IconButton>
                                                    )}
                                                    <IconButton
                                                      size="small"
                                                      color="error"
                                                      onClick={() => {
                                                        if (!selectedStudent) return;
                                                        const newItems = selectedQuizItems.filter((_, i) => i !== index);
                                                        setSelectedQuizItems(newItems);
                                                        saveNextQuizItems(selectedStudent.id, newItems);
                                                      }}
                                                    >
                                                      <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                  </Box>
                                                </Box>
                                              </CardContent>
                                            </Card>
                                          ))}
                                        </Box>
                                      )}
                                      {provided.placeholder}
                                    </Box>
                                  )}
                                </Droppable>

                                <Button
                                  variant="contained"
                                  fullWidth
                                  disabled={selectedQuizItems.length === 0}
                                  onClick={handleValidateAndAssign}
                                >
                                  Valider et assigner
                                </Button>
                              </Paper>
                            </Grid>
                          </Grid>
                          </DragDropContext>
                        ) : selectedQuizSession ? (
                          <Paper sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>
                              {selectedQuizSession.name}
                            </Typography>
                            <Box mb={2}>
                              <Typography variant="caption" color="text.secondary">
                                Créé par: {selectedQuizSession.created_by_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Assigné le : {new Date(selectedQuizSession.assigned_date).toLocaleDateString('fr-FR')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Total: {selectedQuizSession.quiz_count} questions
                              </Typography>
                            </Box>

                            <Box display="flex" gap={2} mb={2}>
                              <Button
                                variant="outlined"
                                size="small"
                                fullWidth
                                color={selectedQuizSession.is_active ? 'success' : 'inherit'}
                                onClick={async () => {
                                  try {
                                    const updatedSession = await quizSessionService.toggleActive(selectedQuizSession.id);
                                    setSelectedQuizSession(updatedSession);
                                    if (selectedStudent) {
                                      await loadQuizSessions(selectedStudent.id);
                                    }
                                    toast.success(updatedSession.is_active ? 'Session activée' : 'Session désactivée');
                                  } catch (error) {
                                    console.error('Error toggling active status:', error);
                                    toast.error('Erreur lors de la modification du statut');
                                  }
                                }}
                              >
                                {selectedQuizSession.is_active ? 'Désactiver' : 'Activer'}
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                fullWidth
                                color={selectedQuizSession.is_available ? 'info' : 'warning'}
                                onClick={async () => {
                                  try {
                                    const updatedSession = await quizSessionService.toggleAvailable(selectedQuizSession.id);
                                    setSelectedQuizSession(updatedSession);
                                    if (selectedStudent) {
                                      await loadQuizSessions(selectedStudent.id);
                                    }
                                    toast.success(updatedSession.is_available ? 'Session rendue disponible' : 'Session rendue indisponible');
                                  } catch (error) {
                                    console.error('Error toggling available status:', error);
                                    toast.error('Erreur lors de la modification de la disponibilité');
                                  }
                                }}
                              >
                                {selectedQuizSession.is_available ? 'Rendre indisponible' : 'Rendre disponible'}
                              </Button>
                            </Box>

                            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                              Quiz dans cette session:
                            </Typography>
                            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                              {quizAssignments.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                                  Aucun quiz dans cette session
                                </Typography>
                              ) : (
                                quizAssignments.map((assignment) => (
                                  <Card
                                    key={assignment.id}
                                    sx={{
                                      mb: 1,
                                      backgroundColor: selectedQuizAssignment?.id === assignment.id ? 'action.selected' : 'inherit',
                                    }}
                                  >
                                    <CardContent sx={{ py: 1 }}>
                                      <Box
                                        display="flex"
                                        alignItems="flex-start"
                                        gap={1}
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => setSelectedQuizAssignment(assignment)}
                                      >
                                        <Box flex={1}>
                                          <Typography variant="body2" fontWeight="bold">
                                            {assignment.quiz_question.substring(0, 60)}
                                            {assignment.quiz_question.length > 60 && '...'}
                                          </Typography>
                                          {assignment.quiz_chapitre && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                              {assignment.quiz_chapitre}
                                            </Typography>
                                          )}
                                          <Box display="flex" gap={2} mt={0.5} alignItems="center" flexWrap="wrap">
                                            {/* Score Progress Bar */}
                                            <Box flex={1} minWidth="200px">
                                              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                                <Typography variant="caption" color="text.secondary">
                                                  Score:
                                                </Typography>
                                                <Typography variant="caption" fontWeight="bold">
                                                  {assignment.student_question_score?.toFixed(1) || '0.0'}%
                                                </Typography>
                                              </Box>
                                              <LinearProgress
                                                variant="determinate"
                                                value={assignment.student_question_score || 0}
                                                sx={{
                                                  height: 8,
                                                  borderRadius: 1,
                                                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                  '& .MuiLinearProgress-bar': {
                                                    backgroundColor: (assignment.student_question_score || 0) >= 50
                                                      ? '#4caf50'
                                                      : '#f44336',
                                                    borderRadius: 1,
                                                  }
                                                }}
                                              />
                                            </Box>

                                            {/* Times Asked with Tooltip */}
                                            <Tooltip title={`Cette question a été posée ${assignment.times_asked || 0} fois`}>
                                              <Typography
                                                variant="body1"
                                                fontWeight="bold"
                                                sx={{
                                                  cursor: 'help',
                                                  color: 'text.secondary',
                                                  '&:hover': {
                                                    color: 'text.primary',
                                                  }
                                                }}
                                              >
                                                {assignment.times_asked || 0}
                                              </Typography>
                                            </Tooltip>
                                          </Box>
                                        </Box>
                                        <IconButton
                                          size="small"
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            if (!selectedStudent) return;
                                            try {
                                              const history = await quizAssignmentService.getQuestionHistory(
                                                selectedStudent.id,
                                                assignment.quiz
                                              );
                                              setQuestionHistory(history);
                                              setSelectedQuestionForHistory(assignment);
                                              setHistoryModalOpen(true);
                                            } catch (error) {
                                              console.error('Error loading history:', error);
                                              toast.error('Erreur lors du chargement de l\'historique');
                                            }
                                          }}
                                        >
                                          <HistoryIcon />
                                        </IconButton>
                                      </Box>
                                    </CardContent>
                                  </Card>
                                ))
                              )}
                            </Box>

                            {selectedQuizAssignment && (
                              <Box mt={2} p={2} sx={{ bgcolor: 'action.hover', borderRadius: 1 }}>
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                  Détails du quiz sélectionné
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Question
                                </Typography>
                                <Typography variant="body2" paragraph>
                                  {selectedQuizAssignment.quiz_question}
                                </Typography>

                                <Typography variant="caption" color="text.secondary" display="block">
                                  Réponse correcte
                                </Typography>
                                <Typography variant="body2" paragraph>
                                  {selectedQuizAssignment.quiz_reponse}
                                </Typography>

                                {selectedQuizAssignment.student_answer && (
                                  <>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Réponse de l'élève
                                    </Typography>
                                    <Typography variant="body2" paragraph>
                                      {selectedQuizAssignment.student_answer}
                                    </Typography>
                                  </>
                                )}

                                {selectedQuizAssignment.score !== null && (
                                  <>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Note
                                    </Typography>
                                    <Typography variant="body2">
                                      {selectedQuizAssignment.score}/100
                                    </Typography>
                                  </>
                                )}
                              </Box>
                            )}
                          </Paper>
                        ) : (
                          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                            <Typography variant="body2" color="text.secondary">
                              Sélectionnez une session pour voir les détails
                            </Typography>
                          </Box>
                        )}
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    {/* Préconisations Section */}
                    <Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">Préconisations</Typography>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          size="small"
                          onClick={() => {
                            setRecoDialogOpen(true);
                            setEditingReco(null);
                            setRecoStartDate(dayjs().format('YYYY-MM-DD'));
                            setRecoEndDate(dayjs().add(1, 'week').format('YYYY-MM-DD'));
                            setRecoSessionsPerDay(3);
                            setRecoSelectedSessions([]);
                            setRecoDialogMonth(dayjs().format('YYYY-MM'));
                            setRecoPickingBound('start');
                          }}
                        >
                          Ajouter
                        </Button>
                      </Box>

                      {/* Calendar View with drag-to-resize */}
                      <Box mb={2}>
                        <Box display="flex" alignItems="center" justifyContent="center" gap={2} mb={2}>
                          <IconButton size="small" onClick={() => setRecoCalendarMonth(prev => dayjs(prev).subtract(1, 'month').format('YYYY-MM'))}>
                            <ChevronLeftIcon />
                          </IconButton>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {dayjs(recoCalendarMonth + '-01').format('MMMM YYYY')}
                          </Typography>
                          <IconButton size="small" onClick={() => setRecoCalendarMonth(prev => dayjs(prev).add(1, 'month').format('YYYY-MM'))}>
                            <ChevronRightIcon />
                          </IconButton>
                        </Box>

                        <Box sx={{ overflowX: 'auto', userSelect: recoDragging ? 'none' : 'auto' }}>
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', minWidth: 500, bgcolor: '#eee' }}>
                            {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(d => (
                              <Box key={d} sx={{ bgcolor: 'background.paper', py: 0.5 }}>
                                <Typography variant="caption" align="center" display="block" fontWeight={600} color="text.secondary">
                                  {d}
                                </Typography>
                              </Box>
                            ))}
                            {(() => {
                              const monthStart = dayjs(recoCalendarMonth + '-01');
                              const startOfWeek = monthStart.startOf('month').day() === 0 ? 6 : monthStart.startOf('month').day() - 1;
                              const daysInMonth = monthStart.daysInMonth();
                              const cells = [];
                              for (let i = 0; i < startOfWeek; i++) {
                                cells.push(<Box key={`empty-${i}`} sx={{ bgcolor: 'background.paper' }} />);
                              }
                              for (let day = 1; day <= daysInMonth; day++) {
                                const dateStr = monthStart.date(day).format('YYYY-MM-DD');
                                const isToday = dateStr === dayjs().format('YYYY-MM-DD');
                                cells.push(
                                  <Box
                                    key={day}
                                    onMouseEnter={() => { if (recoDragging) setRecoDragDate(dateStr); }}
                                    sx={{
                                      position: 'relative',
                                      bgcolor: 'background.paper',
                                      px: 0.25,
                                      pt: 0.5,
                                      pb: 0.25,
                                      textAlign: 'center',
                                      minHeight: 48,
                                      outline: isToday ? '2px solid #1976d2' : 'none',
                                      outlineOffset: '-2px',
                                      borderRadius: isToday ? '4px' : 0,
                                    }}
                                  >
                                    <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: isToday ? 700 : 400, position: 'relative', zIndex: 2 }}>{day}</Typography>
                                    {sortedRecommendations.map((reco) => {
                                      const { start, end } = getEffectiveRange(reco);
                                      if (start > end || dateStr < start || dateStr > end) return null;
                                      const isStart = dateStr === start;
                                      const isEnd = dateStr === end;
                                      const color = getRecoColor(reco.id);
                                      const isDraggingThis = recoDragging?.recoId === reco.id;
                                      return (
                                        <Box
                                          key={reco.id}
                                          sx={{
                                            height: 7,
                                            bgcolor: isDraggingThis ? `${color}cc` : color,
                                            mt: 0.25,
                                            ml: isStart ? '4px' : '-1px',
                                            mr: isEnd ? '4px' : '-1px',
                                            borderTopLeftRadius: isStart ? 4 : 0,
                                            borderBottomLeftRadius: isStart ? 4 : 0,
                                            borderTopRightRadius: isEnd ? 4 : 0,
                                            borderBottomRightRadius: isEnd ? 4 : 0,
                                            position: 'relative',
                                            zIndex: 1,
                                            // During drag, let mouse events pass through bars to cells
                                            pointerEvents: recoDragging ? (isDraggingThis ? 'none' : 'none') : 'auto',
                                            cursor: (isStart || isEnd) ? 'col-resize' : 'pointer',
                                          }}
                                          title={`${reco.sessions_per_day} sessions/jour`}
                                          onMouseDown={(e) => {
                                            if (isStart || isEnd) {
                                              e.preventDefault();
                                              const bound = isStart && !isEnd ? 'start' : isEnd && !isStart ? 'end' : (e.nativeEvent.offsetX < (e.currentTarget.offsetWidth / 2) ? 'start' : 'end');
                                              setRecoDragging({ recoId: reco.id, bound });
                                              setRecoDragDate(dateStr);
                                            } else {
                                              setEditingReco(reco);
                                              setRecoStartDate(reco.start_date);
                                              setRecoEndDate(reco.end_date);
                                              setRecoSessionsPerDay(reco.sessions_per_day);
                                              setRecoSelectedSessions(reco.quiz_sessions || []);
                                              setRecoDialogMonth(dayjs(reco.start_date).format('YYYY-MM'));
                                              setRecoDialogOpen(true);
                                            }
                                          }}
                                        />
                                      );
                                    })}
                                  </Box>
                                );
                              }
                              return cells;
                            })()}
                          </Box>
                        </Box>
                      </Box>

                      {/* List of recommendations (chronological) */}
                      {sortedRecommendations.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                          Aucune préconisation pour cet élève
                        </Typography>
                      ) : (
                        <Box>
                          {sortedRecommendations.map((reco) => (
                            <Paper
                              key={reco.id}
                              variant="outlined"
                              sx={{
                                p: 1.5,
                                mb: 1,
                                bgcolor: getRecoBgColor(reco.id),
                                borderLeft: `4px solid ${getRecoColor(reco.id)}`,
                              }}
                            >
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                  <Typography variant="subtitle2">
                                    {reco.sessions_per_day} session{reco.sessions_per_day > 1 ? 's' : ''}/jour
                                    {reco.quiz_sessions_detail && reco.quiz_sessions_detail.length > 0 && (
                                      <Typography component="span" variant="caption" color="primary" sx={{ ml: 1 }}>
                                        ({reco.quiz_sessions_detail.map(s => s.name).join(', ')})
                                      </Typography>
                                    )}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Du {dayjs(reco.start_date).format('DD/MM/YYYY')} au {dayjs(reco.end_date).format('DD/MM/YYYY')}
                                  </Typography>
                                </Box>
                                <Box>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setEditingReco(reco);
                                      setRecoStartDate(reco.start_date);
                                      setRecoEndDate(reco.end_date);
                                      setRecoSessionsPerDay(reco.sessions_per_day);
                                      setRecoSelectedSessions(reco.quiz_sessions || []);
                                      setRecoDialogMonth(dayjs(reco.start_date).format('YYYY-MM'));
                                      setRecoDialogOpen(true);
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={async () => {
                                      if (!window.confirm('Supprimer cette préconisation ?')) return;
                                      try {
                                        await quizRecommendationService.delete(reco.id);
                                        setRecommendations(prev => prev.filter(r => r.id !== reco.id));
                                        toast.success('Préconisation supprimée');
                                      } catch { toast.error('Erreur'); }
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                            </Paper>
                          ))}
                        </Box>
                      )}

                      <Divider sx={{ my: 3 }} />

                      {/* Teacher Control Section */}
                      <Typography variant="h6" gutterBottom>Contrôle des créneaux</Typography>
                      <Box display="flex" alignItems="center" gap={2} mb={2}>
                        <ToggleButtonGroup
                          value={teacherControlledBy}
                          exclusive
                          onChange={async (_, val) => {
                            if (!val || !selectedStudent) return;
                            setTeacherControlledBy(val);
                            try {
                              await quizRecommendationService.updateTeacherControl(selectedStudent.id, { controlled_by: val });
                              toast.success('Mode mis à jour');
                            } catch { toast.error('Erreur'); }
                          }}
                          size="small"
                        >
                          <ToggleButton value="teacher">Enseignant</ToggleButton>
                          <ToggleButton value="parent">Parent</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>

                      {teacherControlledBy === 'teacher' ? (
                        <Box>
                          {teacherTimeSlots.map((slot, idx) => (
                            <Box key={idx} display="flex" alignItems="center" gap={1} mb={1}>
                              <TextField
                                size="small"
                                label="Label"
                                value={slot.label}
                                onChange={(e) => {
                                  const updated = [...teacherTimeSlots];
                                  updated[idx] = { ...updated[idx], label: e.target.value };
                                  setTeacherTimeSlots(updated);
                                }}
                                sx={{ width: 120 }}
                              />
                              <TextField
                                size="small"
                                label="Début"
                                type="time"
                                value={slot.start_time}
                                onChange={(e) => {
                                  const updated = [...teacherTimeSlots];
                                  updated[idx] = { ...updated[idx], start_time: e.target.value };
                                  setTeacherTimeSlots(updated);
                                }}
                                InputLabelProps={{ shrink: true }}
                                sx={{ width: 130 }}
                              />
                              <TextField
                                size="small"
                                label="Fin"
                                type="time"
                                value={slot.end_time}
                                onChange={(e) => {
                                  const updated = [...teacherTimeSlots];
                                  updated[idx] = { ...updated[idx], end_time: e.target.value };
                                  setTeacherTimeSlots(updated);
                                }}
                                InputLabelProps={{ shrink: true }}
                                sx={{ width: 130 }}
                              />
                              <TextField
                                size="small"
                                label="Max"
                                type="number"
                                value={slot.max_quizzes}
                                onChange={(e) => {
                                  const updated = [...teacherTimeSlots];
                                  updated[idx] = { ...updated[idx], max_quizzes: Math.max(1, parseInt(e.target.value) || 1) };
                                  setTeacherTimeSlots(updated);
                                }}
                                InputProps={{ inputProps: { min: 1, max: 20 } }}
                                sx={{ width: 70 }}
                              />
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setTeacherTimeSlots(prev => prev.filter((_, i) => i !== idx))}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ))}
                          <Box display="flex" gap={1} mt={1}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setTeacherTimeSlots(prev => [...prev, { label: 'Créneau', start_time: '08:00', end_time: '20:00', max_quizzes: 1, order: prev.length }])}
                            >
                              + Créneau
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={async () => {
                                if (!selectedStudent) return;
                                try {
                                  await quizRecommendationService.updateTeacherControl(selectedStudent.id, {
                                    controlled_by: 'teacher',
                                    time_slots: teacherTimeSlots,
                                  });
                                  toast.success('Créneaux mis à jour');
                                } catch { toast.error('Erreur'); }
                              }}
                            >
                              Enregistrer
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Les créneaux sont gérés par le parent.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Famille Tab */}
                {activeTab === 5 && (
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6">Membres de famille</Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        size="small"
                        onClick={() => openFamilyDialog()}
                      >
                        Nouveau membre
                      </Button>
                    </Box>

                    {loadingFamily ? (
                      <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                      </Box>
                    ) : (() => {
                      const studentParents = selectedStudent
                        ? familyMembers.filter(m => m.children?.includes(selectedStudent.id))
                        : familyMembers;
                      return studentParents.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                        Aucun membre de famille associé
                      </Typography>
                    ) : (
                      <Grid container spacing={2}>
                        {/* Family members cards */}
                        {studentParents.map((member) => {
                          const linkedStudentIds = member.children || [];
                          const isLinkedToSelected = selectedStudent ? linkedStudentIds.includes(selectedStudent.id) : false;
                          return (
                            <Grid item xs={12} sm={6} md={4} key={member.id}>
                              <Card
                                sx={{
                                  cursor: 'pointer',
                                  border: isLinkedToSelected ? '2px solid #1976d2' : pushParentIds.has(member.id) ? '3px solid #4caf50' : '1px solid #e0e0e0',
                                  bgcolor: isLinkedToSelected ? 'rgba(25,118,210,0.04)' : 'background.paper',
                                  '&:hover': { boxShadow: 3 },
                                }}
                                onClick={() => openFamilyDialog(member)}
                              >
                                <CardContent sx={{ pb: '12px !important' }}>
                                  <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                                    <Avatar sx={{ bgcolor: member.gender === 'F' ? '#e91e63' : '#1976d2', width: 40, height: 40 }}>
                                      {member.gender === 'F' ? 'F' : 'M'}
                                    </Avatar>
                                    <Box flex={1}>
                                      <Typography variant="subtitle2" fontWeight={600}>
                                        {member.first_name} {member.last_name}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {member.phone_number}
                                      </Typography>
                                      {isErwan && plaintextPasswords[`parent:${member.id}`] && (
                                        <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                                          mdp: {plaintextPasswords[`parent:${member.id}`]}
                                        </Typography>
                                      )}
                                    </Box>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={(e) => { e.stopPropagation(); handleDeleteFamilyMember(member.id); }}
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                  {/* Linked children */}
                                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                                    {member.children_details && member.children_details.length > 0 ? (
                                      member.children_details.map((child) => (
                                        <Chip
                                          key={child.id}
                                          label={child.first_name}
                                          size="small"
                                          color={selectedStudent && child.id === selectedStudent.id ? 'primary' : 'default'}
                                          variant={selectedStudent && child.id === selectedStudent.id ? 'filled' : 'outlined'}
                                          sx={{ fontSize: '0.7rem', height: 22, cursor: 'pointer' }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const student = students.find(s => s.id === child.id);
                                            if (student) handleSelectStudent(student);
                                          }}
                                        />
                                      ))
                                    ) : (
                                      <Typography variant="caption" color="text.secondary" fontStyle="italic">
                                        Aucun élève associé
                                      </Typography>
                                    )}
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          );
                        })}

                        {/* Visual connections section - only in global family view */}
                        {!selectedStudent && (
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle1" fontWeight={600} mb={2}>
                            Liens famille - élèves
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            {students.map((student) => {
                              const linkedParents = familyMembers.filter(m => m.children?.includes(student.id));
                              if (linkedParents.length === 0) return null;
                              return (
                                <Paper key={student.id} variant="outlined" sx={{ p: 1.5, minWidth: 200 }}>
                                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                                    <Avatar
                                      sx={{
                                        width: 28, height: 28, fontSize: '0.75rem',
                                        bgcolor: `#${student.color || '6ea8fe'}`,
                                      }}
                                    >
                                      {student.first_name.charAt(0)}
                                    </Avatar>
                                    <Typography variant="body2" fontWeight={600}>
                                      {student.first_name} {student.last_name || ''}
                                    </Typography>
                                  </Box>
                                  {linkedParents.map((p) => (
                                    <Box key={p.id} display="flex" alignItems="center" gap={0.5} ml={1}>
                                      <FamilyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                      <Typography variant="caption">
                                        {p.first_name} {p.last_name} ({p.phone_number})
                                      </Typography>
                                    </Box>
                                  ))}
                                </Paper>
                              );
                            })}
                          </Box>
                        </Grid>
                        )}
                      </Grid>
                    );
                    })()}
                  </Box>
                )}

                {/* Bac / Brevet : l'onglet de l'app élève, tel qu'il le voit */}
                {activeTab === 6 && <StudentBacPanel studentId={selectedStudent.id} />}

                {/* Sat : portefeuille (kips), barème, capacité, journal */}
                {activeTab === 7 && <StudentSatPanel studentId={selectedStudent.id} />}

              </Paper>
            </Box>
          ) : null}
        </Grid>
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedStudent ? 'Modifier l\'élève' : 'Nouvel élève'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Prénom"
            fullWidth
            variant="outlined"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Nom"
            fullWidth
            variant="outlined"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
          <FormControl fullWidth margin="dense" required>
            <InputLabel>Classe</InputLabel>
            <Select
              value={classGroup}
              onChange={(e) => setClassGroup(e.target.value as number)}
              label="Classe"
            >
              {classes.map((cls) => (
                <MenuItem key={cls.id} value={cls.id}>
                  {cls.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Numéro de téléphone"
            fullWidth
            variant="outlined"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
          <Box mt={2} display="flex" alignItems="center" gap={1}>
            <Autocomplete
              fullWidth
              size="small"
              options={familyMembers}
              value={familyMembers.find(p => p.id === selectedParentId) || null}
              onChange={(_, newValue) => {
                setSelectedParentId(newValue ? newValue.id : null);
                if (newValue) {
                  setParentGender(newValue.gender || '');
                  setParentPhone(newValue.phone_number || '');
                } else {
                  setParentGender('');
                  setParentPhone('');
                }
              }}
              getOptionLabel={(option) => {
                const civility = option.gender === 'F' ? 'Mme' : option.gender === 'M' ? 'M.' : '';
                return `${civility} ${option.first_name} ${option.last_name} (${option.phone_number})`.trim();
              }}
              renderInput={(params) => (
                <TextField {...params} label="Parent référent" margin="dense" variant="outlined" />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
            <Button
              variant="outlined"
              size="small"
              sx={{ mt: '4px', whiteSpace: 'nowrap' }}
              onClick={() => {
                setCreatingParentFromStudentDialog(true);
                openFamilyDialog();
              }}
            >
              + Créer
            </Button>
          </Box>
          <Box mt={2} mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              Couleur
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {AVAILABLE_COLORS.map((color) => (
                <Box
                  key={color}
                  onClick={() => setSelectedColor(color)}
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: `#${color}`,
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: selectedColor === color ? '3px solid #000' : '2px solid #ccc',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      boxShadow: 2,
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
          <TextField
            margin="dense"
            label="Tarif horaire (frs/h)"
            type="number"
            fullWidth
            variant="outlined"
            value={hourlyRate}
            onChange={(e) => setHourlyRate(e.target.value)}
          />
          <FormControlLabel
            control={<Checkbox checked={mathsSpecialite} onChange={(e) => setMathsSpecialite(e.target.checked)} />}
            label="Spécialité maths (Première : sujets de bac avec spécialité)"
          />
          {selectedStudent && (
            <Box mt={3}>
              <Typography variant="subtitle1" gutterBottom>
                Créneaux horaires
              </Typography>

              {timeSlots.length > 0 && (
                <Box mb={2}>
                  {timeSlots.map((slot) => (
                    <Box key={slot.id} display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">
                        {slot.day} : {slot.start_time} - {slot.end_time}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteTimeSlot(slot.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}

              <Box display="flex" gap={1} alignItems="center" mb={1} flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>Jour</InputLabel>
                  <Select
                    value={newSlotDay}
                    onChange={(e) => setNewSlotDay(e.target.value as any)}
                    label="Jour"
                  >
                    <MenuItem value="Lundi">Lundi</MenuItem>
                    <MenuItem value="Mardi">Mardi</MenuItem>
                    <MenuItem value="Mercredi">Mercredi</MenuItem>
                    <MenuItem value="Jeudi">Jeudi</MenuItem>
                    <MenuItem value="Vendredi">Vendredi</MenuItem>
                    <MenuItem value="Samedi">Samedi</MenuItem>
                    <MenuItem value="Dimanche">Dimanche</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 70 }}>
                  <InputLabel>H</InputLabel>
                  <Select
                    value={newSlotStartHour}
                    onChange={(e) => setNewSlotStartHour(e.target.value as number)}
                    label="H"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <MenuItem key={i} value={i}>{String(i).padStart(2, '0')}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 70 }}>
                  <InputLabel>Min</InputLabel>
                  <Select
                    value={newSlotStartMinute}
                    onChange={(e) => setNewSlotStartMinute(e.target.value as number)}
                    label="Min"
                  >
                    {Array.from({ length: 12 }, (_, i) => i * 5).map((min) => (
                      <MenuItem key={min} value={min}>{String(min).padStart(2, '0')}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Typography>à</Typography>

                <FormControl size="small" sx={{ minWidth: 70 }}>
                  <InputLabel>H</InputLabel>
                  <Select
                    value={newSlotEndHour}
                    onChange={(e) => setNewSlotEndHour(e.target.value as number)}
                    label="H"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <MenuItem key={i} value={i}>{String(i).padStart(2, '0')}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 70 }}>
                  <InputLabel>Min</InputLabel>
                  <Select
                    value={newSlotEndMinute}
                    onChange={(e) => setNewSlotEndMinute(e.target.value as number)}
                    label="Min"
                  >
                    {Array.from({ length: 12 }, (_, i) => i * 5).map((min) => (
                      <MenuItem key={min} value={min}>{String(min).padStart(2, '0')}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAddTimeSlot}
                  startIcon={<AddIcon />}
                >
                  Ajouter
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button variant="contained" onClick={handleSave}>
            {selectedStudent && dialogOpen ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Creation Dialog */}
      <Dialog
        open={invoiceDialogOpen}
        onClose={() => {
          setInvoiceDialogOpen(false);
          setStartTimePickerOpen(false);
          setEndTimePickerOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Nouvelle facture</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Date"
            type="date"
            fullWidth
            variant="outlined"
            value={invoiceDate}
            onChange={handleDateChange}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              onClick: (e: any) => {
                if (e.target.showPicker) {
                  e.target.showPicker();
                } else {
                  // Fallback for Firefox
                  e.target.focus();
                }
              },
              style: { cursor: 'pointer' }
            }}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
            <Box sx={{ mt: 1, mb: 1 }}>
              <MobileTimePicker
                label="Heure de début"
                value={invoiceStartTime}
                onChange={handleStartTimeChange}
                onAccept={handleStartTimeAccept}
                open={startTimePickerOpen}
                onOpen={() => setStartTimePickerOpen(true)}
                onClose={() => setStartTimePickerOpen(false)}
                ampm={false}
                minutesStep={5}
                openTo="hours"
                slotProps={{
                  textField: {
                    margin: 'dense',
                    fullWidth: true,
                    variant: 'outlined'
                  },
                  actionBar: {
                    actions: ['accept']
                  }
                }}
              />
            </Box>
            <Box sx={{ mt: 1, mb: 1 }}>
              <MobileTimePicker
                label="Heure de fin"
                value={invoiceEndTime}
                onChange={handleEndTimeChange}
                open={endTimePickerOpen}
                onOpen={() => setEndTimePickerOpen(true)}
                onClose={() => setEndTimePickerOpen(false)}
                ampm={false}
                minutesStep={5}
                openTo="hours"
                slotProps={{
                  textField: {
                    margin: 'dense',
                    fullWidth: true,
                    variant: 'outlined'
                  },
                  actionBar: {
                    actions: ['accept']
                  },
                  dialog: {
                    sx: {
                      '& .MuiDialogActions-root': {
                        flexDirection: 'column',
                        gap: 1,
                      }
                    }
                  }
                }}
                slots={{
                  actionBar: (props) => (
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            handleQuickDuration(1);
                            setEndTimePickerOpen(false);
                          }}
                          disabled={!invoiceStartTime}
                        >
                          +1h
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            handleQuickDuration(1, 30);
                            setEndTimePickerOpen(false);
                          }}
                          disabled={!invoiceStartTime}
                        >
                          +1h30
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            handleQuickDuration(2);
                            setEndTimePickerOpen(false);
                          }}
                          disabled={!invoiceStartTime}
                        >
                          +2h
                        </Button>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button onClick={() => props.onAccept()}>OK</Button>
                      </Box>
                    </Box>
                  )
                }}
              />
            </Box>
          </LocalizationProvider>
          {invoiceStartTime && invoiceEndTime && (
            <Box sx={{ mt: 2, mb: 1, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Amplitude horaire
              </Typography>
              <Typography variant="h6" color="primary">
                {formatDuration(invoiceStartTime, invoiceEndTime)}
              </Typography>
            </Box>
          )}
          <TextField
            margin="dense"
            label="Prix (frs)"
            type="number"
            fullWidth
            variant="outlined"
            value={invoicePrice}
            onChange={(e) => setInvoicePrice(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Réduction (frs) - optionnel"
            type="number"
            fullWidth
            variant="outlined"
            value={invoiceDiscount}
            onChange={(e) => setInvoiceDiscount(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Label de réduction - optionnel"
            fullWidth
            variant="outlined"
            value={invoiceDiscountLabel}
            onChange={(e) => setInvoiceDiscountLabel(e.target.value)}
            placeholder="ex: semaine de stage"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setInvoiceDialogOpen(false);
            setStartTimePickerOpen(false);
            setEndTimePickerOpen(false);
          }}>Annuler</Button>
          <Button variant="contained" onClick={handleCreateInvoice}>
            Créer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Edit Dialog */}
      <Dialog
        open={editInvoiceDialogOpen}
        onClose={() => {
          setEditInvoiceDialogOpen(false);
          setEditingInvoice(null);
          setStartTimePickerOpen(false);
          setEndTimePickerOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Modifier la facture</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Date"
            type="date"
            fullWidth
            variant="outlined"
            value={invoiceDate}
            onChange={handleDateChange}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              onClick: (e: any) => {
                if (e.target.showPicker) {
                  e.target.showPicker();
                } else {
                  // Fallback for Firefox
                  e.target.focus();
                }
              },
              style: { cursor: 'pointer' }
            }}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
            <Box sx={{ mt: 1, mb: 1 }}>
              <MobileTimePicker
                label="Heure de début"
                value={invoiceStartTime}
                onChange={handleStartTimeChange}
                onAccept={handleStartTimeAccept}
                open={startTimePickerOpen}
                onOpen={() => setStartTimePickerOpen(true)}
                onClose={() => setStartTimePickerOpen(false)}
                ampm={false}
                minutesStep={5}
                openTo="hours"
                slotProps={{
                  textField: {
                    margin: 'dense',
                    fullWidth: true,
                    variant: 'outlined'
                  },
                  actionBar: {
                    actions: ['accept']
                  }
                }}
              />
            </Box>
            <Box sx={{ mt: 1, mb: 1 }}>
              <MobileTimePicker
                label="Heure de fin"
                value={invoiceEndTime}
                onChange={handleEndTimeChange}
                open={endTimePickerOpen}
                onOpen={() => setEndTimePickerOpen(true)}
                onClose={() => setEndTimePickerOpen(false)}
                ampm={false}
                minutesStep={5}
                openTo="hours"
                slotProps={{
                  textField: {
                    margin: 'dense',
                    fullWidth: true,
                    variant: 'outlined'
                  },
                  actionBar: {
                    actions: ['accept']
                  },
                  dialog: {
                    sx: {
                      '& .MuiDialogActions-root': {
                        flexDirection: 'column',
                        gap: 1,
                      }
                    }
                  }
                }}
                slots={{
                  actionBar: (props) => (
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            handleQuickDuration(1);
                            setEndTimePickerOpen(false);
                          }}
                          disabled={!invoiceStartTime}
                        >
                          +1h
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            handleQuickDuration(1, 30);
                            setEndTimePickerOpen(false);
                          }}
                          disabled={!invoiceStartTime}
                        >
                          +1h30
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            handleQuickDuration(2);
                            setEndTimePickerOpen(false);
                          }}
                          disabled={!invoiceStartTime}
                        >
                          +2h
                        </Button>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button onClick={() => props.onAccept()}>OK</Button>
                      </Box>
                    </Box>
                  )
                }}
              />
            </Box>
          </LocalizationProvider>
          {invoiceStartTime && invoiceEndTime && (
            <Box sx={{ mt: 2, mb: 1, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Amplitude horaire
              </Typography>
              <Typography variant="h6" color="primary">
                {formatDuration(invoiceStartTime, invoiceEndTime)}
              </Typography>
            </Box>
          )}
          <TextField
            margin="dense"
            label="Prix (frs)"
            type="number"
            fullWidth
            variant="outlined"
            value={invoicePrice}
            onChange={(e) => setInvoicePrice(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Réduction (frs) - optionnel"
            type="number"
            fullWidth
            variant="outlined"
            value={invoiceDiscount}
            onChange={(e) => setInvoiceDiscount(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Label de réduction - optionnel"
            fullWidth
            variant="outlined"
            value={invoiceDiscountLabel}
            onChange={(e) => setInvoiceDiscountLabel(e.target.value)}
            placeholder="ex: semaine de stage"
          />

          {editingInvoice?.status === 'paid' && (
            <FormControl fullWidth margin="dense">
              <InputLabel>Mode de règlement</InputLabel>
              <Select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value as 'cheque' | 'cash' | 'transfer' | 'card' | 'nature')}
                label="Mode de règlement"
              >
                <MenuItem value="cheque">Chèque</MenuItem>
                <MenuItem value="cash">Espèces</MenuItem>
                <MenuItem value="transfer">Virement</MenuItem>
                <MenuItem value="card">Carte</MenuItem>
                <MenuItem value="nature">Nature</MenuItem>
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
          <Box>
            {editingInvoice?.status === 'paid' && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<PdfIcon />}
                  onClick={() => editingInvoice && handleGenerateInvoicePdf(editingInvoice.id)}
                  color="primary"
                >
                  Générer PDF
                </Button>
                {editingInvoice?.pdf_file_url && (
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => editingInvoice && handleDownloadInvoicePdf(editingInvoice.id)}
                    color="success"
                  >
                    Télécharger PDF
                  </Button>
                )}
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => {
              setEditInvoiceDialogOpen(false);
              setEditingInvoice(null);
              setStartTimePickerOpen(false);
              setEndTimePickerOpen(false);
            }}>Annuler</Button>
            <Button variant="contained" onClick={handleUpdateInvoice}>
              Modifier
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={() => {
          setPaymentDialogOpen(false);
          setInvoiceToMarkPaid(null);
          setInvoicesToMarkPaid([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {invoicesToMarkPaid.length > 1
            ? `Règlement de ${invoicesToMarkPaid.length} factures`
            : 'Règlement de la facture'}
        </DialogTitle>
        <DialogContent>
          {/* Résumé des factures à régler */}
          {(() => {
            const invoiceIds = invoicesToMarkPaid.length > 0 ? invoicesToMarkPaid : (invoiceToMarkPaid ? [invoiceToMarkPaid] : []);
            const selectedInvoiceObjects = invoices.filter(inv => invoiceIds.includes(inv.id));
            const totalPrice = selectedInvoiceObjects.reduce((sum, inv) => sum + (Number(inv.price) || 0), 0);
            const totalExistingDiscount = selectedInvoiceObjects.reduce((sum, inv) => sum + (Number(inv.discount) || 0), 0);

            return (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                {selectedInvoiceObjects.map((invoice) => (
                  <Box key={invoice.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">
                      {formatInvoiceDate(invoice.date)} ({formatTime(invoice.start_time)} - {formatTime(invoice.end_time)})
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {formatPrice(Number(invoice.price) || 0)} frs
                    </Typography>
                  </Box>
                ))}
                <Box sx={{ borderTop: '1px solid', borderColor: 'grey.300', mt: 1, pt: 1, display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1" fontWeight="bold">
                    Total
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary.main">
                    {formatPrice(totalPrice)} frs
                  </Typography>
                </Box>
                {totalExistingDiscount > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    (Réductions existantes: -{formatPrice(totalExistingDiscount)} frs)
                  </Typography>
                )}
              </Box>
            );
          })()}

          <FormControl fullWidth margin="dense">
            <InputLabel>Mode de règlement</InputLabel>
            <Select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value as 'cheque' | 'cash' | 'transfer' | 'card' | 'nature')}
              label="Mode de règlement"
            >
              <MenuItem value="cheque">Chèque</MenuItem>
              <MenuItem value="cash">Espèces</MenuItem>
              <MenuItem value="transfer">Virement</MenuItem>
              <MenuItem value="card">Carte</MenuItem>
              <MenuItem value="nature">Nature</MenuItem>
            </Select>
          </FormControl>

          <TextField
            margin="dense"
            label="Réduction supplémentaire (frs) - optionnel"
            type="number"
            fullWidth
            variant="outlined"
            value={paymentDiscount}
            onChange={(e) => setPaymentDiscount(e.target.value)}
          />

          <TextField
            margin="dense"
            label="Label de réduction - optionnel"
            fullWidth
            variant="outlined"
            value={paymentDiscountLabel}
            onChange={(e) => setPaymentDiscountLabel(e.target.value)}
            placeholder="ex: remise fidélité"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setPaymentDialogOpen(false);
            setInvoiceToMarkPaid(null);
            setInvoicesToMarkPaid([]);
          }}>Annuler</Button>
          <Button variant="contained" onClick={handleConfirmPayment}>
            Confirmer le règlement
          </Button>
        </DialogActions>
      </Dialog>

      {/* Container Customization Dialog */}
      <Dialog
        open={customizeDialogOpen}
        onClose={() => setCustomizeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Personnaliser le groupe: {customizingContainer?.title}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Sélectionnez les questions à inclure dans ce groupe
          </Typography>
          <Box mt={2}>
            {containerQuizzes.map((quiz) => (
              <FormControlLabel
                key={quiz.id}
                control={
                  <Checkbox
                    checked={!excludedQuizIds.includes(quiz.id)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      if (e.target.checked) {
                        // Include quiz (remove from excluded list)
                        setExcludedQuizIds(prev => prev.filter(id => id !== quiz.id));
                      } else {
                        // Exclude quiz (add to excluded list)
                        setExcludedQuizIds(prev => [...prev, quiz.id]);
                      }
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">
                      {quiz.question.substring(0, 60)}
                      {quiz.question.length > 60 && '...'}
                    </Typography>
                    {quiz.chapitre && (
                      <Typography variant="caption" color="text.secondary">
                        {quiz.chapitre}
                      </Typography>
                    )}
                  </Box>
                }
                sx={{ display: 'block', mb: 1 }}
              />
            ))}
          </Box>
          {containerQuizzes.length === 0 && (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
              Aucune question dans ce groupe
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomizeDialogOpen(false)}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmCustomization}
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Grade Assignment Dialog */}
      <Dialog
        open={gradeDialogOpen}
        onClose={() => {
          setGradeDialogOpen(false);
          setSelectedAssignment(null);
          setTempGrade(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedAssignment ? `Noter: ${selectedAssignment.objective_title}` : 'Noter l\'objectif'}
        </DialogTitle>
        <DialogContent>
          {selectedAssignment && (
            <Box sx={{ py: 2 }}>
              {selectedAssignment.objective_description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {selectedAssignment.objective_description}
                </Typography>
              )}
              <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                <Typography variant="body1" fontWeight="bold">
                  Note de l'élève
                </Typography>
                <Rating
                  size="large"
                  value={tempGrade}
                  onChange={(event, newValue) => setTempGrade(newValue)}
                />
                <Typography variant="h6" color="primary">
                  {tempGrade || 0}/5
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setGradeDialogOpen(false);
              setSelectedAssignment(null);
              setTempGrade(null);
            }}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (selectedAssignment) {
                try {
                  await objectiveAssignmentService.updateGrade(selectedAssignment.id, tempGrade);
                  toast.success('Note mise à jour');
                  if (selectedStudent) {
                    loadObjectiveAssignments(selectedStudent.id);
                  }
                  setGradeDialogOpen(false);
                  setSelectedAssignment(null);
                  setTempGrade(null);
                } catch (error) {
                  console.error('Error updating grade:', error);
                  toast.error('Erreur lors de la mise à jour de la note');
                }
              }
            }}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onClose={handleClosePasswordDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Modifier le mot de passe</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nouveau mot de passe"
            type="password"
            fullWidth
            variant="outlined"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          <TextField
            margin="dense"
            label="Confirmer le mot de passe"
            type="password"
            fullWidth
            variant="outlined"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleSavePassword}
            disabled={!newPassword || !confirmPassword}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>


      {/* Question History Modal */}
      <QuestionHistoryModal
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        questionText={selectedQuestionForHistory?.quiz_question || ''}
        history={questionHistory}
      />

      {/* Calendar Dialog */}
      <Dialog
        open={calendarDialogOpen}
        onClose={() => { setCalendarDialogOpen(false); if (calendarTeacherId !== null) { setCalendarTeacherId(null); loadAllTimeSlots(); } }}
        maxWidth={false}
        fullWidth
        sx={{ '& .MuiDialog-paper': { maxWidth: 1400, height: '90vh' } }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton onClick={() => setCalendarWeekOffset(prev => prev - 1)} size="small">
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="h6" sx={{ minWidth: 220, textAlign: 'center' }}>
                {(() => {
                  const { monday, sunday } = getNCWeekBounds(calendarWeekOffset);
                  const fmtDate = (d: string) => { const [, m, day] = d.split('-'); return `${day}/${m}`; };
                  const label = calendarWeekOffset === 0 ? 'Cette semaine' : calendarWeekOffset === 1 ? 'Semaine prochaine' : `Semaine +${calendarWeekOffset}`;
                  return `${label} (${fmtDate(monday)} – ${fmtDate(sunday)})`;
                })()}
              </Typography>
              <IconButton onClick={() => setCalendarWeekOffset(prev => prev + 1)} size="small">
                <ChevronRightIcon />
              </IconButton>
              {calendarWeekOffset !== 0 && (
                <Button size="small" variant="text" onClick={() => setCalendarWeekOffset(0)} sx={{ ml: 0.5, fontSize: '0.7rem', minWidth: 'auto' }}>
                  Aujourd'hui
                </Button>
              )}
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              {calendarTeachers.length > 0 && (
                <ToggleButtonGroup
                  value={calendarTeacherId === null ? 'me' : String(calendarTeacherId)}
                  exclusive
                  onChange={(_, v) => {
                    if (!v) return;
                    const tid = v === 'me' ? null : Number(v);
                    setCalendarTeacherId(tid);
                    loadAllTimeSlots(tid);
                  }}
                  size="small"
                >
                  <ToggleButton value="me">
                    <Typography variant="caption">Moi</Typography>
                  </ToggleButton>
                  {calendarTeachers.map(t => (
                    <ToggleButton key={t.id} value={String(t.id)}>
                      <Typography variant="caption">{t.name}</Typography>
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              )}
              <ToggleButtonGroup
                value={calendarView}
                exclusive
                onChange={(_, v) => v && setCalendarView(v)}
                size="small"
              >
                <ToggleButton value="detail">
                  <Tooltip title="Détail par élève"><ViewListIcon fontSize="small" /></Tooltip>
                </ToggleButton>
                <ToggleButton value="count">
                  <Tooltip title="Nombre d'élèves"><GroupsIcon fontSize="small" /></Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
              <IconButton onClick={() => setCalendarDialogOpen(false)}>
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {/* Left Panel - Punctual slots */}
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 1.5, height: '100%' }} elevation={2}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Cours ponctuels
                </Typography>

                {/* Student selector */}
                <Autocomplete
                  size="small"
                  options={students}
                  getOptionLabel={(s) => `${s.first_name} ${s.last_name || ''}`.trim()}
                  value={students.find(s => s.id === punctualStudent) || null}
                  onChange={(_, v) => setPunctualStudent(v ? v.id : '')}
                  renderInput={(params) => <TextField {...params} label="Élève" />}
                  filterOptions={(options, { inputValue }) => {
                    const q = inputValue.toLowerCase();
                    return options.filter(s => `${s.first_name} ${s.last_name || ''}`.toLowerCase().includes(q));
                  }}
                  sx={{ mb: 1 }}
                />

                {/* Date mode toggle */}
                <ToggleButtonGroup
                  value={punctualDateMode}
                  exclusive
                  onChange={(_, v) => v && setPunctualDateMode(v)}
                  size="small"
                  fullWidth
                  sx={{ mb: 1 }}
                >
                  <ToggleButton value="offset" sx={{ fontSize: '0.65rem', py: 0.3 }}>Jour + semaine</ToggleButton>
                  <ToggleButton value="picker" sx={{ fontSize: '0.65rem', py: 0.3 }}>Calendrier</ToggleButton>
                </ToggleButtonGroup>

                {punctualDateMode === 'offset' ? (
                  <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel>Jour</InputLabel>
                      <Select value={punctualDay} onChange={(e) => setPunctualDay(e.target.value)} label="Jour">
                        {DAYS_ORDER.map(d => <MenuItem key={d} value={d}>{d.substring(0, 3)}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel>Sem.</InputLabel>
                      <Select value={punctualWeeksOffset} onChange={(e) => setPunctualWeeksOffset(e.target.value as number)} label="Sem.">
                        <MenuItem value={0}>Cette sem.</MenuItem>
                        <MenuItem value={1}>+1 sem.</MenuItem>
                        <MenuItem value={2}>+2 sem.</MenuItem>
                        <MenuItem value={3}>+3 sem.</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                ) : (
                  <TextField type="date" size="small" fullWidth value={punctualDate} onChange={(e) => setPunctualDate(e.target.value)} sx={{ mb: 1 }} />
                )}

                {/* Time selectors */}
                <Box sx={{ display: 'flex', gap: 0.5, mb: 1, alignItems: 'center' }}>
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel>Deb H</InputLabel>
                    <Select value={punctualStartHour} onChange={(e) => setPunctualStartHour(e.target.value as number)} label="Deb H">
                      {Array.from({ length: 24 }, (_, i) => <MenuItem key={i} value={i}>{String(i).padStart(2, '0')}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel>Min</InputLabel>
                    <Select value={punctualStartMinute} onChange={(e) => setPunctualStartMinute(e.target.value as number)} label="Min">
                      {Array.from({ length: 12 }, (_, i) => i * 5).map(m => <MenuItem key={m} value={m}>{String(m).padStart(2, '0')}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" sx={{ mx: 0.3 }}>→</Typography>
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel>Fin H</InputLabel>
                    <Select value={punctualEndHour} onChange={(e) => setPunctualEndHour(e.target.value as number)} label="Fin H">
                      {Array.from({ length: 24 }, (_, i) => <MenuItem key={i} value={i}>{String(i).padStart(2, '0')}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel>Min</InputLabel>
                    <Select value={punctualEndMinute} onChange={(e) => setPunctualEndMinute(e.target.value as number)} label="Min">
                      {Array.from({ length: 12 }, (_, i) => i * 5).map(m => <MenuItem key={m} value={m}>{String(m).padStart(2, '0')}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Box>

                <Button variant="contained" size="small" fullWidth onClick={handleAddPunctualSlot} disabled={!punctualStudent} startIcon={<AddIcon />}>
                  Ajouter
                </Button>

                <Divider sx={{ my: 1.5 }} />

                {/* Recap list */}
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                  Cette semaine ({punctualTimeSlots.length})
                </Typography>
                <Box sx={{ maxHeight: 250, overflow: 'auto', mt: 0.5 }}>
                  {punctualTimeSlots.length === 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 1 }}>
                      Aucun cours ponctuel
                    </Typography>
                  ) : punctualTimeSlots.map(slot => {
                    const student = students.find(s => s.id === slot.student);
                    const rate = student?.hourly_rate || 3000;
                    const [sh, sm] = slot.start_time.split(':').map(Number);
                    const [eh, em] = slot.end_time.split(':').map(Number);
                    const durationMin = (eh * 60 + em) - (sh * 60 + sm);
                    const amount = Math.round((durationMin / 60) * rate);
                    const bgColor = student?.color ? `#${student.color}` : '#6ea8fe';
                    return (
                      <Box key={slot.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: bgColor, flexShrink: 0 }} />
                            <Typography variant="caption" fontWeight="bold" noWrap>
                              {student?.first_name || '?'}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                            {dateToDayName(slot.date).substring(0, 3)} {slot.date.substring(5)} {slot.start_time.substring(0, 5)}-{slot.end_time.substring(0, 5)} ({formatMinutes(durationMin)}) · {amount.toLocaleString()} frs
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => handleDeletePunctualSlot(slot.id)} sx={{ ml: 0.5 }}>
                          <DeleteOutlineIcon sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Box>

                {/* Punctual total */}
                {punctualTimeSlots.length > 0 && (
                  <Box sx={{ mt: 1, p: 1, bgcolor: 'warning.light', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="warning.contrastText">Total ponctuels</Typography>
                    <Typography variant="subtitle1" fontWeight="bold" color="warning.contrastText">
                      {getPunctualAllTotal().toLocaleString()} frs
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Calendar Grid - Center */}
            <Grid item xs={12} md={6}>
              {(() => {
                const { monday: cwMonday, sunday: cwSunday } = getNCWeekBounds(calendarWeekOffset);
                const viewedWeekPunctual = punctualTimeSlots.filter(
                  slot => slot.date >= cwMonday && slot.date <= cwSunday
                );
                const isCurrentWeek = calendarWeekOffset === 0;
                const todayName = isCurrentWeek ? getNCDayName() : '';

                // Collect all time boundaries for hour range
                const allTimes: { start: string; end: string }[] = [
                  ...allTimeSlots.map(s => ({ start: s.start_time.substring(0, 5), end: s.end_time.substring(0, 5) })),
                  ...viewedWeekPunctual.map(s => ({ start: s.start_time.substring(0, 5), end: s.end_time.substring(0, 5) })),
                ];
                if (allTimes.length === 0) return <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>Aucun créneau</Typography>;

                const minHour = Math.min(...allTimes.map(t => parseInt(t.start.substring(0, 2))));
                const maxHour = Math.max(...allTimes.map(t => {
                  const h = parseInt(t.end.substring(0, 2));
                  const m = parseInt(t.end.substring(3, 5));
                  return m > 0 ? h + 1 : h;
                }));
                const HOUR_H = 80;
                const totalH = (maxHour - minHour) * HOUR_H;
                const hours = Array.from({ length: maxHour - minHour + 1 }, (_, i) => minHour + i);
                const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };

                // ---- DETAIL VIEW: visual timeline ----
                if (calendarView === 'detail') {
                  return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(90vh - 200px)' }}>
                      {/* Day headers */}
                      <Box sx={{ display: 'flex' }}>
                        <Box sx={{ width: 42, flexShrink: 0 }} />
                        {DAYS_ORDER.map(day => {
                          const isToday = todayName === day;
                          return (
                            <Box key={day} sx={{ flex: 1, textAlign: 'center', py: 0.5, fontWeight: 'bold', fontSize: '0.7rem', bgcolor: isToday ? '#1976d2' : '#f5f5f5', color: isToday ? '#fff' : '#333', borderBottom: '2px solid #ddd' }}>
                              {day.substring(0, 3)}
                            </Box>
                          );
                        })}
                      </Box>

                      {/* Timeline grid */}
                      <Box sx={{ display: 'flex', overflowY: 'auto', flex: 1 }}>
                        {/* Hour labels */}
                        <Box sx={{ width: 42, flexShrink: 0, position: 'relative', height: totalH }}>
                          {hours.map(h => (
                            <Box key={h} sx={{ position: 'absolute', top: (h - minHour) * HOUR_H, width: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
                              <Typography sx={{ fontSize: '0.6rem', fontWeight: 'bold', color: '#555', mt: '-0.4em' }}>{String(h).padStart(2, '0')}h</Typography>
                            </Box>
                          ))}
                        </Box>

                        {/* Day columns */}
                        {DAYS_ORDER.map(day => {
                          const isToday = todayName === day;
                          // Collect all slots for this day, grouped by (start, end)
                          type SlotEntry = { studentId: number; isPunctual: boolean };
                          const groupMap = new Map<string, { start: string; end: string; entries: SlotEntry[] }>();
                          allTimeSlots.filter(s => s.day === day).forEach(s => {
                            const key = `${s.start_time.substring(0, 5)}-${s.end_time.substring(0, 5)}`;
                            if (!groupMap.has(key)) groupMap.set(key, { start: s.start_time.substring(0, 5), end: s.end_time.substring(0, 5), entries: [] });
                            groupMap.get(key)!.entries.push({ studentId: s.student, isPunctual: false });
                          });
                          viewedWeekPunctual.filter(s => dateToDayName(s.date) === day).forEach(s => {
                            const key = `${s.start_time.substring(0, 5)}-${s.end_time.substring(0, 5)}`;
                            if (!groupMap.has(key)) groupMap.set(key, { start: s.start_time.substring(0, 5), end: s.end_time.substring(0, 5), entries: [] });
                            groupMap.get(key)!.entries.push({ studentId: s.student, isPunctual: true });
                          });
                          const groups = Array.from(groupMap.values()).sort((a, b) => a.start.localeCompare(b.start));

                          // Assign columns: groups that overlap get different columns
                          const assigned: { group: typeof groups[0]; col: number }[] = [];
                          let maxCol = 0;
                          groups.forEach(g => {
                            let col = 0;
                            const gStart = toMin(g.start);
                            const gEnd = toMin(g.end);
                            while (assigned.some(a => a.col === col && toMin(a.group.start) < gEnd && toMin(a.group.end) > gStart)) {
                              col++;
                            }
                            assigned.push({ group: g, col });
                            maxCol = Math.max(maxCol, col);
                          });
                          const numCols = groups.length > 0 ? maxCol + 1 : 1;

                          return (
                            <Box key={day} sx={{ flex: 1, position: 'relative', height: totalH, borderLeft: isToday ? '2px solid #1976d2' : '1px solid #eee', bgcolor: isToday ? 'rgba(25,118,210,0.04)' : undefined }}>
                              {/* Hour grid lines */}
                              {hours.map(h => (
                                <Box key={h} sx={{ position: 'absolute', top: (h - minHour) * HOUR_H, left: 0, right: 0, borderTop: '1px solid #eee' }} />
                              ))}
                              {/* Slot blocks */}
                              {assigned.map(({ group: g, col }) => {
                                const topPx = (toMin(g.start) - minHour * 60) / 60 * HOUR_H;
                                const heightPx = (toMin(g.end) - toMin(g.start)) / 60 * HOUR_H;
                                return (
                                  <Box key={`${g.start}-${g.end}`} sx={{ position: 'absolute', top: topPx + 3, height: heightPx - 6, left: `calc(${(col / numCols) * 100}% + 2px)`, width: `calc(${(1 / numCols) * 100}% - 4px)`, boxSizing: 'border-box' }}>
                                    <Box sx={{ height: '100%', borderRadius: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', border: '2.5px solid rgba(0,0,0,0.4)', boxShadow: '0 2px 6px rgba(0,0,0,0.25)' }}>
                                      {g.entries.map((entry, i) => {
                                        const student = students.find(s => s.id === entry.studentId);
                                        const bgColor = student?.color ? `#${student.color}` : '#6ea8fe';
                                        return (
                                          <Box key={i} sx={{ flex: 1, bgcolor: bgColor, color: '#fff', px: 0.5, display: 'flex', alignItems: 'center', fontSize: '0.6rem', fontWeight: 'bold', textShadow: '0 1px 2px rgba(0,0,0,0.3)', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', borderBottom: i < g.entries.length - 1 ? '2px solid rgba(0,0,0,0.2)' : 'none', ...(entry.isPunctual ? { borderRight: '2px dashed rgba(255,255,255,0.6)', borderLeft: '2px dashed rgba(255,255,255,0.6)' } : {}) }}>
                                            {student?.first_name || '?'}{entry.isPunctual ? ' (P)' : ''}
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          );
                        })}
                      </Box>

                      {/* Totals row */}
                      <Box sx={{ display: 'flex', borderTop: '2px solid #ddd' }}>
                        <Box sx={{ width: 42, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 'bold', color: '#555' }}>Total</Typography>
                        </Box>
                        {DAYS_ORDER.map(day => {
                          const isToday = todayName === day;
                          const amount = getDayAmount(day) + getPunctualDayAmount(day, calendarWeekOffset);
                          return (
                            <Box key={day} sx={{ flex: 1, textAlign: 'center', py: 0.5, bgcolor: isToday ? 'rgba(25,118,210,0.08)' : '#f5f5f5' }}>
                              <Typography sx={{ fontSize: '0.7rem', fontWeight: 'bold', color: amount > 0 ? '#1976d2' : '#999' }}>
                                {amount > 0 ? `${amount.toLocaleString()} frs` : '-'}
                              </Typography>
                              {amount > 0 && <Typography sx={{ fontSize: '0.6rem', color: '#888' }}>{formatMinutes(getDayMinutes(day))}</Typography>}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                }

                // ---- COUNT VIEW: split intervals table ----
                const timePointsSet = new Set<string>();
                allTimeSlots.forEach(slot => { timePointsSet.add(slot.start_time.substring(0, 5)); timePointsSet.add(slot.end_time.substring(0, 5)); });
                viewedWeekPunctual.forEach(slot => { timePointsSet.add(slot.start_time.substring(0, 5)); timePointsSet.add(slot.end_time.substring(0, 5)); });
                const timePoints = Array.from(timePointsSet).sort();
                const timeRanges: { start: string; end: string }[] = [];
                for (let i = 0; i < timePoints.length - 1; i++) timeRanges.push({ start: timePoints[i], end: timePoints[i + 1] });
                const slotCovers = (ss: string, se: string, rs: string, re: string) => ss.substring(0, 5) <= rs && se.substring(0, 5) >= re;

                return (
                  <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                      <thead>
                        <tr>
                          <th style={{ width: 55, padding: '6px 2px', textAlign: 'center', borderBottom: '2px solid #ddd', fontSize: '0.7rem', color: '#666' }}>Horaire</th>
                          {DAYS_ORDER.map(day => {
                            const isToday = todayName === day;
                            return (<th key={day} style={{ padding: '6px 2px', textAlign: 'center', borderBottom: '2px solid #ddd', backgroundColor: isToday ? '#1976d2' : '#f5f5f5', color: isToday ? '#fff' : '#333', fontWeight: 'bold', fontSize: '0.7rem' }}>{day.substring(0, 3)}</th>);
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {timeRanges.map(range => {
                          const hasAny = DAYS_ORDER.some(day =>
                            allTimeSlots.some(s => s.day === day && slotCovers(s.start_time, s.end_time, range.start, range.end))
                            || viewedWeekPunctual.some(s => dateToDayName(s.date) === day && slotCovers(s.start_time, s.end_time, range.start, range.end))
                          );
                          if (!hasAny) return null;
                          const isRoundHourStart = range.start.endsWith(':00');
                          const topBorder = isRoundHourStart ? '2px solid #999' : '1px solid #eee';
                          return (
                            <tr key={`${range.start}-${range.end}`}>
                              <td style={{ padding: '3px 2px', textAlign: 'center', borderTop: topBorder, borderBottom: '1px solid #eee', fontSize: '0.65rem', fontWeight: isRoundHourStart ? 'bold' : 'normal', color: isRoundHourStart ? '#333' : '#888', verticalAlign: 'middle' }}>{range.start}<br />{range.end}</td>
                              {DAYS_ORDER.map(day => {
                                const isToday = todayName === day;
                                const cellSlots = allTimeSlots.filter(s => s.day === day && slotCovers(s.start_time, s.end_time, range.start, range.end));
                                const cellPunctual = viewedWeekPunctual.filter(s => dateToDayName(s.date) === day && slotCovers(s.start_time, s.end_time, range.start, range.end));
                                const allIds = [...cellSlots.map(s => s.student), ...cellPunctual.map(s => s.student)];
                                const count = allIds.length;
                                const cellStudents = allIds.map(sid => students.find(s => s.id === sid)).filter(Boolean) as Student[];
                                const tooltipNames = cellStudents.map(s => `${s.first_name}${s.last_name ? ' ' + s.last_name : ''}`).join(', ');
                                const levelCounts = new Map<string, number>();
                                cellStudents.forEach(s => { const nm = getClassName(s.class_group) || 'Autre'; levelCounts.set(nm, (levelCounts.get(nm) || 0) + 1); });
                                const levelLabels = Array.from(levelCounts.entries()).map(([n, c]) => `${n} (${c})`).join(', ');
                                return (
                                  <td key={day} style={{ padding: '3px', borderTop: topBorder, borderBottom: '1px solid #eee', borderLeft: isToday ? '2px solid #1976d2' : '1px solid #eee', borderRight: isToday ? '2px solid #1976d2' : '1px solid #eee', verticalAlign: 'middle', textAlign: 'center', backgroundColor: isToday ? 'rgba(25,118,210,0.04)' : undefined }}>
                                    {count > 0 ? (
                                      <Tooltip title={tooltipNames} arrow placement="top">
                                        <Box>
                                          <Typography variant="h6" fontWeight="bold" color="primary" sx={{ lineHeight: 1.2 }}>{count}</Typography>
                                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.55rem', lineHeight: 1.2, display: 'block' }}>{levelLabels}</Typography>
                                        </Box>
                                      </Tooltip>
                                    ) : null}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                        <tr>
                          <td style={{ padding: '6px 2px', textAlign: 'center', borderTop: '2px solid #ddd', fontWeight: 'bold', fontSize: '0.65rem', color: '#555' }}>Total</td>
                          {DAYS_ORDER.map(day => {
                            const isToday = todayName === day;
                            const amount = getDayAmount(day) + getPunctualDayAmount(day, calendarWeekOffset);
                            return (
                              <td key={day} style={{ padding: '6px 2px', textAlign: 'center', borderTop: '2px solid #ddd', fontWeight: 'bold', fontSize: '0.7rem', backgroundColor: isToday ? 'rgba(25,118,210,0.08)' : '#f5f5f5', color: amount > 0 ? '#1976d2' : '#999' }}>
                                {amount > 0 ? `${amount.toLocaleString()} frs` : '-'}
                                {amount > 0 && <div style={{ fontSize: '0.6rem', color: '#888', fontWeight: 'normal' }}>{formatMinutes(getDayMinutes(day))}</div>}
                              </td>
                            );
                          })}
                        </tr>
                      </tbody>
                    </table>
                  </Box>
                );
              })()}
            </Grid>

            {/* Summary Panel - Right */}
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, height: '100%' }} elevation={2}>
                {/* NC Clock */}
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Heure Nouvelle-Calédonie
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                    {(() => {
                      const nc = new Date(ncTime.toLocaleString('en-US', { timeZone: 'Pacific/Noumea' }));
                      return `${String(nc.getHours()).padStart(2, '0')}:${String(nc.getMinutes()).padStart(2, '0')}:${String(nc.getSeconds()).padStart(2, '0')}`;
                    })()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(() => {
                      const nc = new Date(ncTime.toLocaleString('en-US', { timeZone: 'Pacific/Noumea' }));
                      const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
                      return days[nc.getDay()];
                    })()}
                  </Typography>
                </Box>

                {/* Week Total (recurring) */}
                <Box sx={{ textAlign: 'center', mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Total récurrents
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    {getWeekTotal().toLocaleString()} frs
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatMinutes(getWeekMinutes())} de cours
                  </Typography>
                </Box>

                {/* Punctual total for viewed week */}
                {getPunctualWeekTotal(calendarWeekOffset) > 0 && (
                  <Box sx={{ textAlign: 'center', mb: 2, p: 1.5, bgcolor: 'warning.light', borderRadius: 2 }}>
                    <Typography variant="caption" color="warning.contrastText">
                      Ponctuels cette semaine
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="warning.contrastText">
                      {getPunctualWeekTotal(calendarWeekOffset).toLocaleString()} frs
                    </Typography>
                  </Box>
                )}

                {/* Grand total */}
                <Box sx={{ textAlign: 'center', mb: 2, p: 1.5, bgcolor: 'primary.main', borderRadius: 2, color: 'white' }}>
                  <Typography variant="caption">
                    Total semaine (tout inclus)
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {(getWeekTotal() + getPunctualWeekTotal(calendarWeekOffset)).toLocaleString()} frs
                  </Typography>
                </Box>

                {/* Month estimate */}
                <Box sx={{ textAlign: 'center', mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Estimation mensuelle
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    {(getWeekTotal() * 4).toLocaleString()} frs
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatMinutes(getWeekMinutes() * 4)} de cours
                  </Typography>
                </Box>

                {/* Real-time earnings - only on current week */}
                {calendarWeekOffset === 0 && (
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'success.light', borderRadius: 2, color: 'success.contrastText' }}>
                    <Typography variant="caption">
                      Gains aujourd'hui
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {getCurrentEarnings().toLocaleString()} frs
                    </Typography>
                    <Typography variant="caption">
                      / {getDayAmount(getNCDayName()).toLocaleString()} frs prévus
                    </Typography>
                  </Box>
                )}

                {/* Student count by class level */}
                {(() => {
                  const uniqueStudentIds = new Set<number>();
                  allTimeSlots.forEach(s => uniqueStudentIds.add(s.student));
                  punctualTimeSlots.forEach(s => uniqueStudentIds.add(s.student));
                  const levelCounts = new Map<string, number>();
                  uniqueStudentIds.forEach(sid => {
                    const student = students.find(s => s.id === sid);
                    if (student) {
                      const name = getClassName(student.class_group) || 'Autre';
                      levelCounts.set(name, (levelCounts.get(name) || 0) + 1);
                    }
                  });
                  const sorted = Array.from(levelCounts.entries()).sort((a, b) => b[1] - a[1]);
                  return (
                    <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
                        Élèves par niveau ({uniqueStudentIds.size})
                      </Typography>
                      {sorted.map(([name, count]) => (
                        <Box key={name} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                          <Typography variant="caption" color="text.secondary">{name}</Typography>
                          <Typography variant="caption" fontWeight="bold" color="text.primary">{count}</Typography>
                        </Box>
                      ))}
                    </Box>
                  );
                })()}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Quiz Timeline Dialog */}
      <Dialog
        open={quizTimelineOpen}
        onClose={() => setQuizTimelineOpen(false)}
        maxWidth={false}
        fullWidth
        sx={{ '& .MuiDialog-paper': { maxWidth: 1400, height: '90vh' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton onClick={() => setQuizTimelineOffset(prev => prev - 1)} size="small">
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="h6" sx={{ minWidth: 180, textAlign: 'center' }}>
                {getPeriodLabel(quizTimelinePeriod, quizTimelineOffset)}
              </Typography>
              <IconButton
                onClick={() => setQuizTimelineOffset(prev => prev + 1)}
                size="small"
                disabled={quizTimelineOffset >= 0}
              >
                <ChevronRightIcon />
              </IconButton>
              {quizTimelineOffset !== 0 && (
                <Button size="small" variant="text" onClick={() => setQuizTimelineOffset(0)} sx={{ ml: 0.5, fontSize: '0.7rem', minWidth: 'auto' }}>
                  Aujourd'hui
                </Button>
              )}
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <ToggleButtonGroup
                value={quizTimelinePeriod}
                exclusive
                onChange={(_, v) => { if (v) { setQuizTimelinePeriod(v); setQuizTimelineOffset(0); } }}
                size="small"
              >
                <ToggleButton value="month"><Typography variant="caption">1M</Typography></ToggleButton>
                <ToggleButton value="week"><Typography variant="caption">1S</Typography></ToggleButton>
                <ToggleButton value="5days"><Typography variant="caption">5J</Typography></ToggleButton>
                <ToggleButton value="3days"><Typography variant="caption">3J</Typography></ToggleButton>
                <ToggleButton value="day"><Typography variant="caption">1J</Typography></ToggleButton>
              </ToggleButtonGroup>
              <IconButton onClick={() => setQuizTimelineOpen(false)} sx={{ ml: 1 }}>
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ mt: 1 }}>
            <Autocomplete
              multiple
              size="small"
              options={students}
              getOptionLabel={(s) => `${s.first_name} ${s.last_name || ''}`.trim()}
              value={quizTimelineStudents}
              onChange={(_, v) => setQuizTimelineStudents(v)}
              renderInput={(params) => <TextField {...params} label="Filtrer par élève" placeholder="Tous les élèves" />}
              filterOptions={(options, { inputValue }) => {
                const q = inputValue.toLowerCase();
                return options.filter(s => `${s.first_name} ${s.last_name || ''}`.toLowerCase().includes(q));
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={`${option.first_name} ${(option.last_name || '').charAt(0)}.`}
                    size="small"
                    sx={{ bgcolor: option.color ? `#${option.color}` : undefined, color: '#fff' }}
                  />
                ))
              }
            />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {quizTimelineLoading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : (() => {
            const { from: periodFrom, to: periodTo } = getQuizPeriodBounds(quizTimelinePeriod, quizTimelineOffset);
            const periodMs = periodTo.getTime() - periodFrom.getTime();

            // Filter attempts to selected students
            const filteredStudentIds = quizTimelineStudents.length > 0
              ? new Set(quizTimelineStudents.map(s => s.id))
              : null;

            const allSessions = groupAttemptsIntoSessions(quizAttempts);

            // Get sessions within the visible period
            const visibleSessions = allSessions.filter(s =>
              s.end.getTime() >= periodFrom.getTime() && s.start.getTime() <= periodTo.getTime() &&
              (!filteredStudentIds || filteredStudentIds.has(s.student))
            );

            // Group by student and sort students by most recent attempt
            const studentSessionMap = new Map<number, typeof allSessions>();
            for (const s of visibleSessions) {
              if (!studentSessionMap.has(s.student)) studentSessionMap.set(s.student, []);
              studentSessionMap.get(s.student)!.push(s);
            }

            // All sessions for stats (not just visible period)
            const allSessionsByStudent = new Map<number, typeof allSessions>();
            for (const s of allSessions) {
              if (filteredStudentIds && !filteredStudentIds.has(s.student)) continue;
              if (!allSessionsByStudent.has(s.student)) allSessionsByStudent.set(s.student, []);
              allSessionsByStudent.get(s.student)!.push(s);
            }

            // Build list of all student IDs to display (filtered or all)
            const displayStudentIds = filteredStudentIds
              ? Array.from(filteredStudentIds)
              : students.map(s => s.id);

            // Sort: students with visible sessions first (by most recent), then inactive students alphabetically
            const sortedStudentIds = displayStudentIds.sort((a, b) => {
              const aSessions = studentSessionMap.get(a) || [];
              const bSessions = studentSessionMap.get(b) || [];
              const aHas = aSessions.length > 0;
              const bHas = bSessions.length > 0;
              if (aHas && !bHas) return -1;
              if (!aHas && bHas) return 1;
              if (aHas && bHas) {
                const aMax = Math.max(...aSessions.map(s => s.end.getTime()));
                const bMax = Math.max(...bSessions.map(s => s.end.getTime()));
                return bMax - aMax;
              }
              // Both inactive: sort alphabetically
              const aStudent = students.find(s => s.id === a);
              const bStudent = students.find(s => s.id === b);
              const aName = aStudent ? `${aStudent.first_name} ${aStudent.last_name || ''}` : '';
              const bName = bStudent ? `${bStudent.first_name} ${bStudent.last_name || ''}` : '';
              return aName.localeCompare(bName);
            });

            // Stats helper: count sessions in a date range
            const countSessionsInRange = (studentId: number, from: Date, to: Date) => {
              const sessions = allSessionsByStudent.get(studentId) || [];
              return sessions.filter(s => s.start.getTime() >= from.getTime() && s.start.getTime() <= to.getTime()).length;
            };

            const now = new Date();
            const oneWeekAgo = new Date(now); oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
            const threeWeeksAgo = new Date(now); threeWeeksAgo.setDate(threeWeeksAgo.getDate() - 21);
            const fiveWeeksAgo = new Date(now); fiveWeeksAgo.setDate(fiveWeeksAgo.getDate() - 35);

            // Generate grid lines and date ticks for the axis
            const dateTicks: { pos: number, label: string }[] = [];
            const gridLines: { pos: number }[] = [];
            const periodDaysMap: Record<string, number> = { month: 30, week: 7, '5days': 5, '3days': 3, day: 1 };
            const periodDays = periodDaysMap[quizTimelinePeriod] || 7;

            // Grid lines: days for 3J/5J/1S, 2h for 1J, weeks (Mon midnight) for 1M
            const gridStart = new Date(periodFrom);
            gridStart.setMinutes(0, 0, 0);
            if (periodDays <= 1) {
              // 2h intervals
              gridStart.setHours(Math.ceil(gridStart.getHours() / 2) * 2);
              while (gridStart <= periodTo) {
                const pos = ((gridStart.getTime() - periodFrom.getTime()) / periodMs) * 100;
                if (pos > 0 && pos < 100) gridLines.push({ pos });
                gridStart.setHours(gridStart.getHours() + 2);
              }
            } else if (periodDays <= 7) {
              // Day boundaries (midnight)
              gridStart.setHours(0, 0, 0, 0);
              gridStart.setDate(gridStart.getDate() + 1);
              while (gridStart <= periodTo) {
                const pos = ((gridStart.getTime() - periodFrom.getTime()) / periodMs) * 100;
                if (pos > 0 && pos < 100) gridLines.push({ pos });
                gridStart.setDate(gridStart.getDate() + 1);
              }
            } else {
              // Week boundaries (Monday midnight)
              gridStart.setHours(0, 0, 0, 0);
              // Advance to next Monday
              const dayOfWeek = gridStart.getDay();
              const daysUntilMonday = dayOfWeek === 0 ? 1 : (8 - dayOfWeek);
              gridStart.setDate(gridStart.getDate() + daysUntilMonday);
              while (gridStart <= periodTo) {
                const pos = ((gridStart.getTime() - periodFrom.getTime()) / periodMs) * 100;
                if (pos > 0 && pos < 100) gridLines.push({ pos });
                gridStart.setDate(gridStart.getDate() + 7);
              }
            }

            // Date tick labels
            const tickInterval = periodDays <= 1 ? 2 : periodDays <= 5 ? 12 : 24; // hours
            const tickStart = new Date(periodFrom);
            tickStart.setMinutes(0, 0, 0);
            if (tickInterval >= 24) tickStart.setHours(0);
            else tickStart.setHours(Math.ceil(tickStart.getHours() / tickInterval) * tickInterval);

            while (tickStart <= periodTo) {
              const pos = ((tickStart.getTime() - periodFrom.getTime()) / periodMs) * 100;
              if (pos >= 0 && pos <= 100) {
                const label = periodDays <= 1
                  ? `${String(tickStart.getHours()).padStart(2, '0')}h`
                  : periodDays <= 7
                    ? `${['Dim','Lun','Mar','Mer','Jeu','Ven','Sam'][tickStart.getDay()]} ${tickStart.getDate()}`
                    : `${String(tickStart.getDate()).padStart(2, '0')}/${String(tickStart.getMonth() + 1).padStart(2, '0')}`;
                dateTicks.push({ pos, label });
              }
              tickStart.setHours(tickStart.getHours() + tickInterval);
            }

            return (
              <Box>
                {sortedStudentIds.map(studentId => {
                  const student = students.find(s => s.id === studentId);
                  const sessions = studentSessionMap.get(studentId) || [];
                  const studentName = student
                    ? `${student.first_name} ${(student.last_name || '').charAt(0)}.`
                    : `#${studentId}`;

                  // Stats
                  const sessionsThisWeek = countSessionsInRange(studentId, oneWeekAgo, now);
                  const sessions3w = countSessionsInRange(studentId, threeWeeksAgo, now);
                  const sessions5w = countSessionsInRange(studentId, fiveWeeksAgo, now);
                  const avg3w = Math.round(sessions3w / 3 * 10) / 10;
                  const avg5w = Math.round(sessions5w / 5 * 10) / 10;

                  const allStudentSessions = allSessionsByStudent.get(studentId) || [];
                  const totalCorrect = allStudentSessions.reduce((sum, s) => sum + s.correct, 0);
                  const totalCount = allStudentSessions.reduce((sum, s) => sum + s.count, 0);
                  const successRate = totalCount > 0 ? Math.round(totalCorrect / totalCount * 100) : 0;

                  return (
                    <Box key={studentId} sx={{ display: 'flex', alignItems: 'center', mb: 0.5, minHeight: 36 }}>
                      {/* Student name */}
                      <Box sx={{ width: 110, flexShrink: 0, pr: 1 }}>
                        <Tooltip title={student ? `${student.first_name} ${student.last_name || ''}` : `Élève #${studentId}`}>
                          <Typography variant="caption" noWrap sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                            {studentName}
                          </Typography>
                        </Tooltip>
                      </Box>

                      {/* Timeline bar */}
                      <Box sx={{
                        flex: 1,
                        position: 'relative',
                        height: 28,
                        bgcolor: 'action.hover',
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}>
                        {gridLines.map((gl, i) => (
                          <Box key={`g${i}`} sx={{
                            position: 'absolute',
                            left: `${gl.pos}%`,
                            top: 0,
                            bottom: 0,
                            width: '1px',
                            bgcolor: 'grey.300',
                            zIndex: 0,
                          }} />
                        ))}
                        {sessions.map((session, idx) => {
                          const startPos = Math.max(0, ((session.start.getTime() - periodFrom.getTime()) / periodMs) * 100);
                          const endPos = Math.min(100, ((session.end.getTime() - periodFrom.getTime()) / periodMs) * 100);
                          const width = Math.max(0.5, endPos - startPos);
                          const rate = session.count > 0 ? session.correct / session.count : 0;
                          const color = rate > 0.7 ? '#4caf50' : rate > 0.4 ? '#ff9800' : '#f44336';
                          const durationMin = Math.round((session.end.getTime() - session.start.getTime()) / 60000);

                          const fmtTime = (d: Date) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

                          return (
                            <Tooltip
                              key={idx}
                              title={
                                <Box>
                                  <Typography variant="caption" display="block">
                                    {fmtTime(session.start)} – {fmtTime(session.end)} ({durationMin > 0 ? `${durationMin} min` : '< 1 min'})
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    {session.count} réponse{session.count > 1 ? 's' : ''}, {session.correct} correcte{session.correct > 1 ? 's' : ''}
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    Taux : {Math.round(rate * 100)}%
                                  </Typography>
                                </Box>
                              }
                            >
                              <Box sx={{
                                position: 'absolute',
                                left: `${startPos}%`,
                                width: `${width}%`,
                                minWidth: 4,
                                top: 2,
                                bottom: 2,
                                bgcolor: color,
                                borderRadius: 0.5,
                                cursor: 'pointer',
                                opacity: 0.85,
                                '&:hover': { opacity: 1, zIndex: 1 },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                              }}>
                                {width > 3 && (
                                  <Typography variant="caption" sx={{ color: '#fff', fontSize: '0.6rem', fontWeight: 'bold', lineHeight: 1 }}>
                                    {session.count}
                                  </Typography>
                                )}
                              </Box>
                            </Tooltip>
                          );
                        })}
                      </Box>

                      {/* Stats */}
                      <Box sx={{ width: 200, flexShrink: 0, pl: 1.5, display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Tooltip title="Sessions cette semaine">
                          <Chip label={`${sessionsThisWeek}`} size="small" sx={{ minWidth: 28, fontSize: '0.7rem', fontWeight: 'bold' }} color="primary" variant="outlined" />
                        </Tooltip>
                        <Tooltip title="Moy. sessions/sem (3 sem.)">
                          <Chip label={`${avg3w}`} size="small" sx={{ minWidth: 28, fontSize: '0.7rem' }} variant="outlined" />
                        </Tooltip>
                        <Tooltip title="Moy. sessions/sem (5 sem.)">
                          <Chip label={`${avg5w}`} size="small" sx={{ minWidth: 28, fontSize: '0.7rem' }} variant="outlined" />
                        </Tooltip>
                        <Tooltip title={`Taux de réussite : ${successRate}%`}>
                          <Chip
                            label={`${successRate}%`}
                            size="small"
                            sx={{
                              minWidth: 38,
                              fontSize: '0.7rem',
                              fontWeight: 'bold',
                              bgcolor: successRate > 70 ? '#e8f5e9' : successRate > 40 ? '#fff3e0' : '#fce4ec',
                              color: successRate > 70 ? '#2e7d32' : successRate > 40 ? '#e65100' : '#c62828',
                            }}
                          />
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}

                {/* Date axis */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 1 }}>
                  <Box sx={{ width: 110, flexShrink: 0 }} />
                  <Box sx={{ flex: 1, position: 'relative', height: 20, borderTop: '1px solid', borderColor: 'grey.300' }}>
                    {gridLines.map((gl, i) => (
                      <Box key={`ga${i}`} sx={{
                        position: 'absolute',
                        left: `${gl.pos}%`,
                        top: 0,
                        height: 4,
                        width: '1px',
                        bgcolor: 'grey.400',
                      }} />
                    ))}
                    {dateTicks.map((tick, i) => (
                      <Typography
                        key={i}
                        variant="caption"
                        sx={{
                          position: 'absolute',
                          left: `${tick.pos}%`,
                          transform: 'translateX(-50%)',
                          top: 2,
                          fontSize: '0.65rem',
                          color: 'text.secondary',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {tick.label}
                      </Typography>
                    ))}
                  </Box>
                  <Box sx={{ width: 200, flexShrink: 0, pl: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', minWidth: 28, textAlign: 'center' }}>Sem.</Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', minWidth: 28, textAlign: 'center' }}>3s</Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', minWidth: 28, textAlign: 'center' }}>5s</Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', minWidth: 38, textAlign: 'center' }}>Taux</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Family Member Create/Edit Dialog */}
      <Dialog open={familyDialogOpen} onClose={closeFamilyDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editingParent ? 'Modifier le membre de famille' : 'Nouveau membre de famille'}
          <IconButton size="small" onClick={closeFamilyDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Prénom"
              value={parentFirstName}
              onChange={(e) => setParentFirstName(e.target.value)}
              fullWidth
              required
              size="small"
            />
            <TextField
              label="Nom"
              value={parentLastName}
              onChange={(e) => setParentLastName(e.target.value)}
              fullWidth
              required
              size="small"
            />
            <TextField
              label="Téléphone"
              value={parentPhoneNumber}
              onChange={(e) => setParentPhoneNumber(e.target.value)}
              fullWidth
              required
              size="small"
            />
            <TextField
              label="Email"
              value={parentEmail}
              onChange={(e) => setParentEmail(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label={editingParent ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe'}
              type="password"
              value={parentPassword}
              onChange={(e) => setParentPassword(e.target.value)}
              fullWidth
              required={!editingParent}
              size="small"
            />
            <FormControl size="small">
              <InputLabel>Genre</InputLabel>
              <Select
                value={parentGenderValue}
                label="Genre"
                onChange={(e) => setParentGenderValue(e.target.value as 'M' | 'F' | '')}
              >
                <MenuItem value="">Non spécifié</MenuItem>
                <MenuItem value="M">Homme</MenuItem>
                <MenuItem value="F">Femme</MenuItem>
              </Select>
            </FormControl>

            {/* Linked children */}
            <Box>
              <Typography variant="subtitle2" mb={1}>Élèves associés</Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {students.map((student) => (
                  <Chip
                    key={student.id}
                    label={`${student.first_name} ${student.last_name || ''}`}
                    size="small"
                    color={parentLinkedChildren.includes(student.id) ? 'primary' : 'default'}
                    variant={parentLinkedChildren.includes(student.id) ? 'filled' : 'outlined'}
                    onClick={() => {
                      setParentLinkedChildren(prev =>
                        prev.includes(student.id)
                          ? prev.filter(id => id !== student.id)
                          : [...prev, student.id]
                      );
                    }}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFamilyDialog}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleSaveFamilyMember}
            disabled={!parentFirstName || !parentLastName || !parentPhoneNumber || (!editingParent && !parentPassword)}
          >
            {editingParent ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recommendation Create/Edit Dialog */}
      <Dialog open={recoDialogOpen} onClose={() => setRecoDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingReco ? 'Modifier la préconisation' : 'Nouvelle préconisation'}</DialogTitle>
        <DialogContent>
          {/* Inline calendar with existing recommendations + drag-to-resize */}
          <Box mb={2}>
            <Box display="flex" alignItems="center" justifyContent="center" gap={2} mb={1} mt={1}>
              <IconButton size="small" onClick={() => setRecoDialogMonth(prev => dayjs(prev).subtract(1, 'month').format('YYYY-MM'))}>
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="subtitle2" fontWeight={600}>
                {dayjs(recoDialogMonth + '-01').format('MMMM YYYY')}
              </Typography>
              <IconButton size="small" onClick={() => setRecoDialogMonth(prev => dayjs(prev).add(1, 'month').format('YYYY-MM'))}>
                <ChevronRightIcon />
              </IconButton>
            </Box>

            <Box display="flex" justifyContent="center" gap={1} mb={1}>
              <Chip
                label="Début"
                size="small"
                color={recoPickingBound === 'start' ? 'primary' : 'default'}
                onClick={() => setRecoPickingBound('start')}
                variant={recoPickingBound === 'start' ? 'filled' : 'outlined'}
              />
              <Chip
                label="Fin"
                size="small"
                color={recoPickingBound === 'end' ? 'primary' : 'default'}
                onClick={() => setRecoPickingBound('end')}
                variant={recoPickingBound === 'end' ? 'filled' : 'outlined'}
              />
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', ml: 1 }}>
                {recoStartDate ? dayjs(recoStartDate).format('DD/MM') : '...'} → {recoEndDate ? dayjs(recoEndDate).format('DD/MM') : '...'}
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', bgcolor: '#eee', userSelect: recoDragging ? 'none' : 'auto' }}>
              {['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'].map(d => (
                <Box key={d} sx={{ bgcolor: 'background.paper', py: 0.25 }}>
                  <Typography variant="caption" align="center" display="block" fontWeight={600} color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {d}
                  </Typography>
                </Box>
              ))}
              {(() => {
                const monthStart = dayjs(recoDialogMonth + '-01');
                const startOfWeek = monthStart.startOf('month').day() === 0 ? 6 : monthStart.startOf('month').day() - 1;
                const daysInMonth = monthStart.daysInMonth();
                const cells = [];
                // Other recommendations (excluding the one being edited)
                const otherRecos = sortedRecommendations.filter(r => editingReco ? r.id !== editingReco.id : true);
                for (let i = 0; i < startOfWeek; i++) {
                  cells.push(<Box key={`empty-${i}`} sx={{ bgcolor: 'background.paper' }} />);
                }
                for (let day = 1; day <= daysInMonth; day++) {
                  const dateStr = monthStart.date(day).format('YYYY-MM-DD');
                  const isToday = dateStr === dayjs().format('YYYY-MM-DD');
                  const isInSelection = recoStartDate && recoEndDate && dateStr >= recoStartDate && dateStr <= recoEndDate;
                  const isSelStart = dateStr === recoStartDate;
                  const isSelEnd = dateStr === recoEndDate;
                  cells.push(
                    <Box
                      key={day}
                      onMouseEnter={() => { if (recoDragging) setRecoDragDate(dateStr); }}
                      onClick={() => {
                        if (recoDragging) return; // don't pick date during drag
                        if (recoPickingBound === 'start') {
                          setRecoStartDate(dateStr);
                          if (recoEndDate && dateStr > recoEndDate) setRecoEndDate('');
                          setRecoPickingBound('end');
                        } else {
                          if (recoStartDate && dateStr < recoStartDate) {
                            setRecoStartDate(dateStr);
                          } else {
                            setRecoEndDate(dateStr);
                            setRecoPickingBound('start');
                          }
                        }
                      }}
                      sx={{
                        bgcolor: isInSelection ? '#bbdefb' : 'background.paper',
                        px: 0.25,
                        pt: 0.25,
                        pb: 0.25,
                        textAlign: 'center',
                        minHeight: 36,
                        cursor: recoDragging ? 'col-resize' : 'pointer',
                        outline: isToday ? '2px solid #1976d2' : (isSelStart || isSelEnd) ? '2px solid #1565c0' : 'none',
                        outlineOffset: '-2px',
                        borderRadius: (isSelStart || isSelEnd) ? '4px' : 0,
                        '&:hover': { bgcolor: recoDragging ? undefined : (isInSelection ? '#90caf9' : '#f5f5f5') },
                      }}
                    >
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: (isSelStart || isSelEnd) ? 700 : isToday ? 600 : 400, position: 'relative', zIndex: 2 }}>
                        {day}
                      </Typography>
                      {otherRecos.map((reco) => {
                        const { start, end } = getEffectiveRange(reco);
                        if (start > end || dateStr < start || dateStr > end) return null;
                        const isRStart = dateStr === start;
                        const isREnd = dateStr === end;
                        const color = getRecoColor(reco.id);
                        const isDraggingThis = recoDragging?.recoId === reco.id;
                        return (
                          <Box
                            key={reco.id}
                            sx={{
                              height: 5,
                              bgcolor: isDraggingThis ? `${color}cc` : color,
                              opacity: 0.7,
                              mt: 0.25,
                              ml: isRStart ? '3px' : '-1px',
                              mr: isREnd ? '3px' : '-1px',
                              borderTopLeftRadius: isRStart ? 3 : 0,
                              borderBottomLeftRadius: isRStart ? 3 : 0,
                              borderTopRightRadius: isREnd ? 3 : 0,
                              borderBottomRightRadius: isREnd ? 3 : 0,
                              position: 'relative',
                              zIndex: 1,
                              pointerEvents: recoDragging ? 'none' : 'auto',
                              cursor: (isRStart || isREnd) ? 'col-resize' : 'default',
                            }}
                            title={`${reco.sessions_per_day} sessions/jour (${dayjs(reco.start_date).format('DD/MM')} → ${dayjs(reco.end_date).format('DD/MM')})`}
                            onMouseDown={(e) => {
                              if (isRStart || isREnd) {
                                e.preventDefault();
                                e.stopPropagation();
                                const bound = isRStart && !isREnd ? 'start' : isREnd && !isRStart ? 'end' : (e.nativeEvent.offsetX < (e.currentTarget.offsetWidth / 2) ? 'start' : 'end');
                                setRecoDragging({ recoId: reco.id, bound });
                                setRecoDragDate(dateStr);
                              }
                            }}
                          />
                        );
                      })}
                    </Box>
                  );
                }
                return cells;
              })()}
            </Box>
          </Box>

          <FormControl fullWidth margin="dense">
            <InputLabel>Sessions par jour</InputLabel>
            <Select
              value={recoSessionsPerDay}
              onChange={(e) => setRecoSessionsPerDay(e.target.value as number)}
              label="Sessions par jour"
            >
              {[1, 2, 3].map(n => (
                <MenuItem key={n} value={n}>{n}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Sessions (compositions)</InputLabel>
            <Select
              multiple
              value={recoSelectedSessions}
              onChange={(e) => setRecoSelectedSessions(e.target.value as number[])}
              label="Sessions (compositions)"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as number[]).map((id) => {
                    const session = quizSessions.find(s => s.id === id);
                    return <Chip key={id} label={session?.name || `#${id}`} size="small" />;
                  })}
                </Box>
              )}
            >
              {quizSessions.map((session) => (
                <MenuItem key={session.id} value={session.id}>
                  <Checkbox checked={recoSelectedSessions.includes(session.id)} size="small" />
                  <Typography variant="body2" sx={{ ml: 1 }}>{session.name}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          {editingReco && (
            <Button
              color="error"
              onClick={async () => {
                try {
                  await quizRecommendationService.delete(editingReco.id);
                  setRecommendations(prev => prev.filter(r => r.id !== editingReco.id));
                  setRecoDialogOpen(false);
                  toast.success('Préconisation supprimée');
                } catch { toast.error('Erreur'); }
              }}
            >
              Supprimer
            </Button>
          )}
          <Box flex={1} />
          <Button onClick={() => setRecoDialogOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            disabled={!recoStartDate || !recoEndDate || !selectedStudent}
            onClick={async () => {
              if (!selectedStudent) return;
              try {
                if (editingReco) {
                  const updated = await quizRecommendationService.update(editingReco.id, {
                    sessions_per_day: recoSessionsPerDay,
                    start_date: recoStartDate,
                    end_date: recoEndDate,
                    quiz_sessions: recoSelectedSessions,
                  });
                  setRecommendations(prev => prev.map(r => r.id === editingReco.id ? updated : r));
                  toast.success('Préconisation mise à jour');
                } else {
                  const created = await quizRecommendationService.create({
                    student: selectedStudent.id,
                    sessions_per_day: recoSessionsPerDay,
                    start_date: recoStartDate,
                    end_date: recoEndDate,
                    quiz_sessions: recoSelectedSessions,
                  });
                  setRecommendations(prev => [...prev, created]);
                  toast.success('Préconisation créée');
                }
                setRecoDialogOpen(false);
              } catch (err: any) {
                const msg = err?.response?.data?.non_field_errors?.[0] || 'Erreur';
                toast.error(msg);
              }
            }}
          >
            {editingReco ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      </Box>
    </DragDropContext>
  );
};

export default Students;
